// Mindex reader — main React app
// Three screens: landing → org picker → workspace
// Data: fetched from /app/api/* (session-cookie auth)

const { useState, useEffect, useLayoutEffect, useReducer, useRef, useCallback, useMemo } = React;
const NOOP = () => {};
// app.jsx is an UNBUNDLED IIFE — esbuild doesn't follow ES imports at this
// entry. Cross-package data + helpers reach the runtime via vendor.js, which
// IS bundled, runs first, and attaches them to `window.mindex*` globals.
// `window.mindexReaderTheme` is installed by _lib/vendor.entry.js from
// _lib/reader-theme.js (which re-exports SCHEMES/STYLES from
// @mindex/core/reader-theme). Same pattern as window.mdPipeline + window.React.
const { SCHEMES, STYLES, applyScheme: applySchemeBase, applyStyle: applyStyleBase } = window.mindexReaderTheme;

// ─────────────────────────  Persistence keys  ─────────────────────────

const KEY_WIDTH       = "mindex.sidebar.width.v1";
const KEY_STYLE       = "mindex.reader.style.v1";
const KEY_SCHEME      = "mindex.color.scheme.v1";
const KEY_ORG         = "mindex.org.current.v1";
const KEY_OUTLINE_VISIBLE = "mindex.outline.visible.v1";
const KEY_TRASH_VIEW  = "mindex.trash.view.v1";
const keyOrgWorkspace = (org)      => `mindex.org.${org}.workspace.v1`;
const keyExpanded     = (org, ws)  => `mindex.tree.expanded.v1.${org}.${ws}`;
const keyFolds        = (org, ws, file) => `mindex.folds.v1.${org}.${ws}.${file}`;
const keySidebarTab   = (org, ws)  => `mindex.sidebar.tab.v1.${org}.${ws}`;
const keyTabs         = (org, ws)  => `mindex.tabs.v1.${org}.${ws}`;

const MOBILE_QUERY = '(max-width: 767px)';
// Desktop keep-alive window: how many tabs stay mounted at once (active + the
// most-recently-active). Switching among mounted tabs is an instant show/hide;
// tabs past the window remount (restoring scroll) on next activation.
const KEEP_ALIVE_MAX = 6;
// How many recently-closed tabs are remembered for Alt+Y reopen (LIFO).
const CLOSED_TABS_MAX = 10;
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

function lsGet(key, fallback)   { try { return localStorage.getItem(key) ?? fallback; } catch (e) { return fallback; } }
function lsSet(key, value)      { try { localStorage.setItem(key, value); } catch (e) {} }
function lsGetJSON(key, fallback) {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); } catch (e) { return fallback; }
}
function lsSetJSON(key, value)  { lsSet(key, JSON.stringify(value)); }

function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
      return;
    }
  } catch (e) { /* fall through */ }
  // Fallback: a hidden textarea + execCommand('copy').
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch (e) {}
  document.body.removeChild(ta);
}

// ─────────────────────────  Color schemes  ─────────────────────────
// SCHEMES imported from @mindex/core/reader-theme via ./_lib/reader-theme.js

function applyScheme(key) {
  applySchemeBase(key);
  if (SCHEMES[key]) lsSet(KEY_SCHEME, key);
}

// ─────────────────────────  Reader styles  ─────────────────────────
// STYLES imported from @mindex/core/reader-theme via ./_lib/reader-theme.js

function applyStyle(key) {
  applyStyleBase(key);
  if (STYLES[key]) lsSet(KEY_STYLE, key);
}

// ─────────────────────────  SVG icons  ─────────────────────────

const SVG_CHEV = (
  <svg viewBox="0 0 12 12" fill="none">
    <path d="M4 2 L8 6 L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const SVG_FOLDER_CLOSED = (
  <svg viewBox="0 0 16 16" fill="none">
    <path d="M1.5 4.5c0-.55.45-1 1-1h3.4c.27 0 .53.11.72.3l.7.7c.19.19.45.3.72.3h6.46c.55 0 1 .45 1 1v6.7c0 .55-.45 1-1 1H2.5c-.55 0-1-.45-1-1V4.5z"
          stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
  </svg>
);
const SVG_FOLDER_OPEN = (
  <svg viewBox="0 0 16 16" fill="none">
    <path d="M1.5 4.5c0-.55.45-1 1-1h3.4c.27 0 .53.11.72.3l.7.7c.19.19.45.3.72.3h6.46c.55 0 1 .45 1 1v6.7c0 .55-.45 1-1 1H2.5c-.55 0-1-.45-1-1V4.5z"
          stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" fill="currentColor" fillOpacity="0.12"/>
  </svg>
);
const SVG_FILE = (
  <svg viewBox="0 0 16 16" fill="none">
    <path d="M3.5 2h6l3 3v8.5c0 .28-.22.5-.5.5h-8.5c-.28 0-.5-.22-.5-.5v-11c0-.28.22-.5.5-.5z"
          stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
    <path d="M9.5 2v3h3" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
  </svg>
);

// ─────────────────────────  Format helpers  ─────────────────────────

function sortNodes(nodes) {
  return nodes.slice().sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}
function fileExistsInTree(nodes, path) {
  for (const n of nodes) {
    if (n.type === 'file' && n.path === path) return true;
    if (n.type === 'folder' && fileExistsInTree(n.children, path)) return true;
  }
  return false;
}

// ---- API ----

async function apiMe() {
  const r = await fetch('/app/api/me');
  if (r.status === 401) return null;
  if (!r.ok) throw new Error('/app/api/me ' + r.status);
  return r.json();
}

async function apiTree(orgSlug, wsHandle) {
  const r = await fetch(
    '/app/api/tree?org=' + encodeURIComponent(orgSlug) + '&workspace=' + encodeURIComponent(wsHandle)
  );
  if (!r.ok) throw new Error('/app/api/tree ' + r.status);
  return (await r.json()).tree;
}

// B6 — flat, enriched file listing for the workspace browser. Follows
// next_cursor to load all pages (bounded server-side by MAX_BROWSE_FILES).
async function apiFiles(orgSlug, wsHandle) {
  const all = [];
  let cursor = null;
  // Hard client-side page cap as a backstop against a runaway cursor loop.
  for (let guard = 0; guard < 64; guard++) {
    const url = '/app/api/files?org=' + encodeURIComponent(orgSlug) +
      '&workspace=' + encodeURIComponent(wsHandle) +
      (cursor ? '&cursor=' + encodeURIComponent(cursor) : '');
    const r = await fetch(url);
    if (!r.ok) throw new Error('/app/api/files ' + r.status);
    const data = await r.json();
    all.push(...data.files);
    if (!data.next_cursor) break;
    cursor = data.next_cursor;
  }
  return all;
}

// ---- History sheet API (M6) ----

async function apiReadAtVersion(orgSlug, wsHandle, path, sha256) {
  const r = await fetch(
    '/app/api/read?org=' + encodeURIComponent(orgSlug) +
    '&workspace=' + encodeURIComponent(wsHandle) +
    '&path=' + encodeURIComponent(path) +
    '&version=' + encodeURIComponent(sha256)
  );
  if (!r.ok) throw new Error('/app/api/read?version ' + r.status);
  return (await r.json()).content;
}

// ---- Invitations / members API ----

async function apiMembers(orgSlug) {
  const r = await fetch('/app/api/members?org=' + encodeURIComponent(orgSlug));
  if (!r.ok) throw new Error('/app/api/members ' + r.status);
  return (await r.json()).members;
}

async function apiPendingInvitations(orgSlug) {
  const r = await fetch('/app/api/invitations?org=' + encodeURIComponent(orgSlug));
  if (!r.ok) throw new Error('/app/api/invitations ' + r.status);
  return (await r.json()).invitations;
}

async function apiCreateInvitation(orgSlug, email) {
  const r = await fetch('/app/api/invitations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ org: orgSlug, email, role: 'member' }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data.message || ('HTTP ' + r.status));
    err.code = data.error;
    throw err;
  }
  return data;
}

async function apiRevokeInvitation(id) {
  const r = await fetch('/app/api/invitations/' + encodeURIComponent(id), { method: 'DELETE' });
  if (!r.ok) throw new Error('/app/api/invitations/' + id + ' ' + r.status);
  return true;
}

async function doSignIn(provider) {
  try {
    const r = await fetch('/api/auth/sign-in/social', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, callbackURL: window.location.origin + '/' }),
    });
    const data = await r.json().catch(() => ({}));
    if (data && data.url) { window.location.href = data.url; return null; }
    if (!r.ok) return (data && data.message) || ('Sign-in failed (' + r.status + ').');
    return 'Sign-in failed.';
  } catch (e) {
    return 'Could not reach the server.';
  }
}

async function doSignOut() {
  await fetch('/api/auth/sign-out', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  }).catch(() => {});
  window.location.reload();
}

// ---- URL routing helpers ----

// First path segments owned by the worker. Load-bearing guard: the
// asset binding falls through to /index.html for unmatched /app/<x>
// paths (and would for any other prefix not explicitly claimed by a
// route), so the SPA must never interpret a reserved prefix as an
// org slug. See spec §8 item 9.
const RESERVED_PREFIXES = new Set([
  'app', 'api', 'oauth', 'mcp', 's', '.well-known', 'health',
]);

// /<org>/<workspace>/<...path> → { orgSlug, wsHandle, filePath }.
// Each is null if absent or invalid. Returns { orgSlug: null, ... }
// for "/" and for any URL that starts with a RESERVED_PREFIX.
function parseRoute(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return { orgSlug: null, wsHandle: null, filePath: null };
  const head = parts[0];
  if (RESERVED_PREFIXES.has(head)) {
    return { orgSlug: null, wsHandle: null, filePath: null };
  }
  let orgSlug = null, wsHandle = null, filePath = null;
  try { orgSlug = decodeURIComponent(parts[0]); } catch (e) { return { orgSlug: null, wsHandle: null, filePath: null }; }
  if (parts.length >= 2) {
    try { wsHandle = decodeURIComponent(parts[1]); } catch (e) { return { orgSlug, wsHandle: null, filePath: null }; }
  }
  if (parts.length >= 3) {
    try {
      filePath = parts.slice(2).map((s) => decodeURIComponent(s)).join('/');
    } catch (e) {
      return { orgSlug, wsHandle, filePath: null };
    }
  }
  return { orgSlug, wsHandle, filePath };
}

// SPA-navigation helpers — sourced from window.mindexSpaNav, which
// vendor.entry.js attaches before app.js loads (see index.html:26-29:
// /vendor.js is a synchronous <script> ahead of /app.js).
const { isModifiedClick, buildRoute } = window.mindexSpaNav;
const { clampMenuPosition, wikilinkFor, buildContextMenuItems } = window.mindexContextMenu;
const { buildBreadcrumbItems } = window.mindexBreadcrumb;
const { siblingNames, nextDuplicateName, validateBasename, replacePathPrefix, joinPath, splitBasename } = window.mindexTreeMut;
const { singleSelect, clearSelection, toggleSelection, selectRange } = window.mindexSelection;
const { isValidDrop, composeDropTarget, expandFolderMoves, collectFilesUnder, collectFolders, basename: dndBasename } = window.mindexDnd;
const { buildMovePlan, moveConfirmCopy } = window.mindexMovePlan;
// B6 — workspace file browser pure helpers + formatters (window globals
// attached by vendor.entry.js ahead of app.js).
const { filterRows: browseFilterRows, sortRows: browseSortRows, tagCounts: browseTagCounts, defaultBrowseState } = window.mindexBrowse;
const { formatRelativeTime: browseRelTime } = window.mindexRecentGroups;
const { formatBytes: browseBytes } = window.mindexTrashGroups;

// Walk the tree in render order, returning only rows visible under
// the current expanded set.
function flattenVisible(tree, expanded) {
  const out = [];
  const walk = (nodes, parentPath) => {
    for (const node of sortNodes(nodes)) {
      const path = parentPath ? `${parentPath}/${node.name}` : node.name;
      if (node.type === 'folder') {
        out.push({ path, type: 'folder', parentPath });
        if (expanded.has(path)) walk(node.children, path);
      } else {
        out.push({ path, type: 'file', parentPath });
      }
    }
  };
  walk(tree, '');
  return out;
}

function indexOfPath(rows, path) {
  for (let i = 0; i < rows.length; i++) if (rows[i].path === path) return i;
  return -1;
}

function parentOf(path) {
  const i = path.lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

function folderExistsInTree(nodes, path, prefix) {
  for (const n of nodes) {
    const p = prefix ? `${prefix}/${n.name}` : n.name;
    if (n.type === 'folder') {
      if (p === path) return true;
      if (folderExistsInTree(n.children, path, p)) return true;
    }
  }
  return false;
}

function rowExistsInTree(nodes, path) {
  return fileExistsInTree(nodes, path) || folderExistsInTree(nodes, path, '');
}

// ───────────────────────────  Tree search  ───────────────────────────
//
// Pure helpers used by the sidebar search bar. See
// docs/superpowers/specs/2026-05-02-tree-search-design.md.

// Walks the original tree, returns the total file count (for the
// "X of Y" denominator).
function countFilesInTree(nodes) {
  let n = 0;
  for (const node of nodes) {
    if (node.type === 'file') n++;
    else if (node.type === 'folder') n += countFilesInTree(node.children);
  }
  return n;
}

// Filter the tree to nodes that match `query` (case-insensitive
// substring on basename + full path). Returns:
//   {
//     tree:             pruned tree (new array; original untouched),
//     matchedFileCount: # of matched FILES,
//     totalFileCount:   # of files in the original tree,
//     autoExpanded:     Set<string> of folder paths that should be
//                       force-expanded under this filter, OR null
//                       when the query is empty (caller falls back
//                       to the user's manual `expanded` set).
//   }
//
// Empty / whitespace-only query short-circuits to a no-op pass-through.
function filterTree(tree, rawQuery) {
  const totalFileCount = countFilesInTree(tree);
  const query = (rawQuery || '').trim().toLowerCase();
  if (query === '') {
    return { tree, matchedFileCount: 0, totalFileCount, autoExpanded: null };
  }

  const autoExpanded = new Set();
  let matchedFileCount = 0;

  // Walk the tree; for each node, return either the filtered node
  // (possibly with filtered children) or null if it doesn't match.
  function walk(node, parentPath) {
    const path = parentPath ? `${parentPath}/${node.name}` : node.name;
    if (node.type === 'file') {
      const hit = path.toLowerCase().includes(query) ||
                  node.name.toLowerCase().includes(query);
      if (!hit) return null;
      matchedFileCount++;
      // Files are pass-through — we don't mutate the node, but we
      // also don't need to clone because consumers don't write to it.
      return node;
    }
    if (node.type === 'folder') {
      const childResults = [];
      for (const child of node.children) {
        const kept = walk(child, path);
        if (kept !== null) childResults.push(kept);
      }
      const folderNameHit = node.name.toLowerCase().includes(query);
      if (childResults.length === 0 && !folderNameHit) return null;
      autoExpanded.add(path);
      // Return a shallow-cloned folder with filtered children.
      return { ...node, children: childResults };
    }
    return null;
  }

  const filtered = [];
  for (const root of tree) {
    const kept = walk(root, '');
    if (kept !== null) filtered.push(kept);
  }
  return { tree: filtered, matchedFileCount, totalFileCount, autoExpanded };
}

// Render a string with `query` (lowercased) highlighted.
// Returns either the original string (no match) or an array of
// JSX fragments: text-before, <b className="search-hit">match</b>,
// text-after. Multiple matches in the same string are all bolded.
function highlightMatch(text, lowerQuery) {
  if (!lowerQuery) return text;
  const lowerText = text.toLowerCase();
  let i = lowerText.indexOf(lowerQuery);
  if (i < 0) return text;
  const out = [];
  let cursor = 0;
  let key = 0;
  while (i >= 0) {
    if (i > cursor) out.push(text.slice(cursor, i));
    out.push(
      <b key={'h' + key++} className="search-hit">
        {text.slice(i, i + lowerQuery.length)}
      </b>,
    );
    cursor = i + lowerQuery.length;
    i = lowerText.indexOf(lowerQuery, cursor);
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

// ─────────────────────────  Toast (single global instance)  ─────────────────────────

let __mindexToast = null; // imperative handle set by the mounted Toast

// Accepts either a plain string (legacy) or { message, actionLabel, onAction, durationMs }.
function showToast(msgOrOpts) {
  if (!__mindexToast) return;
  if (typeof msgOrOpts === 'string') {
    __mindexToast({ message: msgOrOpts, durationMs: 2000 });
  } else {
    __mindexToast(msgOrOpts);
  }
}

function Toast() {
  const [visible, setVisible] = useState(false);
  const [msg, setMsg] = useState('');
  const [actionLabel, setActionLabel] = useState(null);
  const [onActionFn, setOnActionFn] = useState(null);
  const timerRef = useRef(null);
  useEffect(() => {
    __mindexToast = ({ message, actionLabel, onAction, durationMs }) => {
      setMsg(message);
      setActionLabel(actionLabel ?? null);
      // Wrap in a function-returning-function because useState treats
      // function values as updaters.
      setOnActionFn(() => (onAction || null));
      setVisible(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setVisible(false), durationMs ?? 2000);
    };
    return () => {
      __mindexToast = null;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  const handleAction = () => {
    if (onActionFn) onActionFn();
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };
  return (
    <div className={'toast' + (visible ? ' is-visible' : '')}>
      <svg viewBox="0 0 16 16" fill="none">
        <path d="M3.5 8 L6.5 11 L12.5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{msg}</span>
      {actionLabel && (
        <button type="button" className="toast-action" onClick={handleAction}>{actionLabel}</button>
      )}
    </div>
  );
}

// ─────────────────────────  Topbar (scheme/style/breadcrumbs/nav/copy)  ─────────────────────────

function StylePopover({ open, anchorRect, current, onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  const right = anchorRect ? (window.innerWidth - anchorRect.right) : 14;
  return (
    <div ref={ref} className={'style-pop' + (open ? ' is-open' : '')} style={{ right }} role="menu" aria-label="Reader style">
      <div className="style-pop-head">Reader Style</div>
      <div className="style-list">
        {Object.entries(STYLES).map(([key, s]) => (
          <button
            key={key}
            className={'style-row' + (key === current ? ' is-current' : '')}
            onClick={() => { onPick(key); onClose(); }}
          >
            <div className="style-name" style={{ fontFamily: s.display, fontStyle: s.displayStyle, fontWeight: s.h1Weight }}>{s.name}</div>
            <div className="style-sample" style={{ fontFamily: s.body }}>{s.sample}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function SchemePopover({ open, anchorRect, current, onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  const right = anchorRect ? (window.innerWidth - anchorRect.right) : 14;
  return (
    <div ref={ref} className={'scheme-pop' + (open ? ' is-open' : '')} style={{ right }} role="menu" aria-label="Color scheme">
      <div className="scheme-pop-head">Color Scheme</div>
      <div className="scheme-list">
        {Object.entries(SCHEMES).map(([key, s]) => (
          <button
            key={key}
            className={'scheme-row' + (key === current ? ' is-current' : '')}
            onClick={() => { onPick(key); onClose(); }}
          >
            <div className="scheme-swatch">
              <span style={{ background: s.swatch[0] }} />
              <span style={{ background: s.swatch[1] }} />
            </div>
            <div className="scheme-meta">
              <div className="scheme-name">{s.name}</div>
              <div className="scheme-desc">{s.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MobileMoreSheet({ open, onClose, activeScheme, onPickScheme, activeStyle, onPickStyle, outlineVisible, onToggleOutline }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div className={'mobile-sheet-backdrop' + (open ? ' is-open' : '')} onClick={onClose} />
      <div className={'mobile-sheet' + (open ? ' is-open' : '')}>
        <div className="mobile-sheet-handle" />

        <div className="mobile-sheet-section">
          <div className="mobile-sheet-label">Color Scheme</div>
          {Object.entries(SCHEMES).map(([key, s]) => (
            <button
              key={key}
              className={'mobile-sheet-row' + (key === activeScheme ? ' is-current' : '')}
              onClick={() => onPickScheme(key)}
            >
              <div className="scheme-swatch">
                <span style={{ background: s.swatch[0] }} />
                <span style={{ background: s.swatch[1] }} />
              </div>
              <span className="mobile-sheet-row-label">{s.name}</span>
            </button>
          ))}
        </div>

        <div className="mobile-sheet-section">
          <div className="mobile-sheet-label">Reader Style</div>
          {Object.entries(STYLES).map(([key, s]) => (
            <button
              key={key}
              className={'mobile-sheet-row' + (key === activeStyle ? ' is-current' : '')}
              onClick={() => onPickStyle(key)}
            >
              <span className="mobile-sheet-row-label" style={{ fontFamily: s.display, fontStyle: s.displayStyle }}>{s.name}</span>
            </button>
          ))}
        </div>

        <div className="mobile-sheet-section">
          <div className="mobile-sheet-label">Display</div>
          <button className="mobile-sheet-row" onClick={() => { onToggleOutline(); }}>
            <span className="mobile-sheet-row-label">Heading outline</span>
            <span className="mobile-sheet-toggle">{outlineVisible ? 'On' : 'Off'}</span>
          </button>
        </div>
      </div>
    </>
  );
}

// ── Desktop Topbar ──

function Topbar({
  onBack, onForward, canBack, canForward,
  onToggleSidebar, onOpenSearch,
  activeScheme, onPickScheme,
  activeStyle, onPickStyle,
  tabs, activeIndex, onSwitchTab, onCloseTab, onNewTab, readerActive,
}) {
  const [stylePopOpen, setStylePopOpen]   = useState(false);
  const [schemePopOpen, setSchemePopOpen] = useState(false);
  const [styleAnchor, setStyleAnchor]     = useState(null);
  const [schemeAnchor, setSchemeAnchor]   = useState(null);
  const styleBtnRef  = useRef(null);
  const schemeBtnRef = useRef(null);

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button className="wordmark" onClick={onToggleSidebar} title="Toggle sidebar">mindex</button>
        <div className="nav-arrows" aria-label="History navigation">
          <button className="nav-btn" onClick={onBack}    disabled={!canBack}    title="Back">
            <svg viewBox="0 0 16 16" fill="none"><path d="M10 3 L5 8 L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <button className="nav-btn" onClick={onForward} disabled={!canForward} title="Forward">
            <svg viewBox="0 0 16 16" fill="none"><path d="M6 3 L11 8 L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </div>

      <div className="topbar-right">
        {readerActive && ( /* readerActive already implies tabState.tabs.length > 0 (§4.6) */
          <TabStrip
            tabs={tabs}
            activeIndex={activeIndex}
            onSwitch={onSwitchTab}
            onClose={onCloseTab}
            onNewTab={onNewTab}
            readerActive={readerActive}
          />
        )}

        <div className="spacer" />

        {onOpenSearch && (
          <button
            className="topbar-search-pill"
            onClick={onOpenSearch}
            title="Search file contents (Ctrl/Cmd+Shift+F)"
          >
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M10.5 10.5 L14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            <span className="tsp-label">Search</span>
            <span className="tsp-kbd">⇧⌘F</span>
          </button>
        )}

        <button
          ref={schemeBtnRef}
          className={'palette-btn' + (schemePopOpen ? ' is-open' : '')}
          onClick={() => {
            setSchemeAnchor(schemeBtnRef.current.getBoundingClientRect());
            setSchemePopOpen((o) => !o);
            setStylePopOpen(false);
          }}
          title="Color scheme"
        >
          <svg viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M8 1.5 A 6.5 6.5 0 0 1 8 14.5 Z" fill="currentColor"/>
          </svg>
        </button>

        <button
          ref={styleBtnRef}
          className={'style-btn' + (stylePopOpen ? ' is-open' : '')}
          onClick={() => {
            setStyleAnchor(styleBtnRef.current.getBoundingClientRect());
            setStylePopOpen((o) => !o);
            setSchemePopOpen(false);
          }}
          title="Reader style"
        >
          <span className="Aa-big">A</span><span className="Aa-small">a</span>
        </button>

        <SchemePopover
          open={schemePopOpen}
          anchorRect={schemeAnchor}
          current={activeScheme}
          onPick={onPickScheme}
          onClose={() => setSchemePopOpen(false)}
        />
        <StylePopover
          open={stylePopOpen}
          anchorRect={styleAnchor}
          current={activeStyle}
          onPick={onPickStyle}
          onClose={() => setStylePopOpen(false)}
        />
      </div>
    </header>
  );
}

// ── Mobile Topbar ──

function MobileTopbar({ filePath, workspaceLabel, onHamburger, onShare, activeScheme, onPickScheme, activeStyle, onPickStyle, outlineVisible, onToggleOutline }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const title = filePath ? filePath.split('/').pop() : (workspaceLabel || 'mindex');

  return (
    <header className="topbar mobile-topbar">
      <button className="hamburger" onClick={onHamburger} aria-label="Open menu">
        <span /><span /><span />
      </button>
      <div className="mobile-title">{title}</div>
      {filePath && <button className="mobile-share-btn" onClick={onShare}>Share</button>}
      <button className="more-btn" onClick={() => setMoreOpen(true)} aria-label="More options">···</button>
      <MobileMoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        activeScheme={activeScheme}
        onPickScheme={onPickScheme}
        activeStyle={activeStyle}
        onPickStyle={onPickStyle}
        outlineVisible={outlineVisible}
        onToggleOutline={onToggleOutline}
      />
    </header>
  );
}

// ── Mobile Drawer ──

// Mobile drawer "Open tabs" list — tap a row to jump (and close the drawer),
// × to close the tab. The touch counterpart of the desktop TabStrip; mobile
// also has swipe + the N/total pill, this is the direct-jump affordance.
function MobileDrawerTabs({ tabs, activeIndex, onSwitch, onClose }) {
  if (!tabs.length) return null;
  return (
    <div className="drawer-tabs" role="tablist" aria-label="Open tabs">
      <div className="drawer-tabs__label">Open tabs</div>
      {tabs.map((t, i) => {
        const name = t.path ? t.path.split('/').pop() : 'New Tab';
        return (
          <div
            key={t.id}
            className={'drawer-tab' + (i === activeIndex ? ' is-active' : '')}
            role="tab"
            aria-selected={i === activeIndex}
            tabIndex={0}
            title={t.path || 'New Tab'}
            onClick={() => onSwitch(i)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSwitch(i); } }}
          >
            <span className="drawer-tab__name">{name}</span>
            <button
              className="drawer-tab__close"
              title="Close tab"
              aria-label={'Close ' + name}
              onClick={(e) => { e.stopPropagation(); onClose(i); }}
            >&times;</button>
          </div>
        );
      })}
    </div>
  );
}

function MobileDrawer({ open, onClose, workspace, workspaces, onPickWorkspace, fileCount, tree, treeLoading, visibleTree, activeFile, onOpenFile, expanded, setExpanded, focusedPath, setFocusedPath, onTreeKeyDown, searchQuery, setSearchQuery, matchedFileCount, totalFileCount, lowerQuery, autoExpanded, onCollapseAll, onExpandAll, orgs, org, onSwitchOrg, setView, user, onSignOut,
  activeTab, onPickTab, trashOpen, onOpenTrash,
  tabs, activeTabIndex, onSwitchTab, onCloseTab,
  onRowContextMenu, onTreeBackgroundContextMenu, renamingPath, newRow, onRenameCommit, onRenameCancel, onNewRowCommit, onNewRowCancel }) {
  const [orgSheetOpen, setOrgSheetOpen] = useState(false);
  const [userSheetOpen, setUserSheetOpen] = useState(false);

  useEffect(() => {
    if (!orgSheetOpen && !userSheetOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') { setOrgSheetOpen(false); setUserSheetOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [orgSheetOpen, userSheetOpen]);

  const orgSlug = org && org.slug;
  const current = orgs.find((o) => o.slug === orgSlug);

  return (
    <>
      <div className={'drawer-backdrop' + (open ? ' is-open' : '')} onClick={onClose} />
      <div className={'drawer' + (open ? ' is-open' : '')}>
        <WorkspaceSwitcher
          workspaces={workspaces}
          current={workspace}
          onPick={onPickWorkspace}
          fileCount={fileCount}
          onExpandAll={onExpandAll}
          onCollapseAll={onCollapseAll}
          orgSlug={orgSlug}
        />
        <MobileDrawerTabs
          tabs={tabs}
          activeIndex={activeTabIndex}
          onSwitch={(i) => { onSwitchTab(i); onClose(); }}
          onClose={onCloseTab}
        />
        <SidebarTabs
          activeTab={activeTab}
          onPickTab={onPickTab}
          trashOpen={trashOpen}
          onOpenTrash={onOpenTrash}
          trashCount={null}
        />
        <div className="sidebar-panes">
          <div className="sidebar-pane" hidden={activeTab !== 'files'}>
            <div className="sidebar-tree">
              {!treeLoading && tree.length > 0 && (
                <SearchInput query={searchQuery} setQuery={setSearchQuery} matchedCount={matchedFileCount} totalCount={totalFileCount} />
              )}
              {treeLoading
                ? <div className="tree-empty">Loading{"\u2026"}</div>
                : tree.length === 0
                  ? <div className="tree-empty">This workspace is empty.</div>
                  : (
                    <FileTree
                      tree={visibleTree}
                      fullTree={tree}
                      activeFile={activeFile}
                      onOpenFile={onOpenFile}
                      expanded={expanded}
                      setExpanded={setExpanded}
                      focusedPath={focusedPath}
                      setFocusedPath={setFocusedPath}
                      onKeyDown={onTreeKeyDown}
                      query={lowerQuery}
                      autoExpanded={autoExpanded}
                      orgSlug={orgSlug}
                      wsHandle={workspace && workspace.handle}
                      onRowContextMenu={onRowContextMenu}
                      onTreeBackgroundContextMenu={onTreeBackgroundContextMenu}
                      renamingPath={renamingPath}
                      newRow={newRow}
                      onRenameCommit={onRenameCommit}
                      onRenameCancel={onRenameCancel}
                      onNewRowCommit={onNewRowCommit}
                      onNewRowCancel={onNewRowCancel}
                    />
                  )
              }
            </div>
          </div>
          <div className="sidebar-pane" hidden={activeTab !== 'recent'}>
            <RecentPane
              tree={tree}
              activeFile={activeFile}
              orgSlug={orgSlug}
              wsHandle={workspace && workspace.handle}
              onOpenFile={onOpenFile}
            />
          </div>
        </div>
        <div className="drawer-foot">
          {current && (
            <button className="org-switcher" onClick={() => setOrgSheetOpen(true)} title="Switch organization">
              <span className="org-name">{current.slug}</span>
              <svg className="org-caret" viewBox="0 0 12 12" fill="none">
                <path d="M3 7.5 L6 4.5 L9 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          {user && (
            <button className="user-btn" onClick={() => setUserSheetOpen(true)} title="Account">
              <span className="user-chip">{user.initials}</span>
              <svg className="user-caret" viewBox="0 0 12 12" fill="none">
                <path d="M3 7.5 L6 4.5 L9 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Bottom sheets rendered OUTSIDE the drawer to avoid transform containment */}
      <div className={'mobile-sheet-backdrop' + (orgSheetOpen ? ' is-open' : '')} onClick={() => setOrgSheetOpen(false)} />
      <div className={'mobile-sheet' + (orgSheetOpen ? ' is-open' : '')}>
        <div className="mobile-sheet-handle" />
        <div className="mobile-sheet-section">
          <div className="mobile-sheet-label">Organizations</div>
          {orgs.map((o) => (
            <button
              key={o.slug}
              className={'mobile-sheet-row' + (o.slug === (org && org.slug) ? ' is-current' : '')}
              onClick={() => { onSwitchOrg(o); setOrgSheetOpen(false); onClose(); }}
            >
              <span className="mobile-sheet-row-label">{o.name}</span>
              <span className="mobile-sheet-toggle">{(o.workspaces || []).length} ws</span>
            </button>
          ))}
        </div>
        <div className="mobile-sheet-section">
          {current && current.role === 'owner' && (
            <button className="mobile-sheet-row" onClick={() => { setView('members'); setOrgSheetOpen(false); onClose(); }}>
              <span className="mobile-sheet-row-label">Members of {current.name}</span>
            </button>
          )}
          <a className="mobile-sheet-row" href="/app/orgs" style={{ textDecoration: 'none' }}>
            <span className="mobile-sheet-row-label">+ New organization…</span>
          </a>
        </div>
      </div>

      <div className={'mobile-sheet-backdrop' + (userSheetOpen ? ' is-open' : '')} onClick={() => setUserSheetOpen(false)} />
      <div className={'mobile-sheet' + (userSheetOpen ? ' is-open' : '')}>
        <div className="mobile-sheet-handle" />
        <div className="mobile-sheet-section">
          <div className="mobile-sheet-label">{user ? user.name : ''}</div>
          <div className="mobile-sheet-email">{user ? user.email : ''}</div>
        </div>
        <div className="mobile-sheet-section">
          <a className="mobile-sheet-row" href="/app/tokens" style={{ textDecoration: 'none' }}>
            <span className="mobile-sheet-row-label">Manage tokens…</span>
          </a>
          <button className="mobile-sheet-row" onClick={() => { setUserSheetOpen(false); onSignOut(); }}>
            <span className="mobile-sheet-row-label mobile-sheet-danger">Sign out</span>
          </button>
        </div>
      </div>
    </>
  );
}

// ---------- Landing ----------

function Landing({ onSignIn }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);

  const handleSignIn = (provider) => {
    setError(null);
    setLoading(provider);
    onSignIn(provider).then((err) => {
      setLoading(null);
      if (err) setError(err);
    });
  };

  return (
    <div className="screen-center">
      <div className="auth-block">
        <div className="wordmark">mindex</div>
        <div className="tagline">Your docs. Read anywhere.</div>
        <div style={{ height: 32 }} />
        <button className="btn-primary" onClick={() => handleSignIn('github')} disabled={!!loading}>
          {loading === 'github' ? 'Redirecting…' : 'Continue with GitHub'}
        </button>
        <div style={{ height: 8 }} />
        <button className="btn-secondary" onClick={() => handleSignIn('google')} disabled={!!loading}>
          {loading === 'google' ? 'Redirecting…' : 'Continue with Google'}
        </button>
        {__MINDEX_DEV__ && (
          <>
            <div style={{ height: 8 }} />
            <button className="btn-secondary" onClick={() => { window.location.href = '/app/dev-login'; }}>
              Continue as Dev User
            </button>
          </>
        )}
        {error && (
          <div style={{ marginTop: 16, fontSize: 13, color: '#c00' }}>{error}</div>
        )}
        <div style={{ height: 24 }} />
        <div className="legal">
          By signing in you agree to the{" "}
          <a href="#" className="legal-link">Terms of Service</a>.
        </div>
      </div>
    </div>
  );
}

// ---------- Org Picker ----------

function OrgPicker({ orgs, onPick }) {
  return (
    <div className="screen-center">
      <div className="auth-block">
        <div className="picker-heading">Choose an organization</div>
        <div className="picker-sub">{"You're a member of multiple orgs."}</div>
        <div style={{ height: 16 }} />
        <div className="org-list">
          {orgs.map((o) => (
            <button key={o.slug} className="org-row" onClick={() => onPick(o)}>
              {o.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────  Search input  ─────────────────────────

function SearchInput({ query, setQuery, matchedCount, totalCount }) {
  return (
    <div className="tree-search">
      <input
        type="text"
        className="tree-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setQuery('');
            e.currentTarget.blur();
          }
        }}
        placeholder="Filter files…"
        autoComplete="off"
        spellCheck={false}
        aria-label="Filter files"
      />
      {query.trim() !== '' && (
        <span className="tree-search-count" aria-live="polite">
          {matchedCount} of {totalCount}
        </span>
      )}
    </div>
  );
}

// ---------- Tree context menu ----------

function TreeContextMenu({ open, x, y, items, onPick, onClose }) {
  const ref = useRef(null);
  const [clamped, setClamped] = useState(null);

  // Re-clamp once the menu has measured itself. useLayoutEffect runs before
  // paint so the clamped position is the first frame the user sees.
  useLayoutEffect(() => {
    if (!open) { setClamped(null); return; }
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setClamped(clampMenuPosition({
      x, y,
      menuWidth: rect.width,
      menuHeight: rect.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    }));
  }, [open, x, y]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    const onBlur = () => onClose();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, [open, onClose]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="tree-ctx-menu"
      style={{ position: 'fixed', left: (clamped ?? { x, y }).x, top: (clamped ?? { x, y }).y }}
      role="menu"
    >
      {items.map((it, i) => (
        it.section
          ? <div key={'section-' + i} className="tree-ctx-section" role="presentation">{it.section}</div>
          : it.separator
            ? <div key={'sep-' + i} className="tree-ctx-sep" />
            : (
              <button
                key={it.action}
                type="button"
                role="menuitem"
                className={
                  'tree-ctx-item'
                  + (it.danger ? ' is-danger' : '')
                  + (it.disabled ? ' is-disabled' : '')
                }
                disabled={!!it.disabled}
                onClick={() => { if (!it.disabled) { onPick(it.action); onClose(); } }}
              >
                <span className="tree-ctx-label">{it.label}</span>
                {it.shortcut && <span className="tree-ctx-shortcut">{it.shortcut}</span>}
              </button>
            )
      ))}
    </div>,
    document.body,
  );
}

// ---------- Rename Input ----------

function RenameInput({ initial, siblingNames: siblingsProp, onCommit, onCancel, kind = 'file' }) {
  const [val, setVal] = useState(initial);
  const ref = useRef(null);
  // Sibling names minus the initial value — renaming to the current name is a no-op, not a collision.
  const siblings = useMemo(() => {
    const s = new Set(siblingsProp);
    s.delete(initial);
    return s;
  }, [siblingsProp, initial]);
  const validation = validateBasename(val, siblings, { kind });
  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
      // Select the base portion (before the .md extension) so the user can re-type quickly.
      const dot = initial.lastIndexOf('.');
      const end = dot > 0 ? dot : initial.length;
      ref.current.setSelectionRange(0, end);
    }
  }, []);
  return (
    <span className="tree-rename-wrap">
      <input
        ref={ref}
        className={'tree-rename-input' + (validation.ok ? '' : ' is-invalid')}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (validation.ok && val !== initial) onCommit(val);
            else if (val === initial) onCancel();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => {
          // Defer cancel by one frame so a transient blur (e.g., opening
          // the context menu and dismissing it) that returns focus to the
          // input does NOT discard the user's typed value.
          requestAnimationFrame(() => {
            if (ref.current && document.activeElement !== ref.current) {
              onCancel();
            }
          });
        }}
      />
      {!validation.ok && (
        <span className="tree-rename-err">{validation.reason}</span>
      )}
    </span>
  );
}

// ---------- Confirm Modal ----------

function ConfirmModal({ open, title, body, confirmLabel, danger, onConfirm, onCancel }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      if (e.key === 'Enter')  { e.preventDefault(); onConfirm(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <div className="modal-confirm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-title">{title}</div>
        <div className="modal-body">{body}</div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className={'btn ' + (danger ? 'btn-danger' : 'btn-primary')}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function UnsavedChangesModal({ open, onSave, onDiscard, onStay }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onStay(); }
      if (e.key === 'Enter') {
        // Don't hijack Enter while a text field is focused — Enter there is the
        // field's own intent, not a Save. (The CodeMirror surface is
        // contenteditable, so this also avoids saving mid-keystroke.)
        const el = document.activeElement;
        const tag = el && el.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (el && el.isContentEditable)) return;
        e.preventDefault(); onSave();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onSave, onStay]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onMouseDown={onStay}>
      <div className="modal-confirm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-title">Unsaved changes</div>
        <div className="modal-body">You have unsaved changes. Save them before switching files?</div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onStay}>Stay</button>
          <button type="button" className="btn btn-danger" onClick={onDiscard}>Discard</button>
          <button type="button" className="btn btn-primary" onClick={onSave} autoFocus>Save</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------- Collision Modal (M4) — rename on drop-into-existing path ----------

function CollisionModal({ open, existingPath, proposedPath, onRename, onCancel }) {
  const [value, setValue] = useState(proposedPath || '');
  useEffect(() => { if (open) setValue(proposedPath || ''); }, [open, proposedPath]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);
  if (!open) return null;
  const canSubmit = value.trim() !== '' && value !== existingPath;
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <div className="modal-confirm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-title">A file already exists at {existingPath}</div>
        <div className="modal-body">
          <p style={{ marginTop: 0, opacity: 0.7 }}>Choose a new path for the incoming file.</p>
          <input
            autoFocus
            className="tree-rename-input"
            style={{ display: 'block', width: '100%' }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canSubmit) onRename(value); } }}
          />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={() => onRename(value)}
          >
            Rename
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------- Folder Picker Modal (M4) — "Move to…" multi-select action ----------

function FolderPickerModal({ open, folders, onPick, onCancel }) {
  const [filter, setFilter] = useState('');
  useEffect(() => { if (open) setFilter(''); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);
  if (!open) return null;
  const lower = filter.toLowerCase();
  const filtered = (folders || []).filter((f) => f.toLowerCase().includes(lower));
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <div className="modal-confirm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-title">Move to folder</div>
        <div className="modal-body">
          <input
            autoFocus
            className="tree-rename-input"
            style={{ display: 'block', width: '100%', marginBottom: 8 }}
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <ul className="folder-picker-list" style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 280, overflow: 'auto' }}>
            <li>
              <button type="button" className="btn" style={{ width: '100%', textAlign: 'left' }} onClick={() => onPick('')}>
                / (root)
              </button>
            </li>
            {filtered.map((f) => (
              <li key={f}>
                <button type="button" className="btn" style={{ width: '100%', textAlign: 'left' }} onClick={() => onPick(f)}>
                  {f}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------- New Folder Modal ----------

function NewFolderModal({ open, parentPath, existingSiblings, onSubmit, onCancel }) {
  const [folder, setFolder] = useState('');
  const [file, setFile] = useState('index.md');
  useEffect(() => { if (open) { setFolder(''); setFile('index.md'); } }, [open]);
  // Defence-in-depth before server-side path validation.
  const folderCheck = validateBasename(folder, existingSiblings, { kind: 'folder' });
  const fileCheck = validateBasename(file, new Set());
  const canSubmit = folderCheck.ok && fileCheck.ok;
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <div className="modal-confirm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-title">New folder</div>
        <div className="modal-body">
          <p style={{ marginTop: 0, opacity: 0.7 }}>
            mindex folders are path prefixes — a folder exists when a file lives in it.
            Give it a name and a first file.
          </p>
          <label style={{ display: 'block', marginBottom: 12 }}>
            Folder name
            <input
              autoFocus
              className="tree-rename-input"
              style={{ display: 'block', width: '100%', marginTop: 4 }}
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canSubmit) onSubmit(folder.trim(), file.trim()); } }}
            />
            {!folderCheck.ok && folder !== '' && <span className="tree-rename-err">{folderCheck.reason}</span>}
          </label>
          <label style={{ display: 'block' }}>
            First file
            <input
              className="tree-rename-input"
              style={{ display: 'block', width: '100%', marginTop: 4 }}
              value={file}
              onChange={(e) => setFile(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canSubmit) onSubmit(folder.trim(), file.trim()); } }}
            />
            {!fileCheck.ok && <span className="tree-rename-err">{fileCheck.reason}</span>}
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={() => onSubmit(folder.trim(), file.trim())}
          >
            Create
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------- File Tree ----------

function FileTree({
  tree, activeFile, onOpenFile,
  expanded, setExpanded,
  focusedPath, setFocusedPath,
  onKeyDown,
  query, autoExpanded,
  orgSlug, wsHandle,
  // M3:
  fullTree,                         // unfiltered tree for sibling-collision validation
  onRowContextMenu, onTreeBackgroundContextMenu,
  renamingPath, newRow,
  onRenameCommit, onRenameCancel,
  onNewRowCommit, onNewRowCancel,
  // M4 — multi-select + DnD (optional; back-compat for MobileDrawer caller):
  selectedPaths,
  selectionAnchor,
  onSelectionChange,
  onAnchorChange,
  flatRowsRef,
  onDragStartRow,
  draggingType,
  draggingSource,
  draggingPaths,
  onDropOnFolder,
}) {
  // Safe defaults when caller (MobileDrawer) doesn't thread M4 props.
  const _selectedPaths = selectedPaths || new Set();
  const _onSelectionChange = onSelectionChange || (() => {});
  const _onAnchorChange = onAnchorChange || (() => {});
  const _onDragStartRow = onDragStartRow || (() => {});
  const _onDropOnFolder = onDropOnFolder || (() => {});
  const _draggingType = draggingType || { current: null };
  const _draggingSource = draggingSource || { current: null };
  const _draggingPaths = draggingPaths || { current: null };
  const dndEnabled = !!onDropOnFolder; // gate handlers when caller opted in

  const [draggingPath, setDraggingPath] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // folder path or '' for root

  // Mobile press-and-hold → context menu. Touch has no right-click, so the
  // only way to reach "Open in new tab" on touch is a long press. Gated to the
  // drawer caller (no DnD); on desktop, right-click already works and a touch
  // gesture must not interfere with drag. One active touch at a time, so a
  // single shared ref is safe across rows. `fired` is read by each row's
  // onClick to swallow the tap that follows the release.
  const longPress = useRef({ timer: null, fired: false, x: 0, y: 0 });
  const longPressEnabled = !dndEnabled && !!onRowContextMenu;
  const fileLongPressHandlers = (path) => (longPressEnabled ? {
    onTouchStart: (e) => {
      const t = e.touches[0];
      const st = longPress.current;
      st.fired = false; st.x = t.clientX; st.y = t.clientY;
      clearTimeout(st.timer);
      st.timer = setTimeout(() => {
        st.fired = true;
        onRowContextMenu({ preventDefault: () => {}, clientX: st.x, clientY: st.y }, 'file', path);
      }, 500);
    },
    onTouchMove: (e) => {
      const t = e.touches[0];
      const st = longPress.current;
      if (Math.abs(t.clientX - st.x) > 10 || Math.abs(t.clientY - st.y) > 10) clearTimeout(st.timer);
    },
    onTouchEnd: () => clearTimeout(longPress.current.timer),
    onTouchCancel: () => clearTimeout(longPress.current.timer),
  } : null);
  // Clear any pending long-press timer if the tree unmounts mid-press (e.g. the
  // drawer closes), so it can't fire onRowContextMenu after teardown.
  useEffect(() => () => clearTimeout(longPress.current.timer), []);

  // Default fullTree to tree when caller doesn't pass it (back-compat).
  const validationTree = fullTree || tree;
  // Under search filter, force-expanded set replaces the user's manual
  // `expanded` for both visibility and chevron state. Empty query →
  // autoExpanded is null and we use `expanded` as today.
  const effectiveExpanded = autoExpanded || expanded;
  // If focusedPath isn't a visible row, fall back to the first row
  // so Tab into the tree always lands somewhere sensible (mirrors
  // the same fallback in the key handler).
  const flat = useMemo(() => flattenVisible(tree, effectiveExpanded), [tree, effectiveExpanded]);
  const effectiveFocused = (focusedPath && indexOfPath(flat, focusedPath) >= 0)
    ? focusedPath
    : (flat[0] ? flat[0].path : null);

  // Mirror the current visible flat-row order into the parent's ref so
  // range selection (Shift-click) can resolve indices without re-flattening.
  useEffect(() => {
    if (flatRowsRef) flatRowsRef.current = flat;
  }, [flat]);

  // Move browser focus to the focused row when state changes — but
  // only if focus is already inside the tree. Avoids stealing focus
  // from inputs / the doc pane.
  useEffect(() => {
    if (!effectiveFocused) return;
    const treeEl = document.querySelector('.tree');
    const inTree = treeEl && treeEl.contains(document.activeElement);
    if (!inTree) return;
    const row = document.querySelector(`[data-path="${CSS.escape(effectiveFocused)}"]`);
    if (row && typeof row.focus === 'function') row.focus({ preventScroll: true });
  }, [effectiveFocused]);

  // Auto-scroll the focused row into view (always — even if focus
  // isn't inside the tree, we want the row visible after a click
  // or keyboard jump).
  useEffect(() => {
    if (!effectiveFocused) return;
    const row = document.querySelector(`[data-path="${CSS.escape(effectiveFocused)}"]`);
    if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [effectiveFocused]);

  function renderNodes(nodes, depth, parentPath) {
    return sortNodes(nodes).map((node) => {
      const path = parentPath ? `${parentPath}/${node.name}` : node.name;
      if (node.type === 'folder') {
        const isOpen = effectiveExpanded.has(path);
        const isFocused = path === effectiveFocused;
        return (
          <React.Fragment key={'f-' + path}>
            <div
              className={
                'tree-row tree-folder'
                + (isFocused ? ' is-focused' : '')
                + (dropTarget === path ? ' is-drop-target' : '')
                + (draggingPath === path ? ' is-dragging' : '')
              }
              style={{ paddingLeft: 10 + depth * 14 }}
              data-path={path}
              tabIndex={isFocused ? 0 : -1}
              aria-selected={isFocused}
              draggable={dndEnabled ? true : undefined}
              onDragStart={dndEnabled ? (e) => {
                setDraggingPath(path);
                _onDragStartRow({ path, type: 'folder' });
                try {
                  e.dataTransfer.setData('application/x-mindex-folder', path);
                  e.dataTransfer.effectAllowed = 'move';
                } catch { /* test envs may throw on setData */ }
              } : undefined}
              onDragOver={dndEnabled ? (e) => {
                const sourcePath = _draggingSource.current;
                const sourceType = _draggingType.current;
                if (!sourcePath || !sourceType) return;
                const v = isValidDrop({ sourcePath, sourceType, targetFolder: path });
                if (v.ok) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDropTarget(path);
                }
              } : undefined}
              onDragLeave={dndEnabled ? () => {
                if (dropTarget === path) setDropTarget(null);
              } : undefined}
              onDrop={dndEnabled ? (e) => {
                e.preventDefault();
                setDropTarget(null);
                _onDropOnFolder(path);
              } : undefined}
              onDragEnd={dndEnabled ? () => {
                setDraggingPath(null);
                setDropTarget(null);
                _draggingType.current = null;
                _draggingSource.current = null;
                _draggingPaths.current = null;
              } : undefined}
              onClick={() => {
                setFocusedPath(path);
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(path)) next.delete(path); else next.add(path);
                  return next;
                });
              }}
              onContextMenu={(e) => onRowContextMenu && onRowContextMenu(e, 'folder', path)}
              role="treeitem"
              aria-expanded={isOpen}
            >
              <span className={'chev' + (isOpen ? ' is-open' : '')}>{SVG_CHEV}</span>
              <span className="ic">{isOpen ? SVG_FOLDER_OPEN : SVG_FOLDER_CLOSED}</span>
              {renamingPath === path
                ? <RenameInput
                    initial={node.name}
                    kind="folder"
                    siblingNames={siblingNames(validationTree, parentPath)}
                    onCommit={(newBasename) => onRenameCommit(path, newBasename)}
                    onCancel={onRenameCancel}
                  />
                : <span className="tree-label">{highlightMatch(node.name, query)}</span>
              }
            </div>
            {isOpen && <div className="tree-children">{renderNodes(node.children, depth + 1, path)}</div>}
            {isOpen && newRow && newRow.parentPath === path && (
              <div className="tree-row tree-file" style={{ paddingLeft: 10 + (depth + 1) * 14 }}>
                <span className="chev is-spacer">{SVG_CHEV}</span>
                <span className="ic">{SVG_FILE}</span>
                <RenameInput
                  initial={newRow.initialValue}
                  siblingNames={siblingNames(validationTree, path)}
                  onCommit={(basename) => onNewRowCommit(path, basename)}
                  onCancel={onNewRowCancel}
                />
              </div>
            )}
          </React.Fragment>
        );
      } else {
        const isActive = activeFile === node.path;
        const isFocused = node.path === effectiveFocused;
        const isSelected = _selectedPaths.has(node.path);
        return (
          <a
            key={'file-' + node.path}
            href={buildRoute({ orgSlug, wsHandle, filePath: node.path })}
            className={
              'tree-row tree-file'
              + (isActive ? ' is-active' : '')
              + (isFocused ? ' is-focused' : '')
              + (isSelected ? ' is-selected' : '')
              + (draggingPath === node.path ? ' is-dragging' : '')
            }
            style={{ paddingLeft: 10 + depth * 14 }}
            data-path={node.path}
            tabIndex={isFocused ? 0 : -1}
            aria-selected={isFocused}
            draggable={dndEnabled ? true : undefined}
            onDragStart={dndEnabled ? (e) => {
              const draggedSet = _selectedPaths.has(node.path) && _selectedPaths.size > 0
                ? _selectedPaths
                : singleSelect(node.path);
              if (!_selectedPaths.has(node.path)) _onSelectionChange(draggedSet);
              setDraggingPath(node.path);
              _onDragStartRow({ path: node.path, type: 'file', paths: [...draggedSet] });
              try {
                e.dataTransfer.setData(
                  'application/x-mindex-paths',
                  JSON.stringify([...draggedSet]),
                );
                e.dataTransfer.effectAllowed = 'move';
              } catch { /* test envs */ }
            } : undefined}
            onDragEnd={dndEnabled ? () => {
              setDraggingPath(null);
              setDropTarget(null);
              _draggingType.current = null;
              _draggingSource.current = null;
              _draggingPaths.current = null;
            } : undefined}
            onClick={(e) => {
              // Swallow the tap that fires when a long-press is released — the
              // press-and-hold already opened the context menu; don't also open
              // the file.
              if (longPress.current.fired) { longPress.current.fired = false; e.preventDefault(); return; }
              // Modifier semantics: shift / cmd / ctrl now repurposed for
              // multi-select. Plain modified click (open-in-new-tab) is
              // only honored when DnD/multi-select isn't wired (mobile drawer).
              if (!dndEnabled && isModifiedClick(e)) return;
              e.preventDefault();
              setFocusedPath(node.path);

              if (dndEnabled && e.shiftKey && selectionAnchor && flatRowsRef && flatRowsRef.current) {
                _onSelectionChange(selectRange(flatRowsRef.current, selectionAnchor, node.path, _selectedPaths));
                return;
              }
              if (dndEnabled && (e.metaKey || e.ctrlKey)) {
                _onSelectionChange(toggleSelection(_selectedPaths, node.path));
                _onAnchorChange(node.path);
                return;
              }
              if (dndEnabled) {
                _onSelectionChange(singleSelect(node.path));
                _onAnchorChange(node.path);
              }
              onOpenFile(node.path);
            }}
            onContextMenu={(e) => onRowContextMenu && onRowContextMenu(e, 'file', node.path)}
            {...(fileLongPressHandlers(node.path) || {})}
            role="treeitem"
          >
            <span className="chev is-spacer">{SVG_CHEV}</span>
            <span className="ic">{SVG_FILE}</span>
            {renamingPath === node.path
              ? <RenameInput
                  initial={node.name}
                  siblingNames={siblingNames(validationTree, parentPath)}
                  onCommit={(newBasename) => onRenameCommit(node.path, newBasename)}
                  onCancel={onRenameCancel}
                />
              : <span className="tree-label">{highlightMatch(node.name, query)}</span>
            }
          </a>
        );
      }
    });
  }
  return (
    <div
      className={'tree' + (dropTarget === '' ? ' is-drop-target' : '')}
      role="tree"
      onKeyDown={onKeyDown}
      onContextMenu={(e) => {
        if (e.target === e.currentTarget && onTreeBackgroundContextMenu) {
          onTreeBackgroundContextMenu(e);
        }
      }}
      onClick={(e) => {
        if (dndEnabled && e.target === e.currentTarget) {
          _onSelectionChange(clearSelection());
          _onAnchorChange(null);
        }
      }}
      onDragOver={dndEnabled ? (e) => {
        if (e.target !== e.currentTarget) return;
        const sourcePath = _draggingSource.current;
        const sourceType = _draggingType.current;
        if (!sourcePath || !sourceType) return;
        const v = isValidDrop({ sourcePath, sourceType, targetFolder: '' });
        if (v.ok) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDropTarget('');
        }
      } : undefined}
      onDragLeave={dndEnabled ? (e) => {
        if (e.target !== e.currentTarget) return;
        if (dropTarget === '') setDropTarget(null);
      } : undefined}
      onDrop={dndEnabled ? (e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        setDropTarget(null);
        _onDropOnFolder('');
      } : undefined}
    >
      {newRow && newRow.parentPath === '' && (
        <div className="tree-row tree-file" style={{ paddingLeft: 10 }}>
          <span className="chev is-spacer">{SVG_CHEV}</span>
          <span className="ic">{SVG_FILE}</span>
          <RenameInput
            initial={newRow.initialValue}
            siblingNames={siblingNames(validationTree, '')}
            onCommit={(basename) => onNewRowCommit('', basename)}
            onCancel={onNewRowCancel}
          />
        </div>
      )}
      {renderNodes(tree, 0, '')}
    </div>
  );
}

// ---------- Workspace switcher ----------

function WorkspaceSwitcher({ workspaces, current, onPick, fileCount, onExpandAll, onCollapseAll, orgSlug }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const ref = useRef(null);
  const searchRef = useRef(null);

  const { filterWorkspaces, clampHighlight } = window.mindexWS;
  const filtered = useMemo(() => filterWorkspaces(workspaces, query), [workspaces, query]);

  useEffect(() => { setHighlight(0); }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Global hotkey: Cmd/Ctrl+K opens the popover.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key !== 'k' && e.key !== 'K') return;
      const t = e.target;
      const editable = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (editable) return;
      e.preventDefault();
      setOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => clampHighlight(h + 1, filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => clampHighlight(h - 1, filtered.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[highlight];
      if (pick) { onPick(pick); setOpen(false); setQuery(''); }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const currentLabel = (current && (current.display_name || current.handle)) || 'Workspace';

  return (
    <div className="sidebar-head" ref={ref}>
      <button
        className={'ws-switcher' + (open ? ' is-open' : '')}
        onClick={() => setOpen((o) => !o)}
        title="Switch workspace (Cmd/Ctrl+K)"
      >
        <span className="ws-dot" />
        <span className="ws-name">{currentLabel}</span>
        <svg className="ws-caret" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5 L6 7.5 L9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <span className="ws-counts">{fileCount} files</span>
      <div className="actions">
        <button className="act" onClick={onCollapseAll} title="Collapse all">
          <svg viewBox="0 0 12 12" fill="none"><path d="M2 6h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
        </button>
        <button className="act" onClick={onExpandAll} title="Expand all">
          <svg viewBox="0 0 12 12" fill="none"><path d="M2 6h8M6 2v8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
        </button>
      </div>
      {open && (
        <div className="ws-pop is-open" role="menu">
          <div className="ws-pop-head">Workspaces</div>
          <div className="ws-pop-search-wrap">
            <input
              ref={searchRef}
              className="tree-search-input ws-pop-search"
              type="text"
              placeholder="Filter workspaces\u2026"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          <div className="ws-pop-list">
            {filtered.length === 0 && <div className="ws-pop-empty">No matches</div>}
            {filtered.map((w, i) => {
              const isCurrent = w.handle === (current && current.handle);
              const isHl = i === highlight;
              return (
                <a
                  key={w.handle}
                  className={'ws-pop-row' + (isCurrent ? ' is-current' : '') + (isHl ? ' is-highlight' : '')}
                  href={buildRoute({ orgSlug, wsHandle: w.handle, filePath: null })}
                  onClick={(e) => {
                    if (isModifiedClick(e)) return;
                    e.preventDefault();
                    onPick(w);
                    setOpen(false);
                    setQuery('');
                  }}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <span className="ws-dot" />
                  <span className="ws-pop-label">{w.display_name || w.handle}</span>
                </a>
              );
            })}
          </div>
          <a className="ws-pop-foot" href="/app/workspaces">+ New workspace{"\u2026"}</a>
        </div>
      )}
    </div>
  );
}

// ---------- User menu (sidebar foot, right) ----------

function UserMenu({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref}>
      <button
        className={'user-btn' + (open ? ' is-open' : '')}
        onClick={() => setOpen((o) => !o)}
        title="Account"
      >
        <span className="user-chip">{user.initials}</span>
        <svg className="user-caret" viewBox="0 0 12 12" fill="none">
          <path d="M3 7.5 L6 4.5 L9 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className={'user-menu' + (open ? ' is-open' : '')} role="menu">
        <div className="user-menu-id">
          <div className="user-menu-name">{user.name}</div>
          <div className="user-menu-email">{user.email}</div>
        </div>
        <div className="user-menu-section">
          <a className="user-menu-row" href="/app/tokens">Manage tokens{"\u2026"}</a>
        </div>
        <div className="user-menu-divider" />
        <button className="user-menu-row is-danger" onClick={() => { setOpen(false); onSignOut(); }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// ---------- Org switcher (sidebar foot, left) ----------

function OrgSwitcher({ orgs, currentOrgSlug, onSwitchOrg, onOpenMembers }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = orgs.find((o) => o.slug === currentOrgSlug);
  if (!current) return null;

  return (
    <div ref={ref} style={{ flex: '0 1 auto', minWidth: 0, position: 'static' }}>
      <button
        className={'org-switcher' + (open ? ' is-open' : '')}
        onClick={() => setOpen((o) => !o)}
        title="Switch organization"
      >
        <span className="org-name">{current.slug}</span>
        <svg className="org-caret" viewBox="0 0 12 12" fill="none">
          <path d="M3 7.5 L6 4.5 L9 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className={'org-pop' + (open ? ' is-open' : '')} role="menu">
        <div className="org-pop-head">Organizations</div>
        <div className="org-pop-list">
          {orgs.map((o) => {
            const isCurrent = o.slug === currentOrgSlug;
            return (
              <button
                key={o.slug}
                className={'org-pop-row' + (isCurrent ? ' is-current' : '')}
                onClick={() => { onSwitchOrg(o); setOpen(false); }}
              >
                <span className="org-pop-name">{o.name}</span>
                <span className="org-pop-handle">{(o.workspaces || []).length} ws</span>
              </button>
            );
          })}
        </div>
        {onOpenMembers && current.role === 'owner' && (
          <button className="org-pop-foot" onClick={() => { onOpenMembers(); setOpen(false); }}>
            Members of {current.name}
          </button>
        )}
        <a className="org-pop-foot" href="/app/orgs">+ New organization{"\u2026"}</a>
      </div>
    </div>
  );
}

// ---------- Document pane ----------

// ---------- Share Modal ----------

function ShareModal({ orgSlug, workspaceHandle, filePath, onClose }) {
  const [phase, setPhase] = useState('form');  // 'form' | 'success'
  const [expiresIn, setExpiresIn] = useState(7 * 86400);
  const [maxViews, setMaxViews] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);  // { url, expires_at }
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus the URL input on success so the user can Cmd-A / Cmd-C immediately.
  useEffect(() => {
    if (phase === 'success' && inputRef.current) {
      inputRef.current.select();
    }
  }, [phase]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        org: orgSlug,
        workspace: workspaceHandle,
        path: filePath,
        expires_in_seconds: expiresIn,
      };
      if (maxViews) body.max_views = parseInt(maxViews, 10);
      if (note) body.note = note;
      const r = await fetch('/app/api/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.status === 401) { window.location.href = '/'; return; }
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        const code = (data && data.error) || 'error';
        if (code === 'SHARE_TARGET_NOT_FOUND') setError('That file no longer exists. Refresh the page and try again.');
        else if (code === 'SHARE_EXPIRY_OUT_OF_RANGE') setError("Couldn't pick that expiry. Try a different value.");
        else if (code === 'SHARE_NOTE_TOO_LONG') setError('Note is too long (max 200 characters).');
        else if (code === 'rate_limited') setError('Too many shares created recently. Try again in a minute.');
        else setError("Couldn't create the share. Please try again.");
        setSubmitting(false);
        return;
      }
      const data = await r.json();
      setCreated({ url: data.url, expires_at: data.expires_at });
      setPhase('success');
    } catch (err) {
      setError("Couldn't reach the server. Check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  const onCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select the input.
      inputRef.current && inputRef.current.select();
    }
  };

  return (
    <div className="share-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="share-modal" role="dialog" aria-modal="true">
        {phase === 'form' && (
          <form onSubmit={submit}>
            <div className="share-title">Share this file</div>
            <div className="share-sub">{workspaceHandle} / {filePath}</div>
            <label className="share-field">
              <span>Expires</span>
              <select value={expiresIn} onChange={(e) => setExpiresIn(parseInt(e.target.value, 10))}>
                <option value={3600}>1 hour</option>
                <option value={86400}>24 hours</option>
                <option value={7 * 86400}>7 days</option>
                <option value={30 * 86400}>30 days</option>
                <option value={365 * 86400}>1 year</option>
              </select>
            </label>
            <label className="share-field">
              <span>Max views (optional)</span>
              <input
                type="number"
                min={1}
                placeholder="unlimited"
                value={maxViews}
                onChange={(e) => setMaxViews(e.target.value)}
              />
            </label>
            <label className="share-field">
              <span>Note (optional)</span>
              <input
                type="text"
                maxLength={200}
                placeholder="creator-only label"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            {error && <div className="share-error">{error}</div>}
            <div className="share-actions">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create link'}
              </button>
            </div>
          </form>
        )}
        {phase === 'success' && created && (
          <div>
            <div className="share-title">Share link created</div>
            <div className="share-warning">
              ⚠ This URL is shown once. Anyone with the URL can read this file
              until it expires or is revoked.
            </div>
            <input
              ref={inputRef}
              type="text"
              readOnly
              className="share-url"
              value={created.url}
              onClick={(e) => e.target.select()}
            />
            <div className="share-meta">
              Expires {new Date(created.expires_at).toLocaleString()}
            </div>
            <div className="share-actions">
              <a className="share-manage" href="/app/shares" target="_blank" rel="noopener noreferrer">Manage shares</a>
              <button type="button" className="btn-secondary" onClick={onClose}>Done</button>
              <button type="button" className="btn-primary" onClick={onCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function OutlineRail({ headings, activeId, onJumpTo }) {
  if (!headings || headings.length === 0) return null;
  return (
    <aside className="outline-rail" aria-label="Page outline">
      <div className="outline-head">On this page</div>
      <ol className="outline-list">
        {headings.map((h) => (
          <li
            key={h.id + ':' + h.level}
            className={`outline-item outline-l${h.level} ${h.id === activeId ? 'is-active' : ''}`}
          >
            <a href={`#${h.id}`} onClick={(e) => onJumpTo(e, h.id)}>{h.text}</a>
          </li>
        ))}
      </ol>
    </aside>
  );
}

// ── Related panel — link-based "Heads Up". Mirrors OutlineRail's in-card
// aside (NOT the floating HistorySheet, which uses shadow/animation that
// violate the One-Gray rule). similar[] is reserved for the future semantic
// upgrade and is never rendered while empty. See spec §4.4 / §4.5.
function RelatedGroup({ title, items, onOpenFile }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="related-group">
      <div className="related-head">{title}</div>
      <ul className="related-list">
        {items.map((it) => (
          <li className="related-item" key={it.path}>
            <button type="button" className="related-link" onClick={() => onOpenFile(it.path)}>
              {it.path.split('/').pop()}
              {it.count > 1 && <span className="related-count"> · {it.count}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RelatedPanel({ orgSlug, workspaceHandle, filePath, visible, onOpenFile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!visible || !filePath || !orgSlug || !workspaceHandle) return;
    let cancelled = false;
    setLoading(true); setError(false); setData(null);
    window.mindexApiMut.apiRelated(orgSlug, workspaceHandle, filePath)
      .then((r) => { if (!cancelled) { setData(r); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [orgSlug, workspaceHandle, filePath, visible]);

  if (!visible) return null;

  const isEmpty = data
    && data.outgoing.length === 0
    && data.incoming.length === 0
    && data.broken.length === 0;

  return (
    <aside className="related-panel" aria-label="Related notes">
      <div className="related-title">Related</div>
      {loading && <div className="related-empty">Loading{"…"}</div>}
      {error && <div className="related-empty">Could not load related notes.</div>}
      {!loading && !error && data && (
        <>
          <RelatedGroup title="Linked from here" items={data.outgoing} onOpenFile={onOpenFile} />
          <RelatedGroup title="Linked to here" items={data.incoming} onOpenFile={onOpenFile} />
          {data.broken.length > 0 && (
            <div className="related-group">
              <div className="related-head">Broken links</div>
              <ul className="related-list">
                {data.broken.map((b) => (
                  <li className="related-item is-broken" key={b.text}>
                    {b.text}{b.count > 1 && <span className="related-count"> · {b.count}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isEmpty && <div className="related-empty">No linked notes.</div>}
        </>
      )}
    </aside>
  );
}

function PropertiesPanel({ frontmatter }) {
  if (!frontmatter || (typeof frontmatter === 'object' && Object.keys(frontmatter).length === 0)) {
    return null;
  }

  // Special unparseable case from the pipeline.
  if (frontmatter.frontmatter === 'unparseable') {
    return (
      <aside className="props">
        <div className="props-row">
          <span className="props-key">frontmatter</span>
          <span className="props-value">unparseable</span>
        </div>
      </aside>
    );
  }

  const renderValue = (key, value) => {
    if (key === 'tags' && Array.isArray(value)) {
      return (
        <span className="props-tags">
          {value.map((t) => <span className="props-tag" key={String(t)}>{String(t)}</span>)}
        </span>
      );
    }
    if (Array.isArray(value)) {
      // Locked decision: arrays of objects flatten to a nested list (roadmap §"Locked decisions" Tier 1 #6).
      if (value.length > 0 && value.every((v) => v && typeof v === 'object' && !Array.isArray(v))) {
        return (
          <ul className="props-nested">
            {value.map((obj, i) => (
              <li key={i}>
                <ul className="props-nested-inner">
                  {Object.entries(obj).map(([k, v]) => (
                    <li key={k}>
                      <span className="props-nested-key">{k}</span>{' '}
                      <span className="props-nested-val">{String(v)}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        );
      }
      // Plain array of scalars → comma-joined.
      return <span className="props-value">{value.map(String).join(', ')}</span>;
    }
    if (value && typeof value === 'object') {
      // Single nested object → inline nested list, same shape as above.
      return (
        <ul className="props-nested-inner">
          {Object.entries(value).map(([k, v]) => (
            <li key={k}>
              <span className="props-nested-key">{k}</span>{' '}
              <span className="props-nested-val">{String(v)}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (value instanceof Date) {
      return <span className="props-value">{value.toLocaleDateString()}</span>;
    }
    if (typeof value === 'boolean' || typeof value === 'number') {
      return <span className="props-value">{String(value)}</span>;
    }
    return <span className="props-value">{String(value)}</span>;
  };

  return (
    <aside className="props">
      {Object.entries(frontmatter).map(([key, value]) => (
        <div className="props-row" key={key}>
          <span className="props-key">{key}</span>
          {renderValue(key, value)}
        </div>
      ))}
    </aside>
  );
}


// ── Sidebar: Recent tab pane ──
//
// Flat, mtime-grouped list of files. Reuses the same pure helpers the
// workspace landing page uses (`pickRecentFiles`) plus the new
// `groupByRelativeDate`. No new HTTP — `tree` is already loaded by
// Workspace.
function RecentPane({ tree, activeFile, orgSlug, wsHandle, onOpenFile }) {
  const recent = useMemo(() => {
    const lib = window.mindexLanding;
    if (!lib || !tree) return [];
    return lib.pickRecentFiles(tree, 25);
  }, [tree]);

  const [nowKey, setNowKey] = useState(() => Math.floor(Date.now() / 60000));
  useEffect(() => {
    const id = setInterval(() => setNowKey(Math.floor(Date.now() / 60000)), 60000);
    return () => clearInterval(id);
  }, []);

  const groups = useMemo(() => {
    const lib = window.mindexRecentGroups;
    if (!lib) return [];
    return lib.groupByRelativeDate(recent, nowKey * 60000);
  }, [recent, nowKey]);

  if (!recent || recent.length === 0) {
    return <div className="sb-recent-empty">No recent files.</div>;
  }

  return (
    <div className="sb-recent">
      {groups.map((g) => (
        <React.Fragment key={g.label}>
          <div className="sb-recent-section">{g.label}</div>
          {g.files.map((f) => {
            const href = window.mindexSpaNav.buildRoute({
              orgSlug, wsHandle, filePath: f.path,
            });
            const isActive = f.path === activeFile;
            return (
              <a
                key={f.path}
                className={'sb-recent-row' + (isActive ? ' is-active' : '')}
                href={href}
                onClick={(e) => {
                  if (window.mindexSpaNav.isModifiedClick(e)) return;
                  e.preventDefault();
                  onOpenFile(f.path);
                }}
              >
                <div className="sb-recent-name">
                  <span className="sb-recent-file">{f.name}</span>
                  {f.parentPath && (
                    <span className="sb-recent-path">{f.parentPath}</span>
                  )}
                </div>
                <span className="sb-recent-when">{f.relativeAgo}</span>
              </a>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}


// ---------- Workspace landing view ----------

function WorkspaceLanding({ workspaceLabel, fileCount, tree, orgSlug, wsHandle, onOpenFile }) {
  const { pickRecentFiles } = window.mindexLanding;
  const recent = pickRecentFiles(tree || [], 10);
  return (
    <div className="doc-scroll">
      <div className="doc-card no-outline">
        <div className="workspace-landing">
          <h1 className="workspace-landing-title">{workspaceLabel || 'Workspace'}</h1>
          <div className="workspace-landing-meta">{fileCount} {fileCount === 1 ? 'file' : 'files'}</div>
          {fileCount === 0 ? (
            <p className="workspace-landing-empty">This workspace has no files yet.</p>
          ) : (
            <>
              <div className="workspace-landing-recent-head">Recent files</div>
              <ul className="workspace-landing-recent">
                {recent.map((f) => (
                  <li key={f.path}>
                    <a
                      href={buildRoute({ orgSlug, wsHandle, filePath: f.path })}
                      onClick={(e) => {
                        if (isModifiedClick(e)) return;
                        e.preventDefault();
                        onOpenFile(f.path);
                      }}
                    >
                      {f.path}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Workspace homepage (B5) ----------
//
// Default contentPane when no file tab is open. Hero + recent + pinned +
// health-at-a-glance + tag cloud + jump buttons. Composes /app/api/home,
// /app/api/health (B3), and the in-memory `tree` for warm recents.
function WorkspaceHome({
  workspaceLabel, fileCount, tree, orgSlug, wsHandle, onOpenFile,
  onOpenBrowse, onOpenSearch, onOpenViews, onOpenHealthView,
}) {
  const { useState, useEffect, useMemo, useCallback } = React;
  const [home, setHome] = useState(null);
  const [health, setHealth] = useState(undefined); // undefined=loading, null=unavailable, obj=ok
  const [pinModalOpen, setPinModalOpen] = useState(false);

  const load = useCallback(() => {
    window.mindexApiHome.apiHome(orgSlug, wsHandle, { recent: 8 })
      .then(setHome).catch(() => setHome(null));
  }, [orgSlug, wsHandle]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let live = true;
    window.mindexApiHome.apiHealthSummary(orgSlug, wsHandle, { staleDays: 90, limit: 200 })
      .then((h) => { if (live) setHealth(h); })
      .catch(() => { if (live) setHealth(null); });
    return () => { live = false; };
  }, [orgSlug, wsHandle]);

  const recent = useMemo(() => {
    if (tree && window.mindexLanding) return window.mindexLanding.pickRecentFiles(tree, 8);
    return (home && home.recent) || [];
  }, [tree, home]);

  const pinned = (home && home.pinned) || [];
  const tags = (home && home.tags) || [];
  const hero = (home && home.hero) || { files: fileCount, folders: 0, tags: 0, last_updated: null, truncated: false };

  const openFile = (path) => (e) => {
    if (window.mindexSpaNav.isModifiedClick(e)) return;
    e.preventDefault();
    onOpenFile(path);
  };

  const hc = health && {
    orphans: health.orphans.files.length, orphansMore: health.orphans.truncated,
    broken: health.broken.links.length, brokenMore: health.broken.truncated,
    stale: health.stale.files.length, staleMore: health.stale.truncated,
  };

  return (
    <div className="doc-scroll">
      <div className="ws-home">
        <header className="ws-home-hero">
          <h1 className="ws-home-title">{workspaceLabel || 'Workspace'}</h1>
          <div className="ws-home-stats">
            {hero.files} files · {hero.folders}{hero.truncated ? '+' : ''} folders · {hero.tags} tags
            {hero.last_updated
              ? ' · updated ' + window.mindexRecentGroups.formatRelativeTime(hero.last_updated, Date.now())
              : ' · no files yet'}
          </div>
        </header>

        <div className="ws-home-grid">
          <section className="ws-home-card ws-home-recent">
            <h2>Recent</h2>
            {recent.length === 0 ? <div className="ws-home-empty">No files yet.</div> : (
              recent.map((f) => (
                <a key={f.path} className="mini-row" href={window.mindexSpaNav.buildRoute({ orgSlug, wsHandle, filePath: f.path })} onClick={openFile(f.path)}>
                  <span className="mini-name">{f.name || f.path}</span>
                  <span className="mini-path">{f.parentPath || window.mindexRecentGroups.parentPathOf(f.path)}</span>
                </a>
              ))
            )}
          </section>

          <section className="ws-home-card ws-home-pinned">
            <h2>Pinned <button className="ws-home-manage" onClick={() => setPinModalOpen(true)}>Manage</button></h2>
            {pinned.length === 0 ? (
              <div className="ws-home-empty">No pinned files. <a className="card-link" onClick={() => setPinModalOpen(true)}>Pin one →</a></div>
            ) : pinned.map((p) => (
              <a key={p.file_path}
                 className={'mini-row' + (p.exists ? '' : ' is-missing')}
                 href={p.exists ? window.mindexSpaNav.buildRoute({ orgSlug, wsHandle, filePath: p.file_path }) : undefined}
                 onClick={p.exists ? openFile(p.file_path) : (e) => e.preventDefault()}>
                <span className="mini-name">{p.file_path.slice(p.file_path.lastIndexOf('/') + 1)}</span>
                <span className="mini-path">{p.exists ? window.mindexRecentGroups.parentPathOf(p.file_path) : 'missing'}</span>
              </a>
            ))}
          </section>

          <section className="ws-home-card ws-home-health">
            <h2>Health {onOpenHealthView && <a className="card-link" onClick={onOpenHealthView}>View →</a>}</h2>
            {health === undefined ? <div className="ws-home-empty">Loading…</div>
             : health === null ? <div className="ws-home-empty">Health unavailable.</div>
             : (
              <>
                <div className="health-line"><span className="sig warn" />{hc.orphans}{hc.orphansMore ? '+' : ''} orphans (no inbound links)</div>
                <div className="health-line"><span className="sig bad" />{hc.broken}{hc.brokenMore ? '+' : ''} broken links</div>
                <div className="health-line"><span className="sig warn" />{hc.stale}{hc.staleMore ? '+' : ''} stale (&gt; 90d)</div>
                <div className="health-line"><span className="sig ok" />{Math.max(0, hero.files - (hc.orphans + hc.broken))} healthy &amp; linked</div>
              </>
            )}
          </section>

          <section className="ws-home-card ws-home-tags">
            <h2>Tags</h2>
            {tags.length === 0 ? <div className="ws-home-empty">No tags.</div> : (
              <div className="ws-home-tagcloud">
                {tags.map((t) => (
                  <span key={t.tag} className="ws-home-tagchip">{t.tag} <span className="tag-n">{t.file_count}</span></span>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="ws-home-jump">
          <button onClick={onOpenBrowse} disabled={!onOpenBrowse} title={onOpenBrowse ? '' : 'Coming soon'}>Browse files</button>
          <button onClick={onOpenSearch} disabled={!onOpenSearch} title={onOpenSearch ? '' : 'Coming soon'}>Search</button>
          <button onClick={onOpenViews} disabled={!onOpenViews} title={onOpenViews ? '' : 'Coming soon'}>Saved views</button>
        </div>
      </div>

      {pinModalOpen && (
        <PinnedManagerModal
          orgSlug={orgSlug} wsHandle={wsHandle} tree={tree} pinned={pinned}
          onClose={() => setPinModalOpen(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// Pinned manager modal — reorder (↑/↓) / unpin / pin-by-name-search.
function PinnedManagerModal({ orgSlug, wsHandle, tree, pinned, onClose, onChanged }) {
  const { useState, useEffect, useMemo } = React;
  const [pins, setPins] = useState(pinned || []);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const refetch = () =>
    fetch('/app/api/pins?org=' + encodeURIComponent(orgSlug) + '&workspace=' + encodeURIComponent(wsHandle))
      .then((r) => { if (!r.ok) throw new Error('GET /app/api/pins ' + r.status); return r.json(); })
      .then((b) => setPins(b.pins || [])).then(onChanged);

  const allFiles = useMemo(() => {
    const out = [];
    const walk = (nodes) => { for (const n of nodes || []) { if (n.type === 'file') out.push(n); else walk(n.children); } };
    walk(tree || []);
    return out;
  }, [tree]);

  const pinnedPaths = new Set(pins.map((p) => p.file_path));
  const candidates = search.trim()
    ? allFiles.filter((f) => !pinnedPaths.has(f.path) && f.name.toLowerCase().includes(search.toLowerCase())).slice(0, 8)
    : [];

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= pins.length) return;
    const next = pins.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setPins(next);
    try {
      await window.mindexApiHome.apiPinReorder(orgSlug, wsHandle, next.map((p) => p.file_path));
      onChanged();
    } catch {
      await refetch(); // refetch reverts to server state and calls onChanged itself
    }
  };
  const unpin = async (path) => {
    try { await window.mindexApiHome.apiPinRemove(orgSlug, wsHandle, path); } finally { await refetch(); }
  };
  const addPin = async (path) => {
    setSearch('');
    try { await window.mindexApiHome.apiPinAdd(orgSlug, wsHandle, path); } finally { await refetch(); }
  };

  return (
    <div className="pin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pin-modal">
        <div className="pin-modal-head"><h2>Manage pins</h2><button className="pin-modal-close" onClick={onClose}>×</button></div>
        <div className="pin-list">
          {pins.length === 0 ? <div className="pin-empty">Nothing pinned yet — add one below.</div> :
            pins.map((p, i) => (
              <div className="pin-row" key={p.file_path}>
                <span className="pin-name">{p.file_path.slice(p.file_path.lastIndexOf('/') + 1)}</span>
                <span className="pin-path">{window.mindexRecentGroups.parentPathOf(p.file_path)}</span>
                <span className="pin-move">
                  <button onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
                  <button onClick={() => move(i, +1)} disabled={i === pins.length - 1}>↓</button>
                </span>
                <button className="pin-unpin" onClick={() => unpin(p.file_path)}>Unpin</button>
              </div>
            ))}
        </div>
        <div className="pin-add">
          <input className="pin-add-input" placeholder="Pin a file by name…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {candidates.length > 0 && (
            <div className="pin-add-pop">
              {candidates.map((f) => (
                <div className="pin-add-opt" key={f.path} onClick={() => addPin(f.path)}>
                  <span className="pa-name">{f.name}</span><span className="pa-path">{window.mindexRecentGroups.parentPathOf(f.path)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CopyUrlButton({ onCopyUrl, disabled }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const onClick = () => {
    if (disabled) { showToast('Pick a file first.'); return; }
    onCopyUrl();
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button className={'copy-btn' + (copied ? ' is-copied' : '')} onClick={onClick} title="Copy link to this file">
      <svg viewBox="0 0 16 16" fill="none">
        <path d="M6.5 2.5h5a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M3.5 5v8a1 1 0 0 0 1 1h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
      <span>{copied ? 'Copied' : 'Copy URL'}</span>
    </button>
  );
}

// ---------- History sheet (M6) ----------

function HistorySheet({
  filePath,
  data,
  loading,
  error,
  filter,
  currentUser,
  previewVersion,
  onClose,
  onFilterChange,
  onPreview,
  onRestore,
}) {
  if (!filePath) return null;
  const now = Date.now();
  const rawVersions = (data && data.versions) || [];

  const filtered = rawVersions.filter((v) => {
    if (filter === 'all') return true;
    if (filter === 'you') return v.committed_by === currentUser;
    if (filter === 'agents') {
      return v.committed_by && v.committed_by.startsWith('mxs_');
    }
    return true;
  });

  const rows = filtered.map((v, i) => {
    const parent = filtered[i + 1] || null;
    return window.mindexHistory.formatHistoryRow(v, parent, now, currentUser);
  });

  const firstVersion = rawVersions[rawVersions.length - 1];
  const curId = window.mindexHistory.currentVersionId(rawVersions);

  return (
    <aside className="history-sheet" aria-label="File history">
      <div className="history-header">
        <div className="history-title-row">
          <h2 className="history-title">History</h2>
          <button className="history-close" onClick={onClose} aria-label="Close history">×</button>
        </div>
        <div className="history-path">{filePath}</div>
        <div className="history-meta">
          <span><strong>{rawVersions.length}</strong> versions</span>
          {firstVersion && (
            <React.Fragment>
              <span className="meta-sep">·</span>
              <span>since <strong>{new Date(firstVersion.committed_at).toLocaleDateString()}</strong></span>
            </React.Fragment>
          )}
        </div>
      </div>

      <nav className="history-filters">
        <button className={'hf-pill' + (filter === 'all' ? ' is-on' : '')}    onClick={() => onFilterChange('all')}>All</button>
        <button className={'hf-pill' + (filter === 'you' ? ' is-on' : '')}    onClick={() => onFilterChange('you')}>By you</button>
        <button className={'hf-pill' + (filter === 'agents' ? ' is-on' : '')} onClick={() => onFilterChange('agents')}>By agents</button>
      </nav>

      <div className="history-list">
        {loading && <div className="hv-empty">Loading{"…"}</div>}
        {error && <div className="hv-empty">{error.message || 'Could not load history.'}</div>}
        {!loading && !error && rows.length === 0 && <div className="hv-empty">No versions match.</div>}
        {!loading && !error && rows.map((r, i) => {
          const raw = filtered[i];
          const isCurrent = raw.version_id === curId;
          const isPreviewing = previewVersion && previewVersion.versionId === r.versionId;
          return (
            <HistoryRow
              key={r.versionId}
              row={r}
              isCurrent={isCurrent}
              isPreviewing={isPreviewing}
              onPreview={() => onPreview(raw)}
              onRestore={() => onRestore(raw)}
            />
          );
        })}
      </div>

      <div className="history-footer">
        Older than 90 days are compacted weekly
      </div>
    </aside>
  );
}

function HistoryRow({ row, isCurrent, isPreviewing, onPreview, onRestore }) {
  const cls = [
    'hv-row',
    isCurrent ? 'is-current' : '',
    row.isTombstone ? 'is-delete' : '',
    isPreviewing ? 'is-previewing' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <div className="hv-time">
        {row.timestamp}
        {isCurrent && <span className="hv-badge">current</span>}
      </div>
      <div className="hv-ago">{row.ago}</div>
      <div className="hv-delta">
        {row.delta.kind === 'add' && <span className="add">+{row.delta.n}</span>}
        {row.delta.kind === 'remove' && <span className="rm">−{row.delta.n}</span>}
        {row.delta.kind === 'tombstone' && (
          <span className="rm">{row.delta.n != null ? `−${row.delta.n} ` : ''}tombstone</span>
        )}
        {row.delta.kind === 'none' && <span className="hv-no-diff">no diff</span>}
      </div>
      <div className="hv-author">
        <span className={'hv-author-dot ' + row.authorClass} aria-hidden="true" />
        <span>{row.authorName}</span>
        <span className="hv-sha">{row.shortSha}</span>
      </div>
      <div className="hv-actions">
        {!row.isTombstone && (
          <button className="hv-act" onClick={onPreview} disabled={isPreviewing}>
            {isPreviewing ? 'Previewing' : 'Preview'}
          </button>
        )}
        {!isCurrent && (
          <button className="hv-act is-primary hv-act-restore" onClick={onRestore}>
            Restore
          </button>
        )}
      </div>
      <div className="hv-snippet">{row.snippet}</div>
    </div>
  );
}

function PreviewBanner({ preview, onBackToCurrent, onRestore }) {
  if (!preview) return null;
  const when = new Date(preview.committed_at).toLocaleString();
  const who = preview.committed_by || 'unknown';
  return (
    <div className="preview-banner">
      <span>Viewing v#{preview.versionId} from {when} · author: {who}</span>
      <div className="preview-banner-actions">
        <button onClick={onBackToCurrent}>Back to current</button>
        <button className="is-primary" onClick={onRestore}>Restore this version</button>
      </div>
    </div>
  );
}

// Desktop tab strip — flat pills above the reader. Active pill filled; × per
// pill (hover); + opens a blank tab; middle-click closes.
function TabStrip({ tabs, activeIndex, onSwitch, onClose, onNewTab, readerActive }) {
  const stripRef = useRef(null);
  useEffect(() => {
    const el = stripRef.current && stripRef.current.querySelector('.ttab--active');
    if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeIndex, tabs.length]);
  return (
    <div className="topbar-tabs" role="tablist" ref={stripRef}>
      {tabs.map((t, i) => {
        const name = t.path ? t.path.split('/').pop() : 'New Tab';
        const active = i === activeIndex && readerActive;
        return (
          <div
            key={t.id}
            className={'ttab' + (active ? ' ttab--active' : '')}
            title={t.path || 'New Tab'}
            role="tab"
            aria-selected={active}
            tabIndex={i === activeIndex ? 0 : -1}
            onClick={() => onSwitch(i)}
            onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); onClose(i); } }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSwitch(i); } }}
          >
            <span className="ttab__name">{name}</span>
            <button
              className="ttab__close"
              title="Close tab"
              aria-label={'Close ' + name}
              onClick={(e) => { e.stopPropagation(); onClose(i); }}
            >&times;</button>
          </div>
        );
      })}
      <button className="ttab-new" title="New tab" aria-label="New tab" onClick={onNewTab}>+</button>
    </div>
  );
}

// Mobile swipe carousel. One cell per tab at a fixed position (cell i sits at
// i * 100%); the track translates by -activeIndex * 100% so a swipe slides the
// neighbour in. Only the active ±1 cells render a DocPane (distant cells stay
// empty), so at most three docs are ever live — the neighbours are preloaded so
// they come into view without a render hitch. preventDefault fires only on a
// decided horizontal drag (passive:false) so vertical + code-block scroll work.
const CAROUSEL_EASE = 'transform .22s cubic-bezier(.4, 0, .2, 1)';
function MobileTabView({ tabs, activeIndex, onSwipe, renderPane }) {
  const ref = useRef(null);       // viewport — the gesture surface
  const trackRef = useRef(null);  // the translated track
  // Defer neighbour mounts until the active page has painted, then keep them
  // mounted (the flag only ever flips false→true, so swiping never remounts an
  // already-rendered neighbour).
  const [neighboursReady, setNeighboursReady] = useState(false);
  useEffect(() => {
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 120));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = ric(() => setNeighboursReady(true));
    return () => cancel(id);
  }, []);

  // Resting position. Re-applied after every activeIndex change so a committed
  // swipe eases the new active cell into place.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transition = CAROUSEL_EASE;
    track.style.transform = `translateX(calc(${-activeIndex} * 100%))`;
  }, [activeIndex, tabs.length]);

  useEffect(() => {
    const el = ref.current;
    const track = trackRef.current;
    if (!el || !track) return;
    let sx = 0, sy = 0, tracking = false, decided = false, horiz = false;
    const atStart = () => activeIndex <= 0;
    const atEnd = () => activeIndex >= tabs.length - 1;
    const rest = () => {
      track.style.transition = CAROUSEL_EASE;
      track.style.transform = `translateX(calc(${-activeIndex} * 100%))`;
    };
    const onStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY;
      tracking = true; decided = false; horiz = false;
    };
    const onMove = (e) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - sx;
      const dy = e.touches[0].clientY - sy;
      if (!decided) {
        const adx = Math.abs(dx), ady = Math.abs(dy);
        const scroller = e.target.closest && e.target.closest('pre, table, [style*="overflow-x"]');
        const thr = scroller ? 16 : 10;
        if (adx < thr && ady < thr) return;
        decided = true; horiz = adx > ady;
        if (horiz) track.style.transition = 'none'; // finger drives — no transition
      }
      if (!horiz) return;
      e.preventDefault();
      // Track the finger; rubber-band (1/3 pull) when there's no neighbour that way.
      const resist = (dx > 0 && atStart()) || (dx < 0 && atEnd());
      const px = resist ? dx / 3 : dx;
      track.style.transform = `translateX(calc(${-activeIndex} * 100% + ${px}px))`;
    };
    const onEnd = (e) => {
      if (!tracking) return; tracking = false;
      if (!horiz) return; // vertical/undecided: nothing was transformed
      const ct = e.changedTouches && e.changedTouches[0];
      const dx = ct ? ct.clientX - sx : 0;
      const dir = dx < 0 ? 1 : -1;
      const canMove = dir === 1 ? !atEnd() : !atStart();
      if (Math.abs(dx) > 50 && canMove) onSwipe(dir); // activeIndex changes → layout effect eases in
      else rest();                                    // snap back to current
    };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onSwipe, activeIndex, tabs.length]);

  const multi = tabs.length > 1;
  const active = tabs[activeIndex];
  const name = active ? (active.path ? active.path.split('/').pop() : 'New Tab') : '';
  return (
    <div className="mobile-tab-wrap">
      {multi && (
        // Persistent tab-context strip under the navbar: active filename +
        // a page-position indicator (dots up to 6 tabs, then "N / total").
        <div className="mobile-tab-title" role="status" aria-label={`Tab ${activeIndex + 1} of ${tabs.length}`}>
          <span className="mtt-name" title={active && active.path}>{name}</span>
          <span className="mtt-ind">
            {tabs.length <= 6
              ? tabs.map((_, i) => <span key={i} className={'tpi-dot' + (i === activeIndex ? ' is-on' : '')} />)
              : <span className="tpi-text">{activeIndex + 1} / {tabs.length}</span>}
          </span>
        </div>
      )}
      <div className="carousel-viewport" ref={ref}>
        <div className="carousel-track" ref={trackRef}>
          {tabs.map((tab, i) => {
            const live = i === activeIndex || (neighboursReady && Math.abs(i - activeIndex) <= 1);
            return (
              <div className="carousel-cell" key={tab.id}>
                {live ? renderPane(tab, i === activeIndex) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DocPane({ orgSlug, workspaceHandle, filePath, onLinkClick, activeScheme, outlineVisible, onToggleOutline, relatedVisible, onToggleRelated, onOpenRelated, onCopyUrl, shareOpen, setShareOpen, dirtyRef, saveRequestRef, preview, onBackToCurrentPreview, onRestorePreview, onOpenHistory, docReloadKey, fileMeta, initialScrollTop = 0, onScrollChange, onCrumbNavigate, onWorkspaceCrumbClick, pendingLine, pendingLineSeq, onPendingLineConsumed }) {
  const isMobile = useIsMobile();
  const [content, setContent] = useState(null);
  const [sha256, setSha256] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const scrollRef = useRef(null);
  const articleRef = useRef(null);
  // Per-tab scroll restoration. The first content load restores the tab's saved
  // offset; later in-tab navigations reset to the top. onScrollChange is held in
  // a ref so the scroll listener doesn't re-bind when the parent passes a fresh
  // closure each render.
  const firstLoadRef = useRef(true);
  const onScrollChangeRef = useRef(onScrollChange);
  useEffect(() => { onScrollChangeRef.current = onScrollChange; }, [onScrollChange]);

  useEffect(() => {
    if (!filePath) return;
    let cancelled = false;
    setLoading(true); setError(null); setContent(null); setSha256(null);
    const { apiReadFile } = window.mindexApiMut;
    apiReadFile({ orgSlug, wsHandle: workspaceHandle, path: filePath })
      .then((r) => {
        if (!cancelled) {
          setContent(r.content);
          setSha256(r.sha256);
          setLoading(false);
        }
      })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [orgSlug, workspaceHandle, filePath, docReloadKey]);

  // Restore scroll once content has rendered: the saved offset on first load,
  // top on later in-tab navigations. The markdown HTML is injected by a later
  // effect (mdPipeline → dangerouslySetInnerHTML), so the article has no height
  // when this fires — retry across a few frames until it's tall enough to hold
  // the target offset (or we give up), which also covers Shiki height growth.
  useLayoutEffect(() => {
    if (content === null) return;
    const el = scrollRef.current;
    if (!el) return;
    const top = firstLoadRef.current ? initialScrollTop : 0;
    firstLoadRef.current = false;
    if (top <= 0) { el.scrollTop = 0; return; }
    let tries = 0, raf = 0;
    const apply = () => {
      const node = scrollRef.current;
      if (!node) return;
      node.scrollTop = top;
      if (node.scrollTop < top && ++tries < 20) raf = requestAnimationFrame(apply);
    };
    raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [content]);

  // Save scroll offset (rAF-throttled) so leaving + returning to the tab — or a
  // reload — restores the reading position.
  useEffect(() => {
    const el = scrollRef.current;
    if (content === null || !el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; const cb = onScrollChangeRef.current; if (cb) cb(el.scrollTop); });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [content]);

  // Delegated click handler for permalink anchors, fold toggles, code-copy buttons.
  const handleArticleClick = useCallback((e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    // 1. Heading anchor \u2192 copy permalink.
    const anchor = target.closest('.heading-anchor');
    if (anchor && articleRef.current && articleRef.current.contains(anchor)) {
      e.preventDefault();
      const id = anchor.getAttribute('href')?.replace(/^#/, '') || '';
      if (!id) return;
      const url = window.location.origin + window.location.pathname + '#' + id;
      copyToClipboard(url);
      showToast('Heading link copied');
      return;
    }

    // 2. Heading fold toggle. CSS can't reliably transition `height` to
    // or from `auto`, so we drive the animation through the Web
    // Animations API: animate between the measured content height (in
    // px) and 0, then commit the steady state to the is-folded class.
    // The CSS rule `.heading-block.is-folded > .heading-section { height: 0 }`
    // covers the post-animation resting state (and also covers MO-driven
    // re-applications after Shiki re-renders, so previously-folded
    // sections stay folded across re-renders without flicker).
    const foldBtn = target.closest('.heading-fold-toggle');
    if (foldBtn && articleRef.current && articleRef.current.contains(foldBtn)) {
      e.preventDefault();
      const block = foldBtn.closest('.heading-block');
      if (!block) return;
      const id = block.getAttribute('data-heading-id') || '';
      const section = block.querySelector(':scope > .heading-section');
      const willFold = !block.classList.contains('is-folded');
      foldBtn.setAttribute('aria-expanded', willFold ? 'false' : 'true');
      if (section) {
        // Cancel any in-flight animation on this section so back-to-back
        // clicks don't pile up.
        section.getAnimations?.().forEach((a) => a.cancel());
        const fullHeight = section.scrollHeight;
        const FOLD_MS = 180;
        if (willFold) {
          // Animate to 0 with fill: 'forwards' so the final keyframe
          // stays applied until the steady-state class is committed.
          // onfinish (synchronous) flips the class and then cancels
          // the animation, handing ownership of `height: 0` from the
          // animation back to CSS without a one-frame flash.
          const anim = section.animate(
            [{ height: fullHeight + 'px' }, { height: '0px' }],
            { duration: FOLD_MS, easing: 'ease', fill: 'forwards' },
          );
          anim.onfinish = () => {
            block.classList.add('is-folded');
            anim.cancel();
          };
        } else {
          // Remove the steady-state class first so resting height is
          // auto; the animation overrides height during playback, then
          // lets the section settle to natural auto height when done.
          block.classList.remove('is-folded');
          section.animate(
            [{ height: '0px' }, { height: fullHeight + 'px' }],
            { duration: FOLD_MS, easing: 'ease' },
          );
        }
      } else {
        block.classList.toggle('is-folded', willFold);
      }
      const cur = new Set(lsGetJSON(keyFolds(orgSlug, workspaceHandle, filePath), []));
      if (willFold) cur.add(id); else cur.delete(id);
      lsSetJSON(keyFolds(orgSlug, workspaceHandle, filePath), [...cur]);
      return;
    }

    // 3. Code-block copy button.
    const copyBtn = target.closest('.code-copy-btn');
    if (copyBtn && articleRef.current && articleRef.current.contains(copyBtn)) {
      e.preventDefault();
      const block = copyBtn.closest('.code-block');
      const pre = block?.querySelector('pre');
      if (!pre) return;
      copyToClipboard(pre.textContent || '');
      const lbl = copyBtn.querySelector('.code-copy-label');
      if (!lbl) return;
      const original = lbl.textContent;
      lbl.textContent = 'Copied';
      copyBtn.classList.add('is-copied');
      setTimeout(() => {
        lbl.textContent = original;
        copyBtn.classList.remove('is-copied');
      }, 1600);
      return;
    }
  }, [orgSlug, workspaceHandle, filePath]);

  // URL-fragment scroll on first paint after a load completes.
  useEffect(() => {
    if (content === null) return;
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    requestAnimationFrame(() => {
      const el = articleRef.current?.querySelector(`#${CSS.escape(hash)}`);
      if (el) el.scrollIntoView({ block: 'start' });
    });
  }, [content, filePath]);

  // Render markdown via the pipeline. State-based so we can preserve
  // scroll position across re-renders triggered by scheme changes.
  const [rendered, setRendered] = useState({ html: '', frontmatter: null, headings: [] });

  // When previewing a historical version, render its content; otherwise the
  // live `content`. Falls back to the active read when preview is null.
  const effectiveContent = preview ? preview.content : content;

  useEffect(() => {
    if (effectiveContent === null || effectiveContent === undefined) return;
    const scrollEl = scrollRef.current;
    const prevTop = scrollEl?.scrollTop || 0;
    setRendered(window.mdPipeline.renderMarkdownDoc(effectiveContent, activeScheme));
    requestAnimationFrame(() => {
      if (scrollEl) scrollEl.scrollTop = prevTop;
    });
  }, [effectiveContent, activeScheme]);

  // Re-render once Shiki resolves so the first paint's plain code blocks
  // get replaced with highlighted output.
  useEffect(() => {
    const onReady = () => {
      if (effectiveContent == null) return;
      const scrollEl = scrollRef.current;
      const prevTop = scrollEl?.scrollTop || 0;
      setRendered(window.mdPipeline.renderMarkdownDoc(effectiveContent, activeScheme));
      requestAnimationFrame(() => {
        if (scrollEl) scrollEl.scrollTop = prevTop;
      });
    };
    window.addEventListener('mindex.shiki-ready', onReady);
    return () => window.removeEventListener('mindex.shiki-ready', onReady);
  }, [effectiveContent, activeScheme]);
  const html = rendered.html;
  const frontmatter = rendered.frontmatter;
  const headings = rendered.headings;
  // Locked decision: outline rail threshold-on at ≥4 headings.
  const showRail = !!outlineVisible && headings.length >= 4;
  const showAside = showRail || (!editMode && relatedVisible);

  // B7: scroll the read-mode reader to a content-search hit's source line. The
  // markdown HTML carries `data-line="<1-based>"` on block tokens (buildReaderMd
  // sourceLines flag); find the block with the greatest data-line ≤ target and
  // scrollIntoView it, with a brief non-mutating highlight pulse. Retries across
  // frames until the article has rendered (mirrors the scroll-restore pattern).
  useLayoutEffect(() => {
    if (!pendingLine || pendingLine.path !== filePath || editMode) return;
    let tries = 0, raf = 0, pulseTimer = 0;
    const apply = () => {
      const el = scrollRef.current;
      if (!el) return;
      const blocks = el.querySelectorAll('[data-line]');
      if (blocks.length === 0) {
        if (++tries < 20) { raf = requestAnimationFrame(apply); return; }
        el.scrollTop = 0;
      } else {
        let target = null;
        for (const b of blocks) {
          if (Number(b.getAttribute('data-line')) <= pendingLine.line) target = b;
          else break;
        }
        if (target) {
          target.scrollIntoView({ block: 'center' });
          target.classList.add('search-line-pulse');
          pulseTimer = setTimeout(() => target.classList.remove('search-line-pulse'), 1200);
        } else {
          el.scrollTop = 0;
        }
      }
      if (typeof onPendingLineConsumed === 'function') onPendingLineConsumed();
    };
    raf = requestAnimationFrame(apply);
    return () => { cancelAnimationFrame(raf); clearTimeout(pulseTimer); };
  }, [pendingLine, pendingLineSeq, html, filePath, editMode]);

  const [activeHeadingId, setActiveHeadingId] = useState(null);

  // Scroll-spy — picks the last heading whose top is at-or-above the
  // 25% viewport line. Re-queries headings each tick so Shiki and
  // post-processor re-renders don't leave us pointed at detached DOM.
  useEffect(() => {
    if (content === null) return;
    const article = articleRef.current;
    const scrollEl = scrollRef.current;
    if (!article) return;

    let pendingFrame = null;
    const { pickActiveHeadingId } = window.mindexScrollspy;

    const recompute = () => {
      pendingFrame = null;
      const headingEls = article.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (headingEls.length === 0) return;
      const threshold = window.innerHeight * 0.25;
      const rects = [];
      for (const h of headingEls) {
        const rect = h.getBoundingClientRect();
        rects.push({ id: h.id, top: rect.top });
      }
      const id = pickActiveHeadingId(rects, threshold);
      setActiveHeadingId((prev) => (prev === id ? prev : id));
    };

    const schedule = () => {
      if (pendingFrame != null) return;
      pendingFrame = requestAnimationFrame(recompute);
    };

    schedule();

    scrollEl?.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const mo = new MutationObserver(schedule);
    mo.observe(article, { childList: true, subtree: true });

    return () => {
      scrollEl?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      mo.disconnect();
      if (pendingFrame != null) cancelAnimationFrame(pendingFrame);
    };
  }, [content]);

  const handleOutlineJump = useCallback((e, id) => {
    e.preventDefault();
    const article = articleRef.current;
    if (!article) return;
    // Locked behavior: if the target heading is inside a folded block, unfold first.
    const target = article.querySelector(`#${CSS.escape(id)}`);
    if (!target) return;
    const block = target.closest('.heading-block');
    if (block && block.classList.contains('is-folded')) {
      block.classList.remove('is-folded');
      const btn = block.querySelector('.heading-fold-toggle');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      const cur = new Set(lsGetJSON(keyFolds(orgSlug, workspaceHandle, filePath), []));
      cur.delete(block.getAttribute('data-heading-id') || '');
      lsSetJSON(keyFolds(orgSlug, workspaceHandle, filePath), [...cur]);
    }
    target.scrollIntoView({ block: 'start' });
    history.replaceState(null, '', '#' + id);
  }, [orgSlug, workspaceHandle, filePath]);

  // Apply persisted fold state. The fold-toggle buttons themselves are
  // baked into the rendered HTML by post-processors.wrapHeadingsForFold,
  // but the `is-folded` class is per-user and must be re-applied
  // whenever React rewrites the article's innerHTML — which happens on
  // every Shiki-driven re-render of `html`. A useEffect alone is
  // insufficient: React 19 can rewrite innerHTML even when the html
  // string round-trips through an equal value (e.g., the shiki-ready
  // listener re-fires renderMarkdownDoc, producing a fresh string that
  // happens to equal the prior one), which silently wipes the class
  // without changing the effect's `html` dep. The MutationObserver
  // re-applies after any childList mutation; the effect re-applies for
  // the initial commit and for file/workspace switches.
  useEffect(() => {
    if (content === null) return;
    const article = articleRef.current;
    if (!article) return;

    const applyFoldState = () => {
      const folded = lsGetJSON(keyFolds(orgSlug, workspaceHandle, filePath), []);
      if (folded.length === 0) return;
      for (const id of folded) {
        const block = article.querySelector(`.heading-block[data-heading-id="${CSS.escape(id)}"]`);
        if (block && !block.classList.contains('is-folded')) {
          block.classList.add('is-folded');
          const btn = block.querySelector('.heading-fold-toggle');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      }
    };

    applyFoldState();

    const obs = new MutationObserver(applyFoldState);
    obs.observe(article, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [orgSlug, workspaceHandle, filePath, content]);

  const cmHostRef = useRef(null);
  const editorRef = useRef(null);          // { view, getDoc, setDoc, destroy, focus }
  const bufferRef = useRef(null);          // returned by createEditBuffer
  const [bufferDirty, setBufferDirty] = useState(false);
  const [cursor, setCursor] = useState({ line: 1, col: 1 });

  // Mount CodeMirror when entering edit mode; tear down on exit / file switch.
  useEffect(() => {
    if (!editMode) return;
    if (content == null || sha256 == null) return;
    let cancelled = false;
    const host = cmHostRef.current;
    if (!host) return;

    const { loadCodeMirror } = window.mindexCmLoader;
    const { createEditBuffer } = window.mindexEditBuffer;

    loadCodeMirror().then((mod) => {
      if (cancelled) return;
      bufferRef.current = createEditBuffer({ baseText: content, baseSha256: sha256 });
      setBufferDirty(false);
      editorRef.current = mod.createMarkdownEditor({
        parent: host,
        doc: content,
        scheme: activeScheme,
        onCursor: setCursor,
        onChange: (next) => {
          if (!bufferRef.current) return;
          bufferRef.current.applyEdit(next);
          const d = bufferRef.current.isDirty();
          setBufferDirty((prev) => (prev === d ? prev : d));
        },
      });
      editorRef.current.focus();
    }).catch((e) => {
      if (cancelled) return;
      setError('Could not load editor: ' + e.message);
      setEditMode(false);
    });

    return () => {
      cancelled = true;
      if (editorRef.current) { editorRef.current.destroy(); editorRef.current = null; }
      bufferRef.current = null;
    };
  }, [editMode, content, sha256, activeScheme]);

  // Exit edit mode when the file changes out from under us. The dirty-buffer
  // confirm modal lives at Workspace level (Task 12) and gates the file
  // switch itself; by the time DocPane sees a new filePath, the user has
  // already confirmed discard.
  useEffect(() => {
    setEditMode(false);
  }, [filePath]);

  const [conflict, setConflict] = useState(null); // null | { currentSha256, currentMtime }
  const [saving, setSaving] = useState(false);

  const doSave = useCallback(async ({ force = false } = {}) => {
    if (!bufferRef.current) return;
    const { apiWriteFile, StalePreconditionError } = window.mindexApiMut;
    const st = bufferRef.current.getState();
    setSaving(true);
    try {
      const res = await apiWriteFile({
        orgSlug,
        wsHandle: workspaceHandle,
        path: filePath,
        content: st.currentText,
        ifSha256: force ? undefined : st.baseSha256,
      });
      bufferRef.current.markSaved({ text: st.currentText, sha256: res.sha256 });
      setBufferDirty(false);
      setContent(st.currentText);
      setSha256(res.sha256);
      setConflict(null);
      showToast('Saved');
      setEditMode(false);
    } catch (e) {
      if (e instanceof StalePreconditionError) {
        setConflict({ currentSha256: e.currentSha256, currentMtime: e.currentMtime });
      } else {
        showToast('Save failed: ' + e.message);
      }
    } finally {
      setSaving(false);
    }
  }, [orgSlug, workspaceHandle, filePath]);

  const doCancel = useCallback(() => {
    if (bufferRef.current && bufferRef.current.isDirty()) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    setEditMode(false);
    setConflict(null);
  }, []);

  const doRevert = useCallback(async ({ skipConfirm = false } = {}) => {
    if (!skipConfirm && bufferRef.current && bufferRef.current.isDirty()) {
      if (!window.confirm('Revert to the latest saved version? Local changes will be lost.')) return;
    }
    const { apiReadFile } = window.mindexApiMut;
    try {
      const r = await apiReadFile({ orgSlug, wsHandle: workspaceHandle, path: filePath });
      setContent(r.content);
      setSha256(r.sha256);
      if (bufferRef.current) {
        bufferRef.current.markSaved({ text: r.content, sha256: r.sha256 });
      }
      if (editorRef.current) editorRef.current.setDoc(r.content);
      setBufferDirty(false);
      setConflict(null);
      showToast('Reverted to latest');
    } catch (e) {
      showToast('Revert failed: ' + e.message);
    }
  }, [orgSlug, workspaceHandle, filePath]);

  useEffect(() => {
    const onKey = (e) => {
      // Save: Cmd/Ctrl+S, only when in edit mode.
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        if (!editMode) return;
        e.preventDefault();
        doSave();
        return;
      }
      // Enter edit mode: E (no modifiers, not in an input).
      const { shouldFireEditShortcut } = window.mindexEditShortcut;
      if (!editMode && !isMobile && filePath && shouldFireEditShortcut(e)) {
        e.preventDefault();
        setEditMode(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editMode, filePath, doSave, isMobile]);

  // beforeunload guard while the buffer is dirty.
  useEffect(() => {
    if (!bufferDirty || isMobile) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [bufferDirty, isMobile]);

  // Mirror dirty state into the ref Workspace reads to gate navigation.
  useEffect(() => {
    if (dirtyRef) dirtyRef.current = bufferDirty;
  }, [bufferDirty, dirtyRef]);

  // Expose a save-then-report-clean trigger for the Workspace nav guard.
  useEffect(() => {
    if (!saveRequestRef) return;
    saveRequestRef.current = async () => {
      await doSave();
      return !(bufferRef.current && bufferRef.current.isDirty());
    };
    return () => { if (saveRequestRef) saveRequestRef.current = null; };
  }, [saveRequestRef, doSave]);

  if (!filePath) return <div className="doc-empty">Pick a file to read</div>;
  if (loading && !preview) return <div className="doc-empty" style={{ color: 'var(--r-text-mute)' }}>Loading{"\u2026"}</div>;
  if (error) return <div className="doc-empty">Could not load file.</div>;
  if (content === null && !preview) return <div className="doc-empty">Empty file.</div>;
  return (
    <>
      <PreviewBanner
        preview={preview}
        onBackToCurrent={onBackToCurrentPreview}
        onRestore={onRestorePreview}
      />
      <div className={'doc-header' + (editMode ? ' is-editing' : '')}>
        <div className="doc-path-row">
          <nav className="doc-breadcrumb" aria-label="Breadcrumb">
            {buildBreadcrumbItems(workspaceHandle, filePath).map((item, i) => (
              <React.Fragment key={item.target || `ws:${item.label}`}>
                {i > 0 && <span className="dc-sep">/</span>}
                {item.isCurrent ? (
                  <span className="dc is-current">{item.label}</span>
                ) : (
                  <a
                    className="dc"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.isWorkspace) { if (onWorkspaceCrumbClick) onWorkspaceCrumbClick(); }
                      else if (onCrumbNavigate) onCrumbNavigate(item.target);
                    }}
                  >{item.label}</a>
                )}
              </React.Fragment>
            ))}
          </nav>
          {onOpenHistory && fileMeta && (
            <button
              type="button"
              className="doc-meta doc-meta-button"
              onClick={() => onOpenHistory(filePath)}
              title="View history (Cmd+Y)"
            >
              {window.mindexDocMeta.formatMetaLine(
                { lines: fileMeta.lines, size: fileMeta.size, mtime: fileMeta.mtime },
                Date.now(),
              )}
            </button>
          )}
        </div>
        {editMode ? (
          <div className="doc-toolbar mode-edit">
            <span
              className={'dirty-dot' + (bufferDirty ? ' is-dirty' : '')}
              aria-label={bufferDirty ? 'Unsaved changes' : 'No unsaved changes'}
            >{bufferDirty ? '●' : '○'}</span>
            <button className="doc-btn doc-btn-primary btn-save" onClick={() => doSave()} disabled={saving || !bufferDirty}>
              <span>{saving ? 'Saving…' : 'Save'}</span><kbd>{'⌘S'}</kbd>
            </button>
            <button className="doc-btn btn-cancel" onClick={doCancel}>
              <span>Cancel</span><kbd>Esc</kbd>
            </button>
            <button className="doc-btn doc-btn-ghost btn-revert" onClick={doRevert}>
              <span>Revert</span>
            </button>
          </div>
        ) : isMobile ? (
          <div className="doc-toolbar mode-read">
            <span className="edit-on-desktop">Edit on desktop</span>
          </div>
        ) : (
          <div className="doc-toolbar mode-read">
            <button className="doc-btn doc-btn-primary btn-edit" onClick={() => setEditMode(true)} title="Edit (E)">
              <span>Edit</span><kbd>E</kbd>
            </button>
            <button className="doc-btn btn-share" onClick={() => setShareOpen(true)}>Share</button>
            <button
              className={'doc-btn' + (outlineVisible ? ' is-on' : '')}
              onClick={onToggleOutline}
              title={outlineVisible ? 'Hide outline' : 'Show outline'}
              aria-pressed={outlineVisible}
            >Outline</button>
            <button
              className={'doc-btn' + (relatedVisible ? ' is-on' : '')}
              onClick={onToggleRelated}
              title={relatedVisible ? 'Hide related' : 'Show related'}
              aria-pressed={relatedVisible}
            >Related</button>
            <CopyUrlButton onCopyUrl={onCopyUrl} disabled={!filePath} />
          </div>
        )}
      </div>
      <div className="doc-scroll" ref={scrollRef} onClick={onLinkClick}>
        <div className={'doc-card' + (showAside ? '' : ' no-outline') + (editMode ? ' is-editing' : '')}>
          <PropertiesPanel frontmatter={frontmatter} />
          {editMode ? (
            <>
              <div className="cm-host" ref={cmHostRef} />
              <div className="edit-statusbar">
                <span className="esb-item">Line {cursor.line}, Col {cursor.col}</span>
                <span className="esb-item">Markdown</span>
                <span className="esb-item esb-spacer" />
                <span className="esb-item">sha256 <span className="esb-hash">{sha256 ? window.mindexHistory.shortSha(sha256) : '—'}</span></span>
                <span className="esb-item">UTF-8</span>
              </div>
            </>
          ) : (
            <article
              ref={articleRef}
              className="doc-content"
              onClick={handleArticleClick}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          {!editMode && showRail && (
            <OutlineRail headings={headings} activeId={activeHeadingId} onJumpTo={handleOutlineJump} />
          )}
          {!editMode && (
            <RelatedPanel
              orgSlug={orgSlug}
              workspaceHandle={workspaceHandle}
              filePath={filePath}
              visible={relatedVisible}
              onOpenFile={onOpenRelated}
            />
          )}
        </div>
      </div>
      {shareOpen && (
        <ShareModal
          orgSlug={orgSlug}
          workspaceHandle={workspaceHandle}
          filePath={filePath}
          onClose={() => setShareOpen(false)}
        />
      )}
      {conflict && (
        <div className="conflict-modal-backdrop" onClick={() => setConflict(null)}>
          <div className="conflict-modal" onClick={(e) => e.stopPropagation()}>
            <h2>File changed on the server</h2>
            <p>
              <code>{filePath}</code> was modified by someone (or some agent) since you started editing. Reload to see the latest, or overwrite to force-save your version — this cannot be undone if someone else edited in the meantime.
            </p>
            <div className="conflict-actions">
              <button
                onClick={async () => {
                  if (!window.confirm('Reload and lose your unsaved changes?')) return;
                  await doRevert({ skipConfirm: true });
                }}
              >Reload</button>
              <button
                onClick={() => {
                  if (!window.confirm('Overwrite the server version? Their changes will be lost.')) return;
                  doSave({ force: true });
                }}
              >Overwrite</button>
              <button onClick={() => setConflict(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ── Sidebar tab row ──
//
// Controlled component. Parent owns `activeTab` and `trashOpen`; this
// component renders the row and translates clicks into callbacks.
// Trash is a launcher: clicking calls `onOpenTrash` and does NOT call
// `onPickTab`. While `trashOpen` is true, the tab matching `activeTab`
// gets `is-softened` to telegraph "you'll return here on close".
function SidebarTabs({ activeTab, onPickTab, trashOpen, onOpenTrash, trashCount }) {
  const cls = (tab) => {
    const parts = ['sb-tab'];
    if (!trashOpen && tab === activeTab) parts.push('is-on');
    if (trashOpen && tab === activeTab)  parts.push('is-softened');
    return parts.join(' ');
  };
  const trashCls = ['sb-tab', 'sb-tab-trash'];
  if (trashOpen) trashCls.push('is-on');

  return (
    <nav className="sidebar-tabs" role="tablist" aria-label="Sidebar sections">
      <button
        type="button" role="tab"
        aria-selected={!trashOpen && activeTab === 'files'}
        className={cls('files')}
        onClick={() => onPickTab('files')}
      >Files</button>
      <button
        type="button" role="tab"
        aria-selected={!trashOpen && activeTab === 'recent'}
        className={cls('recent')}
        onClick={() => onPickTab('recent')}
      >Recent</button>
      <button
        type="button" role="tab"
        aria-selected={!!trashOpen}
        className={trashCls.join(' ')}
        onClick={onOpenTrash}
      >
        <span className="sb-tab-label">Trash</span>
        {(trashCount != null && trashCount > 0) && (
          <span className="sb-tab-badge">{trashCount}</span>
        )}
      </button>
    </nav>
  );
}


// ---------- Trash takeover view (B7b) ----------

function TrashEmpty() {
  return (
    <div className="trash-empty">
      <div className="trash-empty-inner">
        <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <rect x="14" y="20" width="36" height="32" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M10 20h44" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M26 16h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M27 28v18M37 28v18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        <h2>Nothing deleted in the last 30 days</h2>
        <p>When you or an agent deletes a file, it lands here for 30 days before being purged.</p>
        <p className="trash-empty-hint">
          Older deletes aren't recoverable — they live in version history only if the file was later restored.
        </p>
      </div>
    </div>
  );
}

function TrashItem({ item, isSelected, onToggle, now }) {
  const { formatBytes, urgentClass, authorClass, formatRelative, splitFolderName } =
    window.mindexTrashGroups;
  // In flat mode, show the parent dir alongside the basename.
  const dir = item.path.lastIndexOf('/') < 0
    ? ''
    : item.path.slice(0, item.path.lastIndexOf('/'));
  const nameNode = dir
    ? (<><em style={{ color: 'var(--r-text-mute)', fontStyle: 'normal' }}>{dir}/</em>{item.name}</>)
    : item.name;
  return (
    <div className={'tg-item' + (isSelected ? ' is-selected' : '')}>
      <input
        type="checkbox"
        className="tg-check"
        checked={isSelected}
        onChange={() => onToggle(item.path)}
        aria-label={`Select ${item.path}`}
      />
      <span className="tg-item-name">{nameNode}</span>
      <span className="tg-item-deleted-by">
        <span className={'hv-author-dot ' + authorClass(item.deleted_by)} />
        {item.deleted_by ?? '—'}
      </span>
      <span className="tg-item-when">{formatRelative(item.deleted_at, now)}</span>
      <span className="tg-item-size">{formatBytes(item.size ?? 0)}</span>
      <span className={'tg-item-remaining ' + urgentClass(item.days_remaining)}>
        {item.days_remaining}d left
      </span>
    </div>
  );
}

function FolderGroup({ group, selection, onToggleOne, onToggleAllInFolder, now }) {
  const [collapsed, setCollapsed] = useState(false);
  const { splitFolderName } = window.mindexTrashGroups;

  const paths = group.items.map((i) => i.path);
  const selectedCount = paths.reduce((n, p) => n + (selection.has(p) ? 1 : 0), 0);
  const allSelected = selectedCount > 0 && selectedCount === paths.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const split = splitFolderName(group.folder);
  const folderDisplay = group.folder === ''
    ? <span className="tg-folder-name"><em>(root)</em></span>
    : (
      <span className="tg-folder-name">
        {split.lead && <em>{split.lead}/</em>}{split.tail}
      </span>
    );

  return (
    <div className={'tg-folder' + (collapsed ? ' is-collapsed' : '')}>
      <div className="tg-folder-head" title="Path at time of deletion">
        <input
          type="checkbox"
          className={'tg-check tg-folder-check' + (someSelected ? ' is-mixed' : '')}
          checked={allSelected}
          data-mixed={someSelected ? 'true' : 'false'}
          onChange={(e) => { e.stopPropagation(); onToggleAllInFolder(group.items); }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select all in ${group.folder || 'root'}`}
        />
        <span className="tg-chev" onClick={() => setCollapsed((v) => !v)}>
          <svg viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <span className="tg-folder-icon" onClick={() => setCollapsed((v) => !v)}>
          <svg viewBox="0 0 14 14" fill="none">
            <path d="M1.5 3.5h3.5l1 1.2h6.5v6.8h-11V3.5z" stroke="currentColor" strokeWidth="1.2"/>
          </svg>
        </span>
        <div style={{ flex: 1, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 10 }}
             onClick={() => setCollapsed((v) => !v)}>
          {folderDisplay}
          <span className="tg-folder-meta">{group.items.length} {group.items.length === 1 ? 'file' : 'files'}</span>
        </div>
      </div>
      <div className="tg-items">
        {group.items.map((it) => (
          <TrashItem
            key={it.path}
            item={it}
            isSelected={selection.has(it.path)}
            onToggle={onToggleOne}
            now={now}
          />
        ))}
      </div>
    </div>
  );
}

function TrashFilters({ value, onChange, availableAuthors, currentUserEmail }) {
  return (
    <div className="trash-filters">
      <label className="trash-filter">
        <span>When</span>
        <select
          name="when"
          value={value.when}
          onChange={(e) => onChange({ ...value, when: e.target.value })}
        >
          <option value="anytime">Anytime (30d)</option>
          <option value="today">Today</option>
          <option value="last7">Last 7 days</option>
          <option value="last30">Last 30 days</option>
        </select>
      </label>

      <label className="trash-filter">
        <span>Deleted by</span>
        <select
          name="deleted_by"
          value={value.deletedBy}
          onChange={(e) => onChange({ ...value, deletedBy: e.target.value })}
        >
          <option value="">Anyone</option>
          {currentUserEmail && <option value={currentUserEmail}>You</option>}
          {availableAuthors
            .filter((a) => a && a !== currentUserEmail)
            .map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </label>

      <label className="trash-filter trash-filter-search">
        <span>Path</span>
        <input
          name="path_prefix"
          type="text"
          placeholder="e.g. docs/"
          value={value.pathPrefix}
          onChange={(e) => onChange({ ...value, pathPrefix: e.target.value })}
        />
      </label>

      <div className="trash-filter-spacer" />

      <div className="trash-view-toggle" role="group" aria-label="View mode">
        <button
          type="button"
          className={'tvt-btn' + (value.viewMode === 'folders' ? ' is-on' : '')}
          onClick={() => onChange({ ...value, viewMode: 'folders' })}
        >Folders</button>
        <button
          type="button"
          className={'tvt-btn' + (value.viewMode === 'flat' ? ' is-on' : '')}
          onClick={() => onChange({ ...value, viewMode: 'flat' })}
        >Flat</button>
      </div>
    </div>
  );
}

function TrashBulkBar({ visible, count, bytes, restoring, onRestore, onClear }) {
  const { formatBytes } = window.mindexTrashGroups;
  return (
    <div className={'trash-bulk-bar' + (visible ? ' is-visible' : '')}>
      <span className="tbb-count">{count} selected</span>
      <span className="tbb-bytes">{formatBytes(bytes)}</span>
      <span className="tbb-spacer" />
      <button type="button" className="btn-text" onClick={onClear} disabled={restoring}>Clear</button>
      <button
        type="button"
        className="btn-primary"
        onClick={onRestore}
        disabled={restoring || count === 0}
      >{restoring ? 'Restoring…' : 'Restore'}</button>
    </div>
  );
}

// Map a `when` filter value to a `deleted_after` ms epoch (or null for anytime).
function whenToDeletedAfter(when, now) {
  if (when === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  if (when === 'last7') return now - 7 * 86400000;
  if (when === 'last30') return now - 30 * 86400000;
  return null;
}

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function TrashView({ orgSlug, wsHandle, currentUserEmail, onClose }) {
  const { groupByFolder, selectionBytes } = window.mindexTrashGroups;
  const { apiListDeleted, apiRestoreMany } = window.mindexApiTrash;

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [selection, setSelection] = useState(() => new Set());
  const [filters, setFilters] = useState(() => ({
    when: 'anytime',
    deletedBy: '',
    pathPrefix: '',
    viewMode: lsGet(KEY_TRASH_VIEW, 'folders'),
  }));
  // Snapshot `now` once per fetch so per-row "relative" labels don't tick.
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());

  useEffect(() => { lsSet(KEY_TRASH_VIEW, filters.viewMode); }, [filters.viewMode]);

  const debouncedPathPrefix = useDebounced(filters.pathPrefix, 200);

  const refetch = useCallback(async () => {
    if (!orgSlug || !wsHandle) return;
    setLoading(true);
    setError(null);
    const now = Date.now();
    const queryFilters = {
      pathPrefix: debouncedPathPrefix,
      deletedBy: filters.deletedBy ? [filters.deletedBy] : null,
      deletedAfter: whenToDeletedAfter(filters.when, now),
    };
    try {
      const res = await apiListDeleted(orgSlug, wsHandle, queryFilters);
      setItems(res.files || []);
      setTotalCount(res.total_count ?? 0);
      setFetchedAt(now);
      if (res.truncated) {
        // eslint-disable-next-line no-console
        console.warn('[trash] result truncated at head_limit=' + res.head_limit + '; pagination UI not yet implemented');
      }
    } catch (e) {
      setError(e.message || String(e));
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [orgSlug, wsHandle, filters.deletedBy, filters.when, debouncedPathPrefix, apiListDeleted]);

  useEffect(() => { refetch(); }, [refetch]);

  // Cmd/Ctrl+A → select all visible items. Esc → close.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        setSelection(new Set(items.map((i) => i.path)));
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [items, onClose]);

  const toggleOne = useCallback((path) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const toggleAllInFolder = useCallback((folderItems) => {
    setSelection((prev) => {
      const allSelected = folderItems.every((it) => prev.has(it.path));
      const next = new Set(prev);
      if (allSelected) {
        for (const it of folderItems) next.delete(it.path);
      } else {
        for (const it of folderItems) next.add(it.path);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelection(new Set()), []);

  const groups = useMemo(() => {
    if (filters.viewMode === 'flat') {
      const sorted = items.slice().sort((a, b) => (b.deleted_at ?? 0) - (a.deleted_at ?? 0));
      // Attach `name` field for TrashItem consumption.
      const withName = sorted.map((i) => ({
        ...i,
        name: i.path.lastIndexOf('/') < 0 ? i.path : i.path.slice(i.path.lastIndexOf('/') + 1),
      }));
      return [{ folder: '__flat__', items: withName, isFlat: true }];
    }
    return groupByFolder(items);
  }, [items, filters.viewMode, groupByFolder]);

  const folderCount = filters.viewMode === 'folders'
    ? groups.filter((g) => g.folder !== '__flat__').length
    : (() => {
        const set = new Set();
        for (const it of items) {
          const i = it.path.lastIndexOf('/');
          set.add(i < 0 ? '' : it.path.slice(0, i));
        }
        return set.size;
      })();

  const availableAuthors = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      if (it.deleted_by) set.add(it.deleted_by);
    }
    return [...set].sort();
  }, [items]);

  const selectedItems = useMemo(
    () => items.filter((i) => selection.has(i.path)),
    [items, selection],
  );

  const onRestore = useCallback(async () => {
    if (selectedItems.length === 0) return;
    setRestoring(true);
    try {
      const { restored, failed } = await apiRestoreMany(orgSlug, wsHandle, selectedItems);
      if (failed.length === 0) {
        showToast('Restored ' + restored.length + (restored.length === 1 ? ' file' : ' files'));
        setSelection(new Set());
      } else {
        const sampled = failed.slice(0, 3).map((f) => f.path).join(', ');
        const more = failed.length > 3 ? ', …' : '';
        showToast(
          'Restored ' + restored.length + ', ' + failed.length + ' failed: ' + sampled + more,
        );
        // Keep failed paths selected so the user can retry.
        const restoredSet = new Set(restored);
        setSelection((prev) => {
          const next = new Set();
          for (const p of prev) if (!restoredSet.has(p)) next.add(p);
          return next;
        });
      }
      await refetch();
    } finally {
      setRestoring(false);
    }
  }, [selectedItems, orgSlug, wsHandle, apiRestoreMany, refetch]);

  return (
    <div className="trash-view">
      <div className="trash-header">
        <div className="trash-title-row">
          <h1 className="trash-title">Recently deleted</h1>
          <span className="trash-count">
            {totalCount} {totalCount === 1 ? 'file' : 'files'}
            {folderCount > 0 && ' in ' + folderCount + (folderCount === 1 ? ' folder' : ' folders')}
          </span>
          <button
            type="button"
            className="trash-close"
            aria-label="Close"
            onClick={onClose}
          >
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        {!loading && totalCount > 0 && (
          <p className="trash-sub">
            Deleted files stay restorable for <strong>30 days</strong>, then they're purged.
            Tick a folder to restore it whole, or pick individual files.
          </p>
        )}
        {!loading && totalCount > 0 && (
          <TrashFilters
            value={filters}
            onChange={setFilters}
            availableAuthors={availableAuthors}
            currentUserEmail={currentUserEmail}
          />
        )}
      </div>

      <div className="trash-list">
        {loading && <div className="tree-empty">Loading{"…"}</div>}
        {!loading && error && (
          <div className="tree-empty">Couldn't load the trash: {error}</div>
        )}
        {!loading && !error && items.length === 0 && <TrashEmpty />}
        {!loading && !error && items.length > 0 && groups.map((g) => (
          <FolderGroup
            key={g.folder}
            group={g}
            selection={selection}
            onToggleOne={toggleOne}
            onToggleAllInFolder={toggleAllInFolder}
            now={fetchedAt}
          />
        ))}
      </div>

      <TrashBulkBar
        visible={selection.size > 0}
        count={selection.size}
        bytes={selectionBytes(selection, items)}
        restoring={restoring}
        onRestore={onRestore}
        onClear={clearSelection}
      />
    </div>
  );
}


// ---------- Workspace screen ----------

// Tab state reducer. All tab math is delegated to the pure window.mindexTabs
// module (_lib/tabs.js); the reducer just maps named actions onto it so every
// transition is centralized and shows up in React DevTools. ids for new tabs
// are minted by the caller (idRef) and passed in the action — the reducer
// stays pure (safe under StrictMode double-invoke).
function tabReducer(state, action) {
  const T = window.mindexTabs;
  switch (action.type) {
    case 'OPEN_OR_ACTIVATE': return T.openOrActivate(state, action.path, action.id);
    case 'OPEN_BLANK':       return T.openOrActivate(state, null, action.id);
    case 'NAVIGATE_ACTIVE':  return T.navigateActive(state, action.path, action.id);
    case 'SWITCH':           return T.switchTo(state, action.index);
    case 'BACK':             return T.backInActive(state).state;
    case 'FORWARD':          return T.forwardInActive(state).state;
    case 'CLOSE': {
      const next = T.closeTab(state, action.index);
      return next.tabs.length === 0 ? T.openOrActivate(next, null, action.id) : next;
    }
    case 'REOPEN':           return T.reopenTab(state, action.closed, action.id);
    case 'SET_ACTIVE_PATH':  return T.setActivePath(state, action.path);
    case 'SET_SCROLL':       return T.setScroll(state, action.index, action.scrollTop);
    case 'RESTORE':          return action.state;
    case 'RESET':            return { tabs: [], activeIndex: -1 };
    default:                 return state;
  }
}

// ─────────────────────────  B6 · FileBrowser  ─────────────────────────
// Flat list / table view of the whole workspace. Read-only; rows open in
// the reader. Filter/sort/tag-match logic is pure (window.mindexBrowse).

const BROWSE_COLS = [
  { key: 'name', label: 'Name' },
  { key: 'dir', label: 'Path' },
  { key: 'mtime', label: 'Modified', num: true },
  { key: 'size', label: 'Size', num: true },
  { key: 'tags', label: 'Tags' },
  { key: 'links_in', label: 'Links', num: true },
];

function keyBrowseView(org, ws) { return `mindex.browse.view.v1.${org}.${ws}`; }

function FileBrowser({ orgSlug, wsHandle, onOpenFile }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [state, setState] = useState(() => {
    const base = defaultBrowseState();
    const v = lsGet(keyBrowseView(orgSlug, wsHandle), null);
    if (v === 'list' || v === 'table') base.view = v;
    return base;
  });
  const [tagPopOpen, setTagPopOpen] = useState(false);
  const now = useMemo(() => Date.now(), [rows]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    apiFiles(orgSlug, wsHandle)
      .then((fs) => { if (live) { setRows(fs); setLoading(false); } })
      .catch((e) => { if (live) { setError(String(e.message || e)); setLoading(false); } });
    return () => { live = false; };
  }, [orgSlug, wsHandle]);

  const visible = useMemo(
    () => browseSortRows(browseFilterRows(rows, state), state),
    [rows, state.filter, state.tags, state.tagMode, state.sort, state.dir],
  );
  const allTags = useMemo(() => browseTagCounts(rows), [rows]);

  const setView = (view) => {
    setState((s) => ({ ...s, view }));
    lsSet(keyBrowseView(orgSlug, wsHandle), view);
  };
  const onHeaderClick = (key) => setState((s) => {
    if (s.sort === key) return { ...s, dir: s.dir * -1 };
    const textCol = key === 'name' || key === 'dir';
    return { ...s, sort: key, dir: textCol ? 1 : -1 };
  });
  const toggleTag = (t) => setState((s) => {
    const i = s.tags.indexOf(t);
    const tags = i >= 0 ? s.tags.filter((x) => x !== t) : [...s.tags, t];
    return { ...s, tags };
  });

  if (loading) return <div className="browse"><div className="browse-empty">Loading…</div></div>;
  if (error) return <div className="browse"><div className="browse-empty">Could not load files: {error}</div></div>;

  return (
    <div className="browse">
      <div className="browse-toolbar">
        <div className="browse-views" role="tablist">
          <button className={'browse-view-btn' + (state.view === 'list' ? ' is-active' : '')} onClick={() => setView('list')}>List</button>
          <button className={'browse-view-btn' + (state.view === 'table' ? ' is-active' : '')} onClick={() => setView('table')}>Table</button>
        </div>
        <input
          className="browse-filter"
          placeholder="Filter by name or path…"
          value={state.filter}
          onChange={(e) => setState((s) => ({ ...s, filter: e.target.value }))}
        />
        <div className="browse-tag-wrap">
          <button className={'browse-tag-btn' + (state.tags.length ? ' is-on' : '')} onClick={() => setTagPopOpen((o) => !o)}>
            {state.tags.length === 0 ? 'All tags' : ('#' + state.tags[0] + (state.tags.length > 1 ? ' +' + (state.tags.length - 1) : ''))}
          </button>
          {tagPopOpen && (
            <div className="browse-tag-pop">
              <div className="browse-tag-mode">
                <button className={state.tagMode === 'all' ? 'is-on' : ''} onClick={() => setState((s) => ({ ...s, tagMode: 'all' }))}>All</button>
                <button className={state.tagMode === 'any' ? 'is-on' : ''} onClick={() => setState((s) => ({ ...s, tagMode: 'any' }))}>Any</button>
                <button className="browse-tag-clear" onClick={() => setState((s) => ({ ...s, tags: [] }))}>Clear</button>
              </div>
              <div className="browse-tag-list">
                {allTags.map(({ tag, count }) => (
                  <button key={tag} className={'browse-tag-opt' + (state.tags.includes(tag) ? ' is-on' : '')} onClick={() => toggleTag(tag)}>
                    <span className="browse-tag-name">#{tag}</span><span className="browse-tag-n">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <span className="browse-count">{visible.length} of {rows.length} files</span>
      </div>

      {state.view === 'list' ? (
        <div className="browse-list">
          {visible.map((f) => (
            <div key={f.path} className="browse-frow" onClick={() => onOpenFile(f.path)}>
              <span className="browse-fname">{f.name}</span>
              <span className="browse-fpath">{f.dir}</span>
              <span className="browse-ftags">{f.tags.map((t) => <span key={t} className="browse-ftag">#{t}</span>)}</span>
              <span className="browse-fmeta">{browseRelTime(f.mtime, now)}</span>
            </div>
          ))}
        </div>
      ) : (
        <table className="browse-table">
          <thead>
            <tr>
              {BROWSE_COLS.map((c) => (
                <th
                  key={c.key}
                  className={(c.num ? 'num ' : '') + (state.sort === c.key ? 'is-sort' : '')}
                  onClick={() => onHeaderClick(c.key)}
                >
                  {c.label}{state.sort === c.key ? (state.dir > 0 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((f) => (
              <tr key={f.path} onClick={() => onOpenFile(f.path)}>
                <td>{f.name}</td>
                <td className="browse-c-path">{f.dir}</td>
                <td className="num">{browseRelTime(f.mtime, now)}</td>
                <td className="num">{browseBytes(f.size)}</td>
                <td>{f.tags.map((t) => <span key={t} className="browse-ftag">#{t}</span>)}</td>
                <td className="num">{f.links_in}↩ {f.links_out}↪</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ───────────────────────  B7 search overlays  ───────────────────────

// Flatten the loaded workspace tree into a flat [{ path, name }] list for the
// quick-open fuzzy ranker (window.mindexSearch.fuzzyRankFiles expects `.path`).
function flattenTreeFiles(nodes, out) {
  for (const node of nodes) {
    if (node.type === 'file') out.push({ path: node.path, name: node.name });
    else if (node.type === 'folder') flattenTreeFiles(node.children, out);
  }
  return out;
}

// Compile a bounded match RegExp for client-side highlight of a SINGLE returned
// line. Never touches the corpus. Returns null if the pattern won't compile.
function buildHighlightRegex(query, regex, caseSensitive) {
  if (!query) return null;
  try {
    const src = regex ? query : window.mindexSearch.escapeRegexLiteral(query);
    return new RegExp(src, caseSensitive ? 'g' : 'gi');
  } catch (e) {
    return null;
  }
}

// Split a line into <mark>-highlighted React fragments using a bounded regex.
function HighlightedLine({ line, re }) {
  if (!re) return line;
  const parts = [];
  let last = 0, m, guard = 0;
  re.lastIndex = 0;
  while ((m = re.exec(line)) !== null && guard++ < 500) {
    if (m.index > last) parts.push(line.slice(last, m.index));
    parts.push(<mark key={m.index + '-' + guard}>{m[0]}</mark>);
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex++; // avoid zero-width infinite loop
  }
  if (last < line.length) parts.push(line.slice(last));
  return parts.length ? parts : line;
}

// Ctrl/Cmd+P — fuzzy file jump (over the in-memory tree) + `>` command mode.
function QuickOpenOverlay({ files, commands, onPickFile, onRunCommand, onClose }) {
  const { fuzzyRankFiles, isCommandMode, filterCommands, nextCursor } = window.mindexSearch;
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { requestAnimationFrame(() => inputRef.current?.focus()); }, []);
  useEffect(() => { setCursor(0); }, [query]);

  const cmdMode = isCommandMode(query);
  const rows = useMemo(() => (
    cmdMode
      ? filterCommands(commands, query).map((c) => ({ kind: 'cmd', cmd: c }))
      : fuzzyRankFiles(files, query).slice(0, 50).map((f) => ({ kind: 'file', file: f }))
  ), [cmdMode, query, files, commands]);

  useEffect(() => {
    const node = listRef.current?.querySelector('.is-cursor');
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [cursor, rows]);

  const commit = (row) => {
    if (!row) return;
    if (row.kind === 'file') onPickFile(row.file.path);
    else onRunCommand(row.cmd);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => nextCursor(c, rows.length, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => nextCursor(c, rows.length, -1)); }
    else if (e.key === 'Enter') { e.preventDefault(); commit(rows[cursor]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  return (
    <div className="overlay-backdrop" onMouseDown={onClose}>
      <div className="search-overlay quick-overlay" onMouseDown={(e) => e.stopPropagation()}>
        <div className="overlay-input-row">
          <input
            ref={inputRef}
            className="overlay-input"
            type="text"
            placeholder={"Jump to file…  (type > for commands)"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="overlay-list" ref={listRef}>
          {rows.length === 0 && (
            <div className="overlay-empty">
              {cmdMode ? 'No commands match' : (query ? ('No files match “' + query + '”') : 'Type to jump to a file')}
            </div>
          )}
          {rows.map((row, i) => row.kind === 'cmd' ? (
            <button
              key={'cmd-' + row.cmd.id}
              className={'overlay-row overlay-cmd-row' + (i === cursor ? ' is-cursor' : '')}
              onMouseMove={() => setCursor(i)}
              onClick={() => commit(row)}
            >
              <span className="overlay-cmd-label">{row.cmd.label}</span>
              <span className="overlay-row-kind">command</span>
            </button>
          ) : (
            <button
              key={'file-' + row.file.path}
              className={'overlay-row overlay-file-row' + (i === cursor ? ' is-cursor' : '')}
              onMouseMove={() => setCursor(i)}
              onClick={() => commit(row)}
            >
              <span className="overlay-file-name">{row.file.name}</span>
              <span className="overlay-file-path">{row.file.path}</span>
            </button>
          ))}
        </div>
        <div className="overlay-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>&gt;</kbd> commands</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

// Ctrl/Cmd+Shift+F — content grep across the active workspace.
function SearchOverlay({ orgSlug, wsHandle, onOpenAtLine, onClose }) {
  const { apiSearch } = window.mindexApiSearch;
  const { nextCursor } = window.mindexSearch;
  const [query, setQuery] = useState('');
  const [regex, setRegex] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [result, setResult] = useState({ groups: [], truncated: false, needs_literal: false });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { requestAnimationFrame(() => inputRef.current?.focus()); }, []);

  // Flatten the grouped result into the cursor's hit list ({ path, line_no, line }).
  const flatHits = useMemo(() => {
    const out = [];
    for (const g of result.groups) for (const h of g.hits) out.push({ path: g.path, line_no: h.line_no, line: h.line });
    return out;
  }, [result]);

  useEffect(() => { setCursor(0); }, [flatHits]);

  // Debounced (180ms) fetch with per-effect AbortController cancellation.
  useEffect(() => {
    const q = query.trim();
    setError(null);
    // Don't fire content search below 2 chars: the engine throws on a sub-literal
    // pattern anyway, so this just avoids a guaranteed-empty network round-trip.
    if (q.length < 2) {
      setResult({ groups: [], truncated: false, needs_literal: false });
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      apiSearch({ orgSlug, wsHandle, q, regex, caseSensitive, signal: controller.signal })
        .then((r) => { setResult(r); setLoading(false); })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          setLoading(false);
          const msg = err && err.body && err.body.message;
          setError(msg || (err && err.message) || 'Search failed');
          setResult({ groups: [], truncated: false, needs_literal: false });
        });
    }, 180);
    return () => { clearTimeout(t); controller.abort(); };
  }, [query, regex, caseSensitive, orgSlug, wsHandle]);

  useEffect(() => {
    const node = listRef.current?.querySelector('.is-cursor');
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [cursor, flatHits]);

  const highlightRe = useMemo(
    () => buildHighlightRegex(query.trim(), regex, caseSensitive),
    [query, regex, caseSensitive],
  );

  const commit = (hit) => {
    if (!hit) return;
    onOpenAtLine(hit.path, hit.line_no);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => nextCursor(c, flatHits.length, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => nextCursor(c, flatHits.length, -1)); }
    else if (e.key === 'Enter') { e.preventDefault(); commit(flatHits[cursor]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  const q = query.trim();
  // Running flat index so the cursor maps across file groups.
  let hitIndex = -1;

  let body;
  if (error) {
    body = <div className="overlay-empty overlay-error">{error}</div>;
  } else if (q.length < 2) {
    body = <div className="overlay-empty">Type to grep file contents across the workspace</div>;
  } else if (result.needs_literal) {
    body = <div className="overlay-empty">Type at least 3 characters of literal text</div>;
  } else if (loading && result.groups.length === 0) {
    body = <div className="overlay-empty">Searching{"…"}</div>;
  } else if (result.groups.length === 0) {
    body = <div className="overlay-empty">No matches for {"“" + q + "”"}</div>;
  } else {
    body = result.groups.map((g) => (
      <div className="overlay-group" key={g.path}>
        <div className="overlay-group-head">{g.path}</div>
        {g.hits.map((h) => {
          hitIndex++;
          const idx = hitIndex;
          return (
            <button
              key={g.path + ':' + h.line_no + ':' + idx}
              className={'overlay-row overlay-hit-row' + (idx === cursor ? ' is-cursor' : '')}
              onMouseMove={() => setCursor(idx)}
              onClick={() => commit({ path: g.path, line_no: h.line_no })}
            >
              <span className="overlay-hit-lineno">{h.line_no}</span>
              <span className="overlay-hit-line"><HighlightedLine line={h.line} re={highlightRe} /></span>
            </button>
          );
        })}
      </div>
    ));
  }

  return (
    <div className="overlay-backdrop" onMouseDown={onClose}>
      <div className="search-overlay content-overlay" onMouseDown={(e) => e.stopPropagation()}>
        <div className="overlay-input-row">
          <input
            ref={inputRef}
            className="overlay-input"
            type="text"
            placeholder="Search file contents…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button
            className={'overlay-toggle' + (regex ? ' is-on' : '')}
            onClick={() => setRegex((v) => !v)}
            title="Regular expression"
          >.*</button>
          <button
            className={'overlay-toggle' + (caseSensitive ? ' is-on' : '')}
            onClick={() => setCaseSensitive((v) => !v)}
            title="Match case"
          >Aa</button>
        </div>
        <div className="overlay-list" ref={listRef}>
          {body}
          {result.truncated && (
            <div className="overlay-trunc">Showing first matches — refine your query</div>
          )}
        </div>
        <div className="overlay-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open at line</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

function Workspace({
  user, org, workspace, setWorkspace, orgs,
  onSwitchOrg, onSwitchAccount, onSignOut,
  signupAllowlistEnabled, initialFilePath, onFileChange,
  routeNotice, onDismissNotice,
  activeScheme, setActiveScheme,
  activeStyle, setActiveStyle,
}) {
  const [tree, setTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  // Tabs: the reader holds N open tabs, one active. `activeFile` is derived
  // from the active tab so every existing reader keeps working unchanged. Pure
  // tab math lives in window.mindexTabs (_lib/tabs.js).
  const idRef = useRef(1);
  // LIFO stack of recently-closed tabs ({ path, scrollTop }) for Alt+Y reopen.
  const closedTabsRef = useRef([]);
  const [tabState, dispatchTabs] = useReducer(tabReducer, { tabs: [], activeIndex: -1 });
  const tabStateRef = useRef(tabState);
  useLayoutEffect(() => { tabStateRef.current = tabState; }, [tabState]);
  // Gate tab persistence until the async initial restore has run for the
  // current (org, ws). Otherwise the mount-time initialFilePath effect sets a
  // transient single-tab state that the persistence effect writes BEFORE the
  // restore reads localStorage — clobbering the saved multi-tab set on every
  // reload of a file URL.
  const tabsRestoredRef = useRef(false);
  const currentTab = tabState.tabs[tabState.activeIndex] || null;
  const activeFile = currentTab ? currentTab.path : null;
  const activeTabId = currentTab ? currentTab.id : null;
  // Desktop keep-alive window (ids of mounted tabs, most-recent first). Kept in
  // sync with the active tab + open set; the pure math lives in window.mindexTabs.
  const [keepAlive, setKeepAlive] = useState([]);
  useEffect(() => {
    const openIds = tabState.tabs.map((t) => t.id);
    setKeepAlive((prev) => {
      const next = window.mindexTabs.updateKeepAlive(prev, activeTabId, openIds, KEEP_ALIVE_MAX);
      return prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next;
    });
  }, [activeTabId, tabState.tabs]);
  const [view, setView] = useState('docs'); // 'docs' | 'members' | 'browse'

  // ---- B7 global search overlays (content grep + quick-open) ----
  // Only one overlay is open at a time; opening one closes the other.
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  // { path, line } target consumed by DocPane on its next render (Enter on a
  // content-search hit → scroll the read-mode reader to the matched line).
  const pendingLineRef = useRef(null);
  // Bump to force DocPane to re-evaluate pendingLineRef even when path/content
  // are unchanged (e.g. a second hit in the already-open file).
  const [pendingLineSeq, setPendingLineSeq] = useState(0);

  // The single global keydown effect that owns the two B7 chords (locked
  // decision §4.5). Per-overlay navigation (↑/↓/Enter/Esc) is handled by each
  // overlay's own focused input, not here.
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault(); setQuickOpen(false); setSearchOpen(true); return;
      }
      if (mod && !e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault(); setSearchOpen(false); setQuickOpen(true); return;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // B7 command-palette registry (quick-open `>` mode). Wired to ONLY the real,
  // reachable actions in this build (spec §4.3 / A7). The mockup's
  // home/browse/health view commands and 'Switch workspace…' / 'Reindex' are
  // intentionally absent: 'Switch workspace…' has no reachable opener (the
  // WorkspaceSwitcher's open state is component-internal, not a Workspace prop),
  // and reindex has no UI-reachable action. They become trivial follow-ons when
  // those affordances land.
  const schemeKeys = useMemo(() => Object.keys(SCHEMES), []);
  const commands = useMemo(() => [
    {
      id: 'scheme', label: 'Toggle color scheme',
      run: () => {
        const i = schemeKeys.indexOf(activeScheme);
        setActiveScheme(schemeKeys[(i + 1) % schemeKeys.length]);
      },
    },
    { id: 'browse', label: 'Browse all files', run: () => { setTrashOpen(false); setView('browse'); } },
    { id: 'members', label: 'Open organization members', run: () => setView('members') },
    { id: 'docs', label: 'Back to files', run: () => setView('docs') },
  ], [schemeKeys, activeScheme, setActiveScheme, setView]);

  // Overlay action wiring (handleOpenFile is defined below; referenced inside
  // these closures, which only run on user action, never at definition time).
  const onQuickPickFile = useCallback((path) => {
    setQuickOpen(false);
    handleOpenFileRef.current(path);
  }, []);
  const onRunCommand = useCallback((cmd) => {
    setQuickOpen(false);
    if (cmd && typeof cmd.run === 'function') cmd.run();
  }, []);
  // Enter on a content-search hit: stage the line target, open the file (read
  // mode), and let DocPane scroll to the matched [data-line] block on its next
  // render. Bump the seq so re-targeting the already-open file still fires.
  const onSearchOpenAtLine = useCallback((path, line) => {
    pendingLineRef.current = { path, line };
    setPendingLineSeq((s) => s + 1);
    setSearchOpen(false);
    handleOpenFileRef.current(path);
  }, []);
  // Flat file list for quick-open fuzzy ranking (derived from the loaded tree).
  const flatFiles = useMemo(() => flattenTreeFiles(tree, []), [tree]);
  // The current pending-line target, re-read from the ref whenever the seq bumps
  // so a re-target of the already-open file still re-fires DocPane's scroll.
  const pendingLine = useMemo(() => pendingLineRef.current, [pendingLineSeq]);
  const clearPendingLine = useCallback(() => { pendingLineRef.current = null; }, []);

  // DocPane mirrors its dirty-buffer state into this ref so navigation
  // handlers can prompt before discarding unsaved edits.
  const docDirtyRef = useRef(false);
  const docSaveRef = useRef(null);
  const [pendingNav, setPendingNav] = useState(null);

  // Per-(org, ws) expanded folders. Hoisted to Workspace so Breadcrumbs can mutate.
  const [expanded, setExpanded] = useState(() => new Set());

  // Outline rail visibility — locked decision: threshold-on (≥4 headings).
  // The value here is the user's *preference*. The actual rendering also
  // gates on heading count (handled in DocPane).
  const [outlineVisible, setOutlineVisible] = useState(() => {
    const raw = lsGet(KEY_OUTLINE_VISIBLE, '1');
    return raw !== '0';
  });
  useEffect(() => { lsSet(KEY_OUTLINE_VISIBLE, outlineVisible ? '1' : '0'); }, [outlineVisible]);
  const [relatedVisible, setRelatedVisible] = useState(() => lsGet('mindex.related.visible.v1', '0') === '1');
  useEffect(() => { lsSet('mindex.related.visible.v1', relatedVisible ? '1' : '0'); }, [relatedVisible]);

  // Sidebar tab — 'files' (default) | 'recent'. Persisted per (org, ws).
  const [activeTab, setActiveTab] = useState('files');

  // Trash launcher state. Not persisted — opening a workspace lands on
  // the last sidebar tab, never on the trash takeover.
  const [trashOpen, setTrashOpen] = useState(false);
  const onOpenTrash  = useCallback(() => setTrashOpen(true),  []);
  const onCloseTrash = useCallback(() => setTrashOpen(false), []);

  // Body class for is-trash-open. Owned by Workspace (single source of truth)
  // to avoid races when desktop + MobileDrawer both mount SidebarTabs.
  useEffect(() => {
    document.body.classList.toggle('is-trash-open', !!trashOpen);
    return () => document.body.classList.remove('is-trash-open');
  }, [trashOpen]);

  const isMobile = useIsMobile();

  // Share modal state (hoisted so mobile topbar can trigger it)
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => { setShareOpen(false); }, [activeFile]);

  // ---- History sheet state (M6) ----
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('all');
  const previewState = useRef(window.mindexHistory.createPreviewState()).current;
  const [previewVersion, setPreviewVersion] = useState(null);
  const [docReloadKey, setDocReloadKey] = useState(0);

  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Currently keyboard-focused tree row. UI-only — does NOT change
  // the active file. Path can refer to a folder or a file.
  const [focusedPath, setFocusedPath] = useState(null);

  // M4 multi-selection state. `selectedPaths` is a Set<string> of file paths;
  // `selectionAnchor` is the last path that anchored a shift-click range. The
  // tree's flat row order is captured in `flatRowsRef` so range selection can
  // resolve indices without re-flattening from the parent.
  const [selectedPaths, setSelectedPaths] = useState(() => new Set());
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const flatRowsRef = useRef(null);

  // M4 DnD source-type refs — populated by `onDragStartRow` (file or folder).
  // Cleared in every `onDragEnd`. Refs not state because their values are read
  // mid-drag inside event handlers without triggering re-renders.
  const draggingType = useRef(null);   // 'file' | 'folder' | null
  const draggingSource = useRef(null); // path | null
  const draggingPaths = useRef(null); // string[] | null — grabbed file set, captured synchronously at file drag-start (fixes stale-selectedPaths E2)
  const onDragStartRow = useCallback(({ path, type, paths }) => {
    draggingType.current = type;
    draggingSource.current = path;
    draggingPaths.current = paths || null;
  }, []);

  // File-tree context menu + per-row mutation state.
  const [ctxMenu, setCtxMenu] = useState(null);
  // ctxMenu = null | { x, y, kind: 'file' | 'folder' | 'background', path: string | null }
  const [renamingPath, setRenamingPath] = useState(null);
  // renamingPath = string | null — when set, that tree row renders an <input>.
  const [newRow, setNewRow] = useState(null);
  // newRow = null | { parentPath: string, initialValue: string } — synthetic in-tree row in rename mode.
  const [confirm, setConfirm] = useState(null);
  // confirm = null | { title, body, confirmLabel, danger, onConfirm }
  const [moveConfirm, setMoveConfirm] = useState(null);
  // moveConfirm = null | { title, body, onConfirm } — drag-move confirmation (spec §4.4)
  const moveFiringRef = useRef(false);
  const [newFolder, setNewFolder] = useState(null);
  // newFolder = null | { parentPath: string }

  // Sidebar tree search query — substring filter on file basename + path.
  // See docs/superpowers/specs/2026-05-02-tree-search-design.md.
  const [searchQuery, setSearchQuery] = useState('');

  // In-session file-history stack (back/forward arrows in topbar). Resets on
  // workspace switch — that's a different navigation context.
  // Unsaved-changes gate shared by every tab navigation.
  const confirmDiscard = useCallback(() => {
    if (!docDirtyRef.current) return true;
    if (!window.confirm('You have unsaved changes. Discard and switch files?')) return false;
    docDirtyRef.current = false;
    return true;
  }, []);
  // Per-tab scroll memory (keyed by tab id). DocPane reports its offset here on
  // scroll — a ref write, so no re-render. `commitActiveScroll` folds the active
  // tab's live offset into tab state when you leave it, so it survives a far
  // swipe (pane unmount) and a reload (serialized with the tab).
  const scrollMem = useRef(new Map());
  const saveScroll = useCallback((id, top) => { scrollMem.current.set(id, top); }, []);
  const commitActiveScroll = useCallback(() => {
    const s = tabStateRef.current;
    const t = s.tabs[s.activeIndex];
    if (!t) return;
    const top = scrollMem.current.get(t.id);
    if (typeof top === 'number') dispatchTabs({ type: 'SET_SCROLL', index: s.activeIndex, scrollTop: top });
  }, []);
  // Plain navigation (sidebar / Recent / in-doc link): replace the active tab's
  // file in place, pushing onto that tab's own history.
  const guardedFileSwitch = useCallback((next) => {
    if (!confirmDiscard()) return false;
    dispatchTabs({ type: 'NAVIGATE_ACTIVE', path: next, id: idRef.current++ });
    return true;
  }, [confirmDiscard]);
  const goBack = useCallback(() => {
    const s = tabStateRef.current; const t = s.tabs[s.activeIndex];
    if (!t || t.hist.index <= 0) return;
    if (!confirmDiscard()) return;
    dispatchTabs({ type: 'BACK' });
  }, [confirmDiscard]);
  const goForward = useCallback(() => {
    const s = tabStateRef.current; const t = s.tabs[s.activeIndex];
    if (!t || t.hist.index >= t.hist.stack.length - 1) return;
    if (!confirmDiscard()) return;
    dispatchTabs({ type: 'FORWARD' });
  }, [confirmDiscard]);
  const handleOpenFile = useCallback((path) => {
    if (!guardedFileSwitch(path)) return;
    setDrawerOpen(false);
  }, [guardedFileSwitch]);
  // Stable ref so the B7 overlay handlers (defined earlier) can call the latest
  // handleOpenFile without depending on its closure identity.
  const handleOpenFileRef = useRef(handleOpenFile);
  useEffect(() => { handleOpenFileRef.current = handleOpenFile; }, [handleOpenFile]);
  const handleOpenInNewTab = useCallback((path) => {
    if (!confirmDiscard()) return;
    commitActiveScroll();
    dispatchTabs({ type: 'OPEN_OR_ACTIVATE', path, id: idRef.current++ });
    setDrawerOpen(false);
  }, [confirmDiscard, commitActiveScroll]);
  const handleNewBlankTab = useCallback(() => {
    if (!confirmDiscard()) return;
    commitActiveScroll();
    dispatchTabs({ type: 'OPEN_BLANK', id: idRef.current++ });
  }, [confirmDiscard, commitActiveScroll]);
  const handleSwitchTab = useCallback((index) => {
    if (!confirmDiscard()) return;
    commitActiveScroll();
    dispatchTabs({ type: 'SWITCH', index });
  }, [confirmDiscard, commitActiveScroll]);
  const handleSwipeTab = useCallback((delta) => {
    const s = tabStateRef.current;
    handleSwitchTab(s.activeIndex + delta);
  }, [handleSwitchTab]);
  const handleCloseTab = useCallback((index) => {
    const s = tabStateRef.current;
    if (index === s.activeIndex && !confirmDiscard()) return;
    const closed = s.tabs[index];
    if (closed) {
      if (closed.path) { // remember it for Alt+Y reopen (blank tabs aren't worth restoring)
        const scrollTop = scrollMem.current.get(closed.id) ?? closed.scrollTop ?? 0;
        closedTabsRef.current.push({ path: closed.path, scrollTop, hist: closed.hist });
        if (closedTabsRef.current.length > CLOSED_TABS_MAX) closedTabsRef.current.shift();
      }
      scrollMem.current.delete(closed.id); // don't leak the closed tab's scroll entry
    }
    dispatchTabs({ type: 'CLOSE', index, id: idRef.current++ });
  }, [confirmDiscard]);
  const handleReopenClosedTab = useCallback(() => {
    const entry = closedTabsRef.current.pop();
    if (!entry) return;
    // reopenTab seeds the new tab with the saved scrollTop, so the DocPane
    // restores the reading position from tab.scrollTop on mount.
    dispatchTabs({ type: 'REOPEN', closed: entry, id: idRef.current++ });
  }, []);

  const guardedSwitchWorkspace = useCallback((ws) => {
    if (docDirtyRef.current) {
      const ok = window.confirm('You have unsaved changes. Discard and switch workspace?');
      if (!ok) return;
      docDirtyRef.current = false;
    }
    setWorkspace(ws);
  }, [setWorkspace]);

  const guardedSwitchOrg = useCallback((slug) => {
    if (docDirtyRef.current) {
      const ok = window.confirm('You have unsaved changes. Discard and switch organization?');
      if (!ok) return;
      docDirtyRef.current = false;
    }
    onSwitchOrg(slug);
  }, [onSwitchOrg]);

  const guardedSignOut = useCallback(() => {
    if (docDirtyRef.current) {
      const ok = window.confirm('You have unsaved changes. Discard and sign out?');
      if (!ok) return;
      docDirtyRef.current = false;
    }
    onSignOut();
  }, [onSignOut]);

  // Single derivation: filtered tree + match counts + auto-expand set.
  // FileTree consumes `visibleTree` and `autoExpanded`; the keyboard
  // handler and SearchInput consume the counts. Empty query → pass-through.
  const { tree: visibleTree, matchedFileCount, totalFileCount, autoExpanded } = useMemo(
    () => filterTree(tree, searchQuery),
    [tree, searchQuery],
  );
  // Lowercased + trimmed query for highlight matching. Empty string → no highlight.
  const lowerQuery = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery],
  );

  // Refs that mirror latest focusedPath / expanded so the keydown
  // handler reads current state even under rapid auto-repeat (when
  // React hasn't re-rendered between two key events).
  const focusedPathRef = useRef(null);
  useEffect(() => { focusedPathRef.current = focusedPath; }, [focusedPath]);
  const expandedRef = useRef(expanded);
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);
  // Ref-mirrored handleDelete: onTreeKeyDown is declared before handleDelete
  // in source order; reading handleDelete in its dep array would TDZ-crash on
  // every render. The ref pattern decouples them.
  const handleDeleteRef = useRef(null);

  const onTreeKeyDown = useCallback((e) => {
    // Don't intercept typing in inputs / contenteditable.
    const tag = (e.target && e.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (e.target && e.target.isContentEditable)) return;
    // Modifier keys → let the browser handle (Cmd+Arrow scrolls,
    // Alt+Arrow is browser history, etc.).
    // Allow Cmd/Ctrl+C and Cmd/Ctrl+Shift+N through; block other modifier combos.
    const isCopy = (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'c' || e.key === 'C');
    const isNewFile = (e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && (e.key === 'n' || e.key === 'N');
    if ((e.metaKey || e.ctrlKey || e.altKey) && !isCopy && !isNewFile) return;

    if (e.key === 'F2' && focusedPathRef.current) {
      e.preventDefault();
      setRenamingPath(focusedPathRef.current);
      return;
    }

    if (e.key === 'Delete' && focusedPathRef.current) {
      const curExpanded = autoExpanded || expandedRef.current;
      const navTree = autoExpanded ? visibleTree : tree;
      const flat = flattenVisible(navTree, curExpanded);
      const idx = indexOfPath(flat, focusedPathRef.current);
      if (idx < 0) return;
      const row = flat[idx];
      e.preventDefault();
      setCtxMenu(null);
      if (row.type === 'folder') {
        // Route folder-Delete through the same recursive flow the context
        // menu uses (count-and-confirm + sequential apiDelete + undo).
        handleFolderDeleteRef.current?.(row.path);
        return;
      }
      setConfirm({
        title: 'Delete this file?',
        body: `Delete '${row.path}'? You can restore it from version history for 30 days.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => handleDeleteRef.current?.(row.path, row.type),
      });
      return;
    }

    if (isCopy && focusedPathRef.current) {
      e.preventDefault();
      copyToClipboard(focusedPathRef.current);
      showToast('Path copied');
      return;
    }
    if (isNewFile) {
      e.preventDefault();
      // Use the focused row's folder (or its parent if a file is focused) as parent.
      const curExpanded = autoExpanded || expandedRef.current;
      const navTree = autoExpanded ? visibleTree : tree;
      const flat = flattenVisible(navTree, curExpanded);
      const idx = indexOfPath(flat, focusedPathRef.current);
      const focused = idx >= 0 ? flat[idx] : null;
      let parent = '';
      if (focused) parent = focused.type === 'folder' ? focused.path : parentOf(focused.path);
      if (parent) {
        setExpanded((prev) => { const n = new Set(prev); n.add(parent); return n; });
      }
      setNewRow({ parentPath: parent, initialValue: 'untitled.md' });
      return;
    }

    const PAGE_JUMP = 10;
    // Use the filtered tree + autoExpanded under search; otherwise the
    // user's manual expanded set.
    const curExpanded = autoExpanded || expandedRef.current;
    const navTree = autoExpanded ? visibleTree : tree;
    const flat = flattenVisible(navTree, curExpanded);
    if (flat.length === 0) return;

    const fp = focusedPathRef.current;
    const cur = (fp && indexOfPath(flat, fp) >= 0) ? fp : flat[0].path;
    const idx = indexOfPath(flat, cur);
    const node = flat[idx];

    const move = (newIdx) => {
      const clamped = Math.max(0, Math.min(flat.length - 1, newIdx));
      if (flat[clamped].path !== cur) setFocusedPath(flat[clamped].path);
    };

    switch (e.key) {
      case 'ArrowDown':
      case 'j':
        e.preventDefault();
        move(idx + 1);
        return;
      case 'ArrowUp':
      case 'k':
        e.preventDefault();
        move(idx - 1);
        return;
      case 'PageDown':
        e.preventDefault();
        move(idx + PAGE_JUMP);
        return;
      case 'PageUp':
        e.preventDefault();
        move(idx - PAGE_JUMP);
        return;
      case 'Home':
        e.preventDefault();
        move(0);
        return;
      case 'End':
        e.preventDefault();
        move(flat.length - 1);
        return;

      case 'ArrowRight':
      case 'l':
        e.preventDefault();
        if (node.type === 'folder') {
          if (curExpanded.has(node.path)) {
            const next = flat[idx + 1];
            if (next && next.path.startsWith(node.path + '/')) {
              setFocusedPath(next.path);
            }
          } else {
            setExpanded((prev) => {
              const n = new Set(prev);
              n.add(node.path);
              return n;
            });
          }
        }
        // Right on file: noop
        return;

      case 'ArrowLeft':
      case 'h':
        e.preventDefault();
        if (node.type === 'folder' && curExpanded.has(node.path)) {
          // Expanded folder → collapse, focus stays
          setExpanded((prev) => {
            const n = new Set(prev);
            n.delete(node.path);
            return n;
          });
        } else {
          // File or collapsed folder → focus parent (no collapse)
          const parent = parentOf(node.path);
          if (parent) setFocusedPath(parent);
          // parent === '' → top level, noop
        }
        return;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (node.type === 'file') {
          handleOpenFile(node.path);
        } else {
          setExpanded((prev) => {
            const n = new Set(prev);
            if (n.has(node.path)) n.delete(node.path);
            else n.add(node.path);
            return n;
          });
        }
        return;

      case 'Escape':
        e.preventDefault();
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
        return;

      default:
        return;
    }
  }, [tree, visibleTree, autoExpanded, handleOpenFile, setExpanded, setFocusedPath]);

  // Snap back to docs when org changes
  useEffect(() => { setView('docs'); }, [org && org.slug]);

  const orgSlug = org && org.slug;
  const wsHandle = workspace && workspace.handle;

  // Snap back to docs when the workspace changes, so the browser doesn't
  // linger across a switch with stale rows. (Placed after the wsHandle decl
  // above — referencing it earlier would hit the temporal-dead-zone.)
  useEffect(() => { setView('docs'); }, [wsHandle]);

  // Reset the filter when (org, ws) changes — new context. (Tabs are reset by
  // the initial-load effect below.)
  useEffect(() => {
    setSearchQuery('');
  }, [orgSlug, wsHandle]);

  const handleCopyUrl = useCallback(async () => {
    if (!activeFile) return;
    const url = `${window.location.origin}${buildRoute({ orgSlug, wsHandle, filePath: activeFile })}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      ta.remove();
    }
    showToast('Link copied to clipboard');
  }, [orgSlug, wsHandle, activeFile]);

  // ---- History sheet handlers (M6) ----
  const openHistory = useCallback(async (path) => {
    if (!path || !orgSlug || !wsHandle) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    setHistoryData(null);
    if (previewState.get()) {
      previewState.exit();
      setPreviewVersion(null);
    }
    try {
      const data = await window.mindexApiMut.apiVersions(orgSlug, wsHandle, path);
      setHistoryData(data);
    } catch (e) {
      setHistoryError(e);
    } finally {
      setHistoryLoading(false);
    }
  }, [orgSlug, wsHandle]);

  const closeHistory = useCallback(() => {
    setHistoryOpen(false);
    setHistoryData(null);
    setHistoryError(null);
    if (previewState.get()) {
      previewState.exit();
      setPreviewVersion(null);
    }
  }, [previewState]);

  // Reflect open state on <body> so CSS can shift the doc-scroll margin.
  useEffect(() => {
    document.body.classList.toggle('is-history-open', historyOpen);
    return () => document.body.classList.remove('is-history-open');
  }, [historyOpen]);

  // Cmd/Ctrl+Y toggles the history sheet; Esc closes it.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const cmdLike = e.metaKey || e.ctrlKey;
      if (cmdLike && (e.key === 'y' || e.key === 'Y') && activeFile) {
        e.preventDefault();
        if (historyOpen) {
          closeHistory();
        } else {
          openHistory(activeFile);
        }
        return;
      }
      if (e.key === 'Escape' && historyOpen) {
        e.preventDefault();
        closeHistory();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeFile, historyOpen, openHistory, closeHistory]);

  // Re-point history to the newly-active file instead of closing the sheet
  // (mockup #14 — the sheet survives a file switch). openHistory re-fetches the
  // version list and resets any active preview. Guard on activeFile so a null
  // active file (workspace root) leaves the sheet's last content in place.
  useEffect(() => {
    if (historyOpen && activeFile) openHistory(activeFile);
  }, [activeFile, historyOpen, openHistory]);

  const handlePreview = useCallback(async (raw) => {
    if (!raw || !raw.sha256 || !activeFile) return;
    let content;
    if (previewState.isCached(activeFile, raw.sha256)) {
      content = previewState.getCached(activeFile, raw.sha256);
    } else {
      try {
        content = await apiReadAtVersion(orgSlug, wsHandle, activeFile, raw.sha256);
      } catch (e) {
        setHistoryError(e);
        return;
      }
    }
    previewState.enter(activeFile, {
      versionId:    raw.version_id,
      sha256:       raw.sha256,
      committed_by: raw.committed_by,
      committed_at: raw.committed_at,
      content,
    });
    setPreviewVersion(previewState.get());
  }, [activeFile, orgSlug, wsHandle, previewState]);

  const handleBackToCurrent = useCallback(() => {
    previewState.exit();
    setPreviewVersion(null);
  }, [previewState]);

  const handleRestore = useCallback(async (raw) => {
    if (!activeFile) return;
    let targetSha = raw.sha256;
    if (raw.kind === 'delete') {
      const parent = historyData?.versions.find((v) => v.version_id === raw.parent_version_id);
      if (!parent) {
        setHistoryError(new Error('Load full history first to restore this version'));
        return;
      }
      if (!parent.sha256) {
        setHistoryError(new Error('Cannot restore: no prior version available'));
        return;
      }
      targetSha = parent.sha256;
    }
    if (!targetSha) {
      setHistoryError(new Error('Cannot restore: missing sha256'));
      return;
    }
    try {
      await window.mindexApiMut.apiRestore(orgSlug, wsHandle, activeFile, targetSha);
      const data = await window.mindexApiMut.apiVersions(orgSlug, wsHandle, activeFile);
      setHistoryData(data);
      previewState.exit();
      setPreviewVersion(null);
      setDocReloadKey((k) => k + 1);
      showToast('File restored');
    } catch (e) {
      setHistoryError(e);
    }
  }, [activeFile, orgSlug, wsHandle, historyData, previewState]);

  const handleRestorePreview = useCallback(() => {
    if (!previewVersion) return;
    handleRestore({
      sha256: previewVersion.sha256,
      version_id: previewVersion.versionId,
      kind: 'write',
      committed_by: previewVersion.committed_by,
      committed_at: previewVersion.committed_at,
      parent_version_id: null,
    });
  }, [previewVersion, handleRestore]);

  // Reset + restore expand state when (org, ws) changes.
  useEffect(() => {
    if (!orgSlug || !wsHandle) return;
    const restored = new Set(lsGetJSON(keyExpanded(orgSlug, wsHandle), []));
    setExpanded(restored);
  }, [orgSlug, wsHandle]);

  // Restore activeTab when (org, ws) changes. trashOpen intentionally NOT restored.
  useEffect(() => {
    if (!orgSlug || !wsHandle) return;
    const raw = lsGet(keySidebarTab(orgSlug, wsHandle), 'files');
    setActiveTab(raw === 'recent' ? 'recent' : 'files');
  }, [orgSlug, wsHandle]);

  // Persist activeTab on every change.
  useEffect(() => {
    if (!orgSlug || !wsHandle) return;
    lsSet(keySidebarTab(orgSlug, wsHandle), activeTab);
  }, [activeTab, orgSlug, wsHandle]);

  // Sidebar width — restored from localStorage, clamped 0–600px.
  // Below 100px snaps to 0 (fully collapsed).
  const DEFAULT_SIDEBAR_W = 272;
  const setSidebarW = useCallback((w) => {
    document.documentElement.style.setProperty('--sidebar-w', w + 'px');
    lsSet(KEY_WIDTH, String(w));
  }, []);

  useEffect(() => {
    const saved = parseInt(lsGet(KEY_WIDTH, String(DEFAULT_SIDEBAR_W)), 10);
    if (saved >= 0 && saved <= 600) {
      document.documentElement.style.setProperty('--sidebar-w', saved + 'px');
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    const cur = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w'), 10);
    setSidebarW(cur < 100 ? DEFAULT_SIDEBAR_W : 0);
  }, [setSidebarW]);

  const draggingRef = useRef(false);
  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const raw = Math.max(0, Math.min(600, e.clientX));
      const w = raw < 100 ? 0 : raw;
      document.documentElement.style.setProperty('--sidebar-w', w + 'px');
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.classList.remove('is-resizing');
      const w = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w'), 10);
      lsSet(KEY_WIDTH, String(w));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const onResizeMouseDown = (e) => {
    draggingRef.current = true;
    document.body.classList.add('is-resizing');
    e.preventDefault();
  };

  const onCollapseAll = useCallback(() => setExpanded(new Set()), []);
  const onExpandAll = useCallback(() => {
    setExpanded(() => {
      const all = new Set();
      const walk = (nodes, prefix) => {
        for (const n of nodes) {
          if (n.type === 'folder') {
            const p = prefix ? `${prefix}/${n.name}` : n.name;
            all.add(p);
            walk(n.children, p);
          }
        }
      };
      walk(tree, '');
      return all;
    });
  }, [tree]);

  const fileCount = useMemo(() => {
    let n = 0;
    const walk = (nodes) => { for (const x of nodes) { if (x.type === 'file') n++; else walk(x.children); } };
    walk(tree);
    return n;
  }, [tree]);

  const ctxItems = useMemo(
    () => (ctxMenu
      ? buildContextMenuItems({
          kind: ctxMenu.kind,
          isMulti: ctxMenu.isMulti,
          selectedCount: selectedPaths.size,
        })
      : []),
    [ctxMenu, selectedPaths],
  );

  const handleDelete = useCallback(async (path, kind) => {
    const { apiStat, apiDelete, apiRestore } = window.mindexApiMut;
    // Folder delete in M3 is empty-only — no version to restore.
    // For files: snapshot sha256 BEFORE delete so undo can use it.
    let snapshotSha = null;
    if (kind === 'file') {
      try {
        const s = await apiStat(org.slug, workspace.handle, path);
        if (s.exists && typeof s.sha256 === 'string') {
          snapshotSha = s.sha256;
        }
      } catch { /* fall through — undo will be unavailable */ }
    }
    try {
      await apiDelete(org.slug, workspace.handle, path);
      const fresh = await apiTree(org.slug, workspace.handle);
      setTree(fresh);
      // If a tab holds the deleted file, close it (→ a blank tab if it was the
      // last one). The activeFile→URL bubble effect then re-syncs the address bar.
      const delIdx = window.mindexTabs.findTabIndex(tabStateRef.current.tabs, path);
      if (delIdx >= 0) {
        dispatchTabs({ type: 'CLOSE', index: delIdx, id: idRef.current++ });
      }
      if (kind === 'file' && snapshotSha !== null) {
        showToast({
          message: `Deleted ${path}`,
          actionLabel: 'Undo',
          durationMs: 10000,
          onAction: async () => {
            try {
              await apiRestore(org.slug, workspace.handle, path, snapshotSha);
              const t = await apiTree(org.slug, workspace.handle);
              setTree(t);
              showToast('Restored');
            } catch (e) {
              showToast('Restore failed: ' + (e.message || e.code));
            }
          },
        });
      } else {
        showToast(kind === 'folder' ? 'Folder deleted' : 'Deleted');
      }
    } catch (e) {
      showToast('Delete failed: ' + (e.message || e.code || 'unknown'));
    }
  }, [org, workspace, activeFile]);
  useEffect(() => { handleDeleteRef.current = handleDelete; }, [handleDelete]);

  // Tree refresh — re-fetches the workspace file tree. Declared before any
  // callback that mentions it (handleFolderDelete / onDeleteAll / onDropOnFolder
  // / focus-revalidate) so the const isn't in the TDZ when those callbacks
  // are created during the first render.
  const lastFetchRef = useRef(0);
  const refreshTree = useCallback(() => {
    if (!orgSlug || !wsHandle) return;
    apiTree(orgSlug, wsHandle).then((t) => {
      lastFetchRef.current = Date.now();
      setTree(t);
    }).catch(() => {});
  }, [orgSlug, wsHandle]);

  // Recursive folder delete with descendant-count confirm + undo. Used by
  // both the context menu and the keyboard Delete shortcut so they stay in
  // sync. Kept here (not inlined per-call-site) so M5's batch-delete
  // optimisation has one place to land.
  const handleFolderDelete = useCallback(async (path) => {
    const files = collectFilesUnder(tree, path);
    if (files.length === 0) {
      setConfirm({
        title: 'Delete this folder?',
        body: `Delete folder '${path}'? It's empty.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => handleDeleteRef.current?.(path, 'folder'),
      });
      return;
    }
    const confirmed = await new Promise((resolve) => {
      setConfirm({
        title: `Delete ${files.length} file${files.length === 1 ? '' : 's'} in ${path}/?`,
        body: 'You can restore individual files from version history for 30 days.',
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => { setConfirm(null); resolve(true); },
        onCancel: () => { setConfirm(null); resolve(false); },
      });
    });
    if (!confirmed) return;
    const { apiStat, apiDelete, apiRestore } = window.mindexApiMut;
    const stats = await Promise.all(files.map((p) =>
      apiStat(org.slug, workspace.handle, p).catch(() => null),
    ));
    try {
      for (const p of files) {
        await apiDelete(org.slug, workspace.handle, p);
      }
      refreshTree();
      showToast({
        message: `Deleted ${files.length} files in ${path}/`,
        actionLabel: 'Undo',
        durationMs: 10000,
        onAction: async () => {
          for (let i = 0; i < files.length; i++) {
            const s = stats[i];
            if (s && s.sha256) {
              try {
                await apiRestore(org.slug, workspace.handle, files[i], s.sha256);
              } catch { /* best effort */ }
            }
          }
          refreshTree();
        },
      });
    } catch (err) {
      showToast(err.message || 'Delete failed');
      refreshTree();
    }
  }, [tree, org, workspace, refreshTree]);
  const handleFolderDeleteRef = useRef(null);
  useEffect(() => { handleFolderDeleteRef.current = handleFolderDelete; }, [handleFolderDelete]);

  const handleDuplicate = useCallback(async (path) => {
    const { apiCopy } = window.mindexApiMut;
    const { parent, basename } = splitBasename(path);
    const siblings = siblingNames(tree, parent);
    const newBasename = nextDuplicateName(basename, siblings);
    const newPath = joinPath(parent, newBasename);
    try {
      await apiCopy(org.slug, workspace.handle, path, newPath);
      const fresh = await apiTree(org.slug, workspace.handle);
      setTree(fresh);
      showToast('Duplicated');
      // Drop into rename so the user can name it immediately.
      setRenamingPath(newPath);
    } catch (e) {
      showToast('Duplicate failed: ' + (e.message || e.code));
    }
  }, [tree, org, workspace]);

  // M4 multi-select handlers ------------------------------------------------

  const onDeleteAll = useCallback(async () => {
    const paths = [...selectedPaths];
    if (paths.length === 0) return;
    const { apiStat, apiDelete, apiRestore } = window.mindexApiMut;
    const confirmed = await new Promise((resolve) => {
      setConfirm({
        title: `Delete ${paths.length} files?`,
        body: 'You can restore them individually from version history for 30 days.',
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => { setConfirm(null); resolve(true); },
        onCancel: () => { setConfirm(null); resolve(false); },
      });
    });
    if (!confirmed) return;
    // Snapshot sha256 of every path BEFORE delete (apiRestore takes sha256).
    const stats = await Promise.all(paths.map((p) =>
      apiStat(org.slug, workspace.handle, p).catch(() => null),
    ));
    try {
      for (const p of paths) {
        await apiDelete(org.slug, workspace.handle, p);
      }
      refreshTree();
      setSelectedPaths(clearSelection());
      setSelectionAnchor(null);
      showToast({
        message: `Deleted ${paths.length} files`,
        actionLabel: 'Undo',
        durationMs: 10000,
        onAction: async () => {
          for (let i = 0; i < paths.length; i++) {
            const s = stats[i];
            if (s && s.sha256) {
              try {
                await apiRestore(org.slug, workspace.handle, paths[i], s.sha256);
              } catch { /* best effort */ }
            }
          }
          refreshTree();
        },
      });
    } catch (err) {
      showToast(err.message || 'Delete failed');
      refreshTree();
    }
  }, [selectedPaths, org, workspace, refreshTree]);

  // Forward-declared ref to onDropOnFolder so onMoveTo can call it without
  // referencing the const directly in its dep array (would TDZ — onDropOnFolder
  // is declared further down). Mirror pattern used elsewhere (handleDeleteRef).
  const onDropOnFolderRef = useRef(null);

  const onMoveTo = useCallback(async () => {
    const folders = collectFolders(tree);
    const target = await new Promise((resolve) => {
      setFolderPicker({
        folders,
        onPick: (p) => { setFolderPicker(null); resolve(p); },
        onCancel: () => { setFolderPicker(null); resolve(null); },
      });
    });
    if (target == null) return;
    // Reuse the drop-on-folder pipeline. Force sourceType=file so DnD branch
    // takes the multi-source path; clear refs after to avoid leaking state.
    // Clear draggingPaths up front too: onMoveTo must source from selectedPaths
    // (no drag set the ref), and a prior aborted drag could leave it stale.
    draggingType.current = 'file';
    draggingSource.current = null;
    draggingPaths.current = null;
    try {
      await onDropOnFolderRef.current?.(target);
    } finally {
      draggingType.current = null;
      draggingSource.current = null;
      draggingPaths.current = null;
    }
  }, [tree]);

  const onCopyPaths = useCallback(() => {
    const text = [...selectedPaths].join('\n');
    copyToClipboard(text);
    showToast(`Copied ${selectedPaths.size} paths`);
  }, [selectedPaths]);

  const handleCtxAction = useCallback(async (action) => {
    if (!ctxMenu) return;
    const { kind, path } = ctxMenu;
    if (action === 'delete-all') {
      await onDeleteAll();
      return;
    }
    if (action === 'move-to') {
      await onMoveTo();
      return;
    }
    if (action === 'copy-paths') {
      onCopyPaths();
      return;
    }
    if (action === 'rename' && path) {
      setRenamingPath(path);
      return;
    }
    if (action === 'open' && kind === 'file' && path) {
      handleOpenFile(path);
      return;
    }
    if (action === 'open-new-tab' && kind === 'file' && path) {
      handleOpenInNewTab(path);
      return;
    }
    if (action === 'history' && kind === 'file' && path) {
      openHistory(path);
      return;
    }
    if (action === 'delete' && path) {
      if (kind === 'folder') {
        await handleFolderDelete(path);
        return;
      }
      // File path
      setConfirm({
        title: 'Delete this file?',
        body: `Delete '${path}'? You can restore it from version history for 30 days.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => handleDelete(path, kind),
      });
      return;
    }
    if (action === 'duplicate' && kind === 'file' && path) {
      await handleDuplicate(path);
      return;
    }
    if (action === 'new-file') {
      // Parent: clicked folder => itself; clicked file => its containing folder; background => root.
      const parent = kind === 'folder'
        ? (path || '')
        : (kind === 'file' && path ? splitBasename(path).parent : '');
      // Expand the parent so the new row is visible.
      if (parent) {
        setExpanded((prev) => {
          const n = new Set(prev);
          n.add(parent);
          return n;
        });
      }
      setNewRow({ parentPath: parent, initialValue: 'untitled.md' });
      return;
    }
    if (action === 'new-folder') {
      const parent = kind === 'folder'
        ? (path || '')
        : (kind === 'file' && path ? splitBasename(path).parent : '');
      setNewFolder({ parentPath: parent });
      return;
    }
    if (action === 'copy-path' && path) {
      copyToClipboard(path);
      showToast('Path copied');
      return;
    }
    if (action === 'copy-wikilink' && path) {
      copyToClipboard(wikilinkFor(path));
      showToast('Wikilink copied');
      return;
    }
    if (action === 'expand-all') {
      const all = new Set();
      const walk = (nodes, prefix) => {
        for (const n of nodes) {
          if (n.type === 'folder') {
            const p = prefix ? `${prefix}/${n.name}` : n.name;
            all.add(p);
            walk(n.children, p);
          }
        }
      };
      walk(tree, '');
      setExpanded(all);
      return;
    }
    if (action === 'collapse-all') {
      setExpanded(new Set());
      return;
    }
  }, [ctxMenu, handleOpenFile, tree, handleDelete, handleDuplicate, setExpanded, onDeleteAll, onMoveTo, onCopyPaths, openHistory]);

  const handleRenameCommit = useCallback(async (oldPath, newBasename) => {
    setRenamingPath(null);
    const { parent } = splitBasename(oldPath);
    const newPath = joinPath(parent, newBasename);
    if (newPath === oldPath) return;
    try {
      const { apiMove } = window.mindexApiMut;
      await apiMove(org.slug, workspace.handle, oldPath, newPath);
      showToast('Renamed');
      const fresh = await apiTree(org.slug, workspace.handle);
      setTree(fresh);
      // Routing fixup
      if (activeFile === oldPath) {
        dispatchTabs({ type: 'SET_ACTIVE_PATH', path: newPath });
        window.history.replaceState(null, '', buildRoute({
          orgSlug: org.slug, wsHandle: workspace.handle, filePath: newPath,
        }));
      } else if (activeFile && activeFile.startsWith(oldPath + '/')) {
        const rewritten = replacePathPrefix(activeFile, oldPath, newPath);
        dispatchTabs({ type: 'SET_ACTIVE_PATH', path: rewritten });
        window.history.replaceState(null, '', buildRoute({
          orgSlug: org.slug, wsHandle: workspace.handle, filePath: rewritten,
        }));
      }
    } catch (e) {
      showToast('Rename failed: ' + (e.message || e.code || 'unknown error'));
    }
  }, [org, workspace, activeFile]);

  const handleNewRowCommit = useCallback(async (parentPath, basename) => {
    setNewRow(null);
    const { apiNewFile } = window.mindexApiMut;
    const newPath = joinPath(parentPath, basename);
    try {
      await apiNewFile(org.slug, workspace.handle, newPath, '');
      const fresh = await apiTree(org.slug, workspace.handle);
      setTree(fresh);
      showToast('Created');
      handleOpenFile(newPath);
    } catch (e) {
      showToast('Create failed: ' + (e.message || e.code));
    }
  }, [org, workspace, handleOpenFile]);
  const handleNewFolderSubmit = useCallback(async (parentPath, folderName, fileName) => {
    setNewFolder(null);
    const { apiNewFile } = window.mindexApiMut;
    const folderPath = joinPath(parentPath, folderName);
    const filePath = joinPath(folderPath, fileName);
    try {
      await apiNewFile(org.slug, workspace.handle, filePath, '');
      const fresh = await apiTree(org.slug, workspace.handle);
      setTree(fresh);
      setExpanded((prev) => { const n = new Set(prev); n.add(folderPath); return n; });
      showToast('Folder created');
      handleOpenFile(filePath);
    } catch (e) {
      showToast('Create failed: ' + (e.message || e.code));
    }
  }, [org, workspace, handleOpenFile, setExpanded]);

  // Persist expanded set on every change.
  useEffect(() => {
    if (!orgSlug || !wsHandle) return;
    lsSetJSON(keyExpanded(orgSlug, wsHandle), [...expanded]);
  }, [expanded, orgSlug, wsHandle]);

  // Persist open tabs per (org, ws) so a reload restores them. Skip writes
  // until the initial restore has populated tabState — see tabsRestoredRef.
  useEffect(() => {
    if (!orgSlug || !wsHandle) return;
    if (!tabsRestoredRef.current) return;
    lsSetJSON(keyTabs(orgSlug, wsHandle), window.mindexTabs.serializeTabs(tabState));
  }, [tabState, orgSlug, wsHandle]);

  // Soft cap: warn (don't block) once the user crosses 20 open tabs.
  useEffect(() => {
    if (tabState.tabs.length >= 20) {
      try { console.warn('[mindex:tabs] 20 tabs open — consider closing some.'); } catch { /* noop */ }
    }
  }, [tabState.tabs.length]);

  // Tab keyboard shortcuts, all on Alt — the Cmd/Ctrl equivalents are
  // intercepted by the browser (open/close/switch *browser* tabs). Alt+W close
  // active, Alt+T new tab, Alt+Y reopen last closed, Alt+1–9 switch. Match on
  // e.code, since e.key becomes a special char when Alt is held on macOS layouts.
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const t = e.target; // don't hijack Alt+key while typing in a field
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.code === 'KeyW') {
        const s = tabStateRef.current;
        if (s.activeIndex >= 0) { e.preventDefault(); handleCloseTab(s.activeIndex); }
      } else if (e.code === 'KeyT') {
        e.preventDefault(); handleNewBlankTab();
      } else if (e.code === 'KeyY') {
        e.preventDefault(); handleReopenClosedTab();
      } else if (e.code >= 'Digit1' && e.code <= 'Digit9') {
        const idx = parseInt(e.code.slice(5), 10) - 1;
        if (idx < tabStateRef.current.tabs.length) { e.preventDefault(); handleSwitchTab(idx); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleNewBlankTab, handleCloseTab, handleReopenClosedTab, handleSwitchTab]);

  // Auto-expand ancestors of the active file (so the file row is visible).
  useEffect(() => {
    if (!activeFile) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      const segs = activeFile.split('/');
      for (let i = 1; i < segs.length; i++) next.add(segs.slice(0, i).join('/'));
      return next;
    });
  }, [activeFile]);

  // Sync focused row to active file. Arrow-only moves don't touch
  // activeFile, so they don't trigger this — focus stays where the
  // arrows put it. Net result: focus = active file by default,
  // except after pure focus moves.
  useEffect(() => {
    if (activeFile) setFocusedPath(activeFile);
  }, [activeFile]);

  // Reconcile focusedPath when the tree changes (refresh-on-focus
  // can drop the row we were sitting on, e.g. external delete).
  // Falls back to the active file (if visible) or null.
  useEffect(() => {
    if (!focusedPath) return;
    if (rowExistsInTree(tree, focusedPath)) return;
    setFocusedPath(activeFile && rowExistsInTree(tree, activeFile) ? activeFile : null);
  }, [tree, focusedPath, activeFile]);

  const handleCrumbClick = useCallback((targetFolderPath) => {
    // Expand the ancestor chain so the target row is in the visible tree.
    setExpanded((prev) => {
      const next = new Set(prev);
      const parts = targetFolderPath.split('/');
      for (let i = 1; i <= parts.length; i++) next.add(parts.slice(0, i).join('/'));
      return next;
    });
    // Setting focused-path lets the existing focus-effect (which runs after
    // the render commits) scroll the row into view — no rAF race.
    setFocusedPath(targetFolderPath);
  }, [setExpanded, setFocusedPath]);

  // M4: collision modal state (open when batch/single move hits EEXIST on
  // a destination path).
  const [collisionState, setCollisionState] = useState(null);

  // M4: folder picker modal state for the "Move to…" multi-select action.
  const [folderPicker, setFolderPicker] = useState(null);

  // M4 drop handler, now confirm-gated (spec 2026-05-24-editability-move-confirmation).
  // Phase 1: build the concrete plan from drag state. Phase 2: confirm (unless
  // no-op). Phase 3: execute the unchanged apiMove/apiMoveBatch flow.
  const executeMovePlan = useCallback(async (plan) => {
    const { apiMove, apiMoveBatch } = window.mindexApiMut;
    const { kind, moves, dest } = plan;
    const targetFolder = dest;

    if (kind === 'folder') {
      try {
        await apiMoveBatch({ orgSlug, wsHandle: workspace.handle, moves });
        refreshTree();
        showToast({
          message: `Moved ${moves.length} files into ${targetFolder || 'root'}`,
          actionLabel: 'Undo',
          durationMs: 10000,
          onAction: async () => {
            try {
              await apiMoveBatch({
                orgSlug,
                wsHandle: workspace.handle,
                moves: moves.map((m) => ({ source: m.destination, destination: m.source })),
              });
            } catch (err) {
              showToast(`Undo failed — check version history. (${err.message || 'unknown error'})`);
            }
            refreshTree();
          },
        });
      } catch (err) {
        showToast(err.message || 'Move failed');
      }
      return;
    }

    // file / multi: detect internal collisions (multiple sources → same dest)
    {
      const seen = new Set();
      const dupes = [];
      for (const m of moves) {
        if (seen.has(m.destination)) dupes.push(m.destination);
        seen.add(m.destination);
      }
      if (dupes.length > 0) {
        showToast(`Naming conflict in selection: ${dupes.length} file${dupes.length === 1 ? '' : 's'} would share the same destination (${dupes[0]}). Move them in smaller batches.`);
        return;
      }
    }

    try {
      if (moves.length === 1) {
        await apiMove(orgSlug, workspace.handle, moves[0].source, moves[0].destination);
      } else {
        await apiMoveBatch({ orgSlug, wsHandle: workspace.handle, moves });
      }
      refreshTree();
      showToast({
        message: moves.length > 1
          ? `Moved ${moves.length} files into ${targetFolder || 'root'}`
          : `Moved ${dndBasename(moves[0].source)}`,
        actionLabel: 'Undo',
        durationMs: 10000,
        onAction: async () => {
          try {
            if (moves.length === 1) {
              await apiMove(orgSlug, workspace.handle, moves[0].destination, moves[0].source);
            } else {
              await apiMoveBatch({
                orgSlug,
                wsHandle: workspace.handle,
                moves: moves.map((m) => ({ source: m.destination, destination: m.source })),
              });
            }
          } catch (err) {
            showToast(`Undo failed — check version history. (${err.message || 'unknown error'})`);
          }
          refreshTree();
        },
      });
    } catch (err) {
      if (err && err.code === 'EEXIST') {
        const colliding = moves[0];
        setCollisionState({
          existingPath: colliding.destination,
          proposedPath: colliding.destination,
          onRename: async (newDestination) => {
            const retry = moves.map((m, i) => (i === 0 ? { ...m, destination: newDestination } : m));
            try {
              if (retry.length === 1) {
                await apiMove(orgSlug, workspace.handle, retry[0].source, retry[0].destination);
              } else {
                await apiMoveBatch({ orgSlug, wsHandle: workspace.handle, moves: retry });
              }
              refreshTree();
              setCollisionState(null);
            } catch (retryErr) {
              showToast(retryErr.message || 'Retry failed');
            }
          },
          onCancel: () => setCollisionState(null),
        });
      } else {
        showToast(err.message || 'Move failed');
      }
    }
  }, [orgSlug, workspace, refreshTree, setCollisionState]);

  const onDropOnFolder = useCallback(async (targetFolder) => {
    const plan = buildMovePlan({
      sourceType: draggingType.current,
      sourceFolder: draggingSource.current,
      draggedPaths: draggingPaths.current ?? [...selectedPaths],
      targetFolder,
      tree,
    });
    if (plan.kind === 'noop') return; // spec G3 — no prompt, no move

    const copy = moveConfirmCopy(plan);
    moveFiringRef.current = false;
    setMoveConfirm({
      title: copy.title,
      body: copy.sources
        ? (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 200, overflow: 'auto' }}>
            {copy.sources.map((s) => (
              <li key={s} style={{ fontFamily: 'var(--reader-mono, ui-monospace, monospace)', fontSize: '0.85em', opacity: 0.8 }}>{s}</li>
            ))}
            {copy.capped && (
              <li style={{ fontSize: '0.85em', opacity: 0.6 }}>…and {copy.capped.more} more</li>
            )}
          </ul>
        )
        : null,
      onConfirm: () => executeMovePlan(plan),
    });
  }, [tree, selectedPaths, executeMovePlan]);
  useEffect(() => { onDropOnFolderRef.current = onDropOnFolder; }, [onDropOnFolder]);

  // Initial load
  useEffect(() => {
    if (!orgSlug || !wsHandle) return;
    tabsRestoredRef.current = false;
    // Drop per-tab memory tied to the previous (org, ws): the reopen stack and
    // scroll map are keyed by old tab ids/paths and must not bleed across.
    closedTabsRef.current = [];
    scrollMem.current.clear();
    setTree([]); dispatchTabs({ type: 'RESET' }); setTreeLoading(true);
    apiTree(orgSlug, wsHandle)
      .then((t) => {
        lastFetchRef.current = Date.now();
        setTree(t);
        // Restore persisted tabs for this (org, ws), pruning paths no longer in
        // the tree; then activate the URL's file if any. Empty → WorkspaceLanding.
        const saved = lsGetJSON(keyTabs(orgSlug, wsHandle), null);
        let restored = window.mindexTabs.deserializeTabs(saved, (p) => fileExistsInTree(t, p));
        // deserializeTabs assigns ids 0..N-1; advance the counter past them so a
        // newly-opened tab never collides with a restored tab's React key. Floor
        // at 1 so an all-pruned restore (0 tabs) doesn't reset the counter to 0.
        idRef.current = Math.max(1, restored.tabs.length);
        if (initialFilePath) {
          restored = window.mindexTabs.openOrActivate(restored, initialFilePath, idRef.current++);
        }
        if (__MINDEX_DEV__) {
          try { console.info('[mindex:tabs]', { tabCount: restored.tabs.length, activeIndex: restored.activeIndex }); } catch { /* noop */ }
        }
        dispatchTabs({ type: 'RESTORE', state: restored });
        // Restore is complete; allow persistence writes for this (org, ws).
        tabsRestoredRef.current = true;
        setTreeLoading(false);
      })
      .catch(() => setTreeLoading(false));
  }, [orgSlug, wsHandle]);

  // Bubble activeFile changes up to App so it can sync the URL. Seed
  // the ref with `null` (matches activeFile's initial useState value)
  // so the mount-time effect run treats it as a no-op — otherwise we
  // fire onFileChange(null) on mount, which clobbers App's pending
  // filePath that was just seeded from the URL and pushes a stray
  // /<org>/<ws> history entry before apiTree resolves.
  const lastFiredRef = useRef(null);
  useEffect(() => {
    if (lastFiredRef.current === activeFile) return;
    lastFiredRef.current = activeFile;
    if (typeof onFileChange === 'function') onFileChange(activeFile);
  }, [activeFile, onFileChange]);

  // Sync activeFile ← initialFilePath when the prop changes externally
  // (back/forward). The [orgSlug, wsHandle] effect above only seeds
  // activeFile on workspace switch, so within-workspace popstate would
  // otherwise leave the page stuck on the previous file. Skip null
  // (workspace switches reset pendingFilePath to null and rely on the
  // readme-fallback in the workspace-switch effect).
  useEffect(() => {
    if (initialFilePath && initialFilePath !== activeFile) {
      if (docDirtyRef.current) {
        setPendingNav(initialFilePath);
        // Re-assert the current file in the URL so the address bar doesn't
        // race ahead of the still-open document while the modal is up.
        if (typeof onFileChange === 'function' && activeFile) onFileChange(activeFile);
        return;
      }
      dispatchTabs({ type: 'OPEN_OR_ACTIVATE', path: initialFilePath, id: idRef.current++ });
    }
  }, [initialFilePath]);

  // Auto-refresh on window focus if the cached tree is older than 30s.
  // Tree changes come from MCP/CLI writes (this SPA is read-only), so
  // alt-tabbing back to the browser is the natural moment to re-sync.
  // No interval poll — it just blips the UI without buying anything.
  useEffect(() => {
    const STALE_MS = 30000;
    const onFocus = () => {
      if (Date.now() - lastFetchRef.current > STALE_MS) refreshTree();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshTree]);

  // Keyboard nav (j/k/Enter/Space, /-to-open-switcher) deferred — see spec §12 item 12.

  const onDocClick = useCallback((e) => {
    const a = e.target.closest('a[data-md-link]');
    if (!a) return;
    const href = a.getAttribute('data-md-link');
    if (!href || href.startsWith('http') || href.startsWith('#')) return;
    e.preventDefault();
    const baseParts = (activeFile || '').split('/');
    baseParts.pop();
    for (const p of href.split('/')) {
      if (p === '..') baseParts.pop();
      else if (p !== '.' && p !== '') baseParts.push(p);
    }
    guardedFileSwitch(baseParts.join('/'));
  }, [activeFile, guardedFileSwitch]);

  // Lookup file meta (size/lines/mtime) from the current tree for the doc-meta strip.
  const activeFileMeta = useMemo(() => {
    if (!activeFile) return null;
    const walk = (nodes) => {
      for (const n of nodes) {
        if (n.type === 'file' && n.path === activeFile) return n;
        if (n.type === 'folder') {
          const hit = walk(n.children);
          if (hit) return hit;
        }
      }
      return null;
    };
    return walk(tree);
  }, [tree, activeFile]);

  const contentPane = trashOpen ? (
    <TrashView
      orgSlug={orgSlug}
      wsHandle={wsHandle}
      currentUserEmail={user && user.email}
      onClose={onCloseTrash}
    />
  ) : view === 'members' ? (
    <MembersPanel org={org} isOwner={org && org.role === 'owner'} signupAllowlistEnabled={signupAllowlistEnabled} />
  ) : view === 'browse' ? (
    <FileBrowser orgSlug={orgSlug} wsHandle={wsHandle} onOpenFile={handleOpenFile} />
  ) : tabState.tabs.length > 0 ? (
    // Tabbed docs render through the keep-alive stack (desktop) or carousel
    // (mobile); contentPane only reaches here as the 0-tab fallback, so this is
    // the transient "active tab is blank" state.
    <div className="doc-scroll"><div className="doc-empty-state">Select a file to begin</div></div>
  ) : (
    <WorkspaceHome
      workspaceLabel={workspace ? (workspace.display_name || workspace.handle) : null}
      fileCount={fileCount}
      tree={tree}
      orgSlug={orgSlug}
      wsHandle={wsHandle}
      onOpenFile={handleOpenFile}
      onOpenHealthView={undefined /* B3 not merged here → "View →" link hidden via the onOpenHealthView guard */}
      onOpenBrowse={() => { setTrashOpen(false); setView('browse'); }} /* B6 browse view (merged from v2) */
      onOpenSearch={undefined /* B7 not shipped → button disabled */}
      onOpenViews={undefined /* B1 not shipped → button disabled */}
    />
  );

  // Per-tab renderer for the mobile swipe carousel. The active tab gets the full
  // interactive DocPane; preloaded neighbours render content-only with inert
  // handlers so they never drive share/history/dirty/outline singletons.
  const neighborRef = useRef(null);
  const renderTabPane = useCallback((tab, isActive) => {
    if (!tab || !tab.path) {
      return <div className="doc-scroll"><div className="doc-empty-state">Select a file to begin</div></div>;
    }
    return (
      <DocPane
        orgSlug={orgSlug}
        workspaceHandle={wsHandle}
        filePath={tab.path}
        onLinkClick={isActive ? onDocClick : NOOP}
        activeScheme={activeScheme}
        outlineVisible={isActive ? outlineVisible : false}
        onToggleOutline={isActive ? (() => setOutlineVisible((v) => !v)) : NOOP}
        relatedVisible={isActive ? relatedVisible : false}
        onToggleRelated={isActive ? (() => setRelatedVisible((v) => !v)) : NOOP}
        onOpenRelated={isActive ? handleOpenFile : NOOP}
        onCopyUrl={isActive ? handleCopyUrl : NOOP}
        shareOpen={isActive ? shareOpen : false}
        setShareOpen={isActive ? setShareOpen : NOOP}
        dirtyRef={isActive ? docDirtyRef : neighborRef}
        saveRequestRef={isActive ? docSaveRef : neighborRef}
        preview={isActive ? previewVersion : null}
        onBackToCurrentPreview={isActive ? handleBackToCurrent : NOOP}
        onRestorePreview={isActive ? handleRestorePreview : NOOP}
        onOpenHistory={isActive ? openHistory : NOOP}
        docReloadKey={docReloadKey}
        fileMeta={isActive ? activeFileMeta : null}
        initialScrollTop={scrollMem.current.get(tab.id) ?? tab.scrollTop ?? 0}
        onScrollChange={(top) => saveScroll(tab.id, top)}
        onCrumbNavigate={isActive ? handleCrumbClick : NOOP}
        onWorkspaceCrumbClick={isActive ? (() => setFocusedPath(null)) : NOOP}
        pendingLine={isActive && pendingLine && pendingLine.path === tab.path ? pendingLine : null}
        pendingLineSeq={pendingLineSeq}
        onPendingLineConsumed={isActive ? clearPendingLine : NOOP}
      />
    );
  }, [orgSlug, wsHandle, onDocClick, activeScheme, outlineVisible, relatedVisible, handleCopyUrl, shareOpen, previewVersion, handleBackToCurrent, handleRestorePreview, openHistory, docReloadKey, activeFileMeta, saveScroll, handleCrumbClick, setFocusedPath, pendingLine, pendingLineSeq, clearPendingLine, handleOpenFile]);

  // Desktop tabbed-docs content: a keep-alive stack. Every tab in the keep-alive
  // window (plus the active one, always) stays mounted; only the active pane is
  // shown — inactive panes are content-hidden + inert, preserving their scroll
  // and warm content so switching back is instant. Mobile uses the carousel.
  const mountedTabIds = new Set(keepAlive);
  if (activeTabId != null) mountedTabIds.add(activeTabId);
  const docStack = isMobile ? null : (
    <div className="doc-stack">
      {tabState.tabs.filter((t) => mountedTabIds.has(t.id)).map((t) => {
        const paneActive = t.id === activeTabId;
        return (
          <div
            key={t.id}
            className={'doc-stack__pane ' + (paneActive ? 'is-active' : 'is-inert')}
            aria-hidden={paneActive ? undefined : 'true'}
            inert={paneActive ? undefined : true}
          >
            {renderTabPane(t, paneActive)}
          </div>
        );
      })}
    </div>
  );

  const treePane = (
    <>
      {!treeLoading && tree.length > 0 && (
        <SearchInput query={searchQuery} setQuery={setSearchQuery} matchedCount={matchedFileCount} totalCount={totalFileCount} />
      )}
      {treeLoading
        ? <div className="tree-empty">Loading{"\u2026"}</div>
        : tree.length === 0
          ? <div className="tree-empty">This workspace is empty.</div>
          : (
            <FileTree
              tree={visibleTree}
              fullTree={tree}
              activeFile={activeFile}
              onOpenFile={handleOpenFile}
              expanded={expanded}
              setExpanded={setExpanded}
              focusedPath={focusedPath}
              setFocusedPath={setFocusedPath}
              onKeyDown={onTreeKeyDown}
              query={lowerQuery}
              autoExpanded={autoExpanded}
              orgSlug={orgSlug}
              wsHandle={wsHandle}
              onRowContextMenu={(e, kind, path) => {
                e.preventDefault();
                const isMulti = kind === 'file' && selectedPaths.has(path) && selectedPaths.size > 1;
                if (!isMulti && kind === 'file') {
                  // Collapse selection to just this row so the single-row menu
                  // acts on what's visually highlighted.
                  setSelectedPaths(singleSelect(path));
                  setSelectionAnchor(path);
                }
                setCtxMenu({ x: e.clientX, y: e.clientY, kind, path, isMulti });
              }}
              onTreeBackgroundContextMenu={(e) => {
                e.preventDefault();
                setCtxMenu({ x: e.clientX, y: e.clientY, kind: 'background', path: null, isMulti: false });
              }}
              renamingPath={renamingPath}
              newRow={newRow}
              onRenameCommit={handleRenameCommit}
              onRenameCancel={() => setRenamingPath(null)}
              onNewRowCommit={handleNewRowCommit}
              onNewRowCancel={() => setNewRow(null)}
              selectedPaths={selectedPaths}
              selectionAnchor={selectionAnchor}
              onSelectionChange={setSelectedPaths}
              onAnchorChange={setSelectionAnchor}
              flatRowsRef={flatRowsRef}
              onDragStartRow={onDragStartRow}
              draggingType={draggingType}
              draggingSource={draggingSource}
              draggingPaths={draggingPaths}
              onDropOnFolder={onDropOnFolder}
            />
          )
      }
    </>
  );

  // Portal-mounted modals: shared between mobile + desktop returns.
  // They render into document.body so they don't need to live inside the
  // branch's DOM tree.
  const modals = (
    <>
      {searchOpen && (
        <SearchOverlay
          orgSlug={orgSlug}
          wsHandle={wsHandle}
          onOpenAtLine={onSearchOpenAtLine}
          onClose={() => setSearchOpen(false)}
        />
      )}
      {quickOpen && (
        <QuickOpenOverlay
          files={flatFiles}
          commands={commands}
          onPickFile={onQuickPickFile}
          onRunCommand={onRunCommand}
          onClose={() => setQuickOpen(false)}
        />
      )}
      <TreeContextMenu
        open={!!ctxMenu}
        x={ctxMenu ? ctxMenu.x : 0}
        y={ctxMenu ? ctxMenu.y : 0}
        items={ctxItems}
        onClose={() => setCtxMenu(null)}
        onPick={handleCtxAction}
      />
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const cb = confirm?.onConfirm; setConfirm(null); cb && cb(); }}
      />
      <ConfirmModal
        open={!!moveConfirm}
        title={moveConfirm?.title}
        body={moveConfirm?.body}
        confirmLabel="Move"
        danger={false}
        onCancel={() => { moveFiringRef.current = false; setMoveConfirm(null); }}
        onConfirm={() => {
          // Guard ConfirmModal's Enter double-fire: the modal's document keydown
          // handler (app.jsx:1122) AND the autofocused button's native Enter→click
          // both invoke onConfirm in the same tick. setMoveConfirm(null) is async
          // (batched), so a synchronous ref flag is what actually prevents a
          // double apiMove/apiMoveBatch for the same plan.
          if (moveFiringRef.current) return;
          moveFiringRef.current = true;
          const cb = moveConfirm?.onConfirm;
          setMoveConfirm(null);
          if (cb) cb();
        }}
      />
      <UnsavedChangesModal
        open={pendingNav != null}
        onStay={() => {
          setPendingNav(null);
          if (typeof onFileChange === 'function' && activeFile) onFileChange(activeFile);
        }}
        onDiscard={() => {
          docDirtyRef.current = false;
          const target = pendingNav;
          setPendingNav(null);
          dispatchTabs({ type: 'OPEN_OR_ACTIVATE', path: target, id: idRef.current++ });
        }}
        onSave={async () => {
          const fn = docSaveRef.current;
          if (!fn) { setPendingNav(null); return; }
          const clean = await fn();
          if (clean) {
            docDirtyRef.current = false;
            const target = pendingNav;
            setPendingNav(null);
            dispatchTabs({ type: 'OPEN_OR_ACTIVATE', path: target, id: idRef.current++ });
          } else {
            // Save failed (e.g. 409). Close the nav modal; the in-DocPane
            // conflict modal takes over. Do not switch files.
            setPendingNav(null);
          }
        }}
      />
      <NewFolderModal
        open={!!newFolder}
        parentPath={newFolder?.parentPath || ''}
        existingSiblings={newFolder ? siblingNames(tree, newFolder.parentPath) : new Set()}
        onCancel={() => setNewFolder(null)}
        onSubmit={(folderName, fileName) => handleNewFolderSubmit(newFolder.parentPath, folderName, fileName)}
      />
      <CollisionModal
        open={collisionState != null}
        existingPath={collisionState?.existingPath ?? ''}
        proposedPath={collisionState?.proposedPath ?? ''}
        onRename={collisionState?.onRename ?? (() => {})}
        onCancel={collisionState?.onCancel ?? (() => setCollisionState(null))}
      />
      <FolderPickerModal
        open={folderPicker != null}
        folders={folderPicker?.folders ?? []}
        onPick={folderPicker?.onPick ?? (() => setFolderPicker(null))}
        onCancel={folderPicker?.onCancel ?? (() => setFolderPicker(null))}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="workspace-shell mobile">
        <MobileTopbar
          filePath={activeFile}
          workspaceLabel={workspace ? workspace.handle : null}
          onHamburger={openDrawer}
          onShare={() => setShareOpen(true)}
          activeScheme={activeScheme}
          onPickScheme={setActiveScheme}
          activeStyle={activeStyle}
          onPickStyle={setActiveStyle}
          outlineVisible={outlineVisible}
          onToggleOutline={() => setOutlineVisible((v) => !v)}
        />
        <MobileDrawer
          open={drawerOpen} onClose={closeDrawer}
          workspace={workspace} workspaces={org && org.workspaces ? org.workspaces : []}
          onPickWorkspace={guardedSwitchWorkspace} fileCount={fileCount}
          tree={tree} treeLoading={treeLoading}
          visibleTree={visibleTree} activeFile={activeFile} onOpenFile={handleOpenFile}
          expanded={expanded} setExpanded={setExpanded}
          focusedPath={focusedPath} setFocusedPath={setFocusedPath}
          onTreeKeyDown={onTreeKeyDown} searchQuery={searchQuery} setSearchQuery={setSearchQuery}
          matchedFileCount={matchedFileCount} totalFileCount={totalFileCount}
          lowerQuery={lowerQuery} autoExpanded={autoExpanded}
          onCollapseAll={onCollapseAll} onExpandAll={onExpandAll}
          orgs={orgs} org={org} onSwitchOrg={guardedSwitchOrg} setView={setView}
          user={user} onSignOut={guardedSignOut}
          activeTab={activeTab} onPickTab={setActiveTab}
          trashOpen={trashOpen} onOpenTrash={onOpenTrash}
          tabs={tabState.tabs} activeTabIndex={tabState.activeIndex}
          onSwitchTab={handleSwitchTab} onCloseTab={handleCloseTab}
          onRowContextMenu={(e, kind, path) => {
            e.preventDefault();
            setCtxMenu({ x: e.clientX, y: e.clientY, kind, path });
          }}
          onTreeBackgroundContextMenu={(e) => {
            e.preventDefault();
            setCtxMenu({ x: e.clientX, y: e.clientY, kind: 'background', path: null });
          }}
          renamingPath={renamingPath}
          newRow={newRow}
          onRenameCommit={handleRenameCommit}
          onRenameCancel={() => setRenamingPath(null)}
          onNewRowCommit={handleNewRowCommit}
          onNewRowCancel={() => setNewRow(null)}
        />
        {routeNotice && (
          <div className="route-notice" role="status">
            <span>{routeNotice}</span>
            <button className="route-notice-close" onClick={onDismissNotice} aria-label="Dismiss">×</button>
          </div>
        )}
        <main className="content-col mobile-content">
          {view === 'docs' && !trashOpen && tabState.tabs.length > 0 ? (
            <MobileTabView
              tabs={tabState.tabs}
              activeIndex={tabState.activeIndex}
              onSwipe={handleSwipeTab}
              renderPane={renderTabPane}
            />
          ) : contentPane}
        </main>
        {historyOpen && (
          <HistorySheet
            filePath={activeFile}
            data={historyData}
            loading={historyLoading}
            error={historyError}
            filter={historyFilter}
            currentUser={user && user.email}
            previewVersion={previewVersion}
            onClose={closeHistory}
            onFilterChange={setHistoryFilter}
            onPreview={handlePreview}
            onRestore={handleRestore}
          />
        )}
        {modals}
      </div>
    );
  }

  return (
    <div className="workspace-shell">
      <Topbar
        onBack={goBack}
        onForward={goForward}
        canBack={!!currentTab && currentTab.hist.index > 0}
        canForward={!!currentTab && currentTab.hist.index < currentTab.hist.stack.length - 1}
        onToggleSidebar={toggleSidebar}
        onOpenSearch={() => { setQuickOpen(false); setSearchOpen(true); }}
        activeScheme={activeScheme}
        onPickScheme={setActiveScheme}
        activeStyle={activeStyle}
        onPickStyle={setActiveStyle}
        tabs={tabState.tabs}
        activeIndex={tabState.activeIndex}
        onSwitchTab={handleSwitchTab}
        onCloseTab={handleCloseTab}
        onNewTab={handleNewBlankTab}
        readerActive={view === 'docs' && !trashOpen && tabState.tabs.length > 0}
      />
      {routeNotice && (
        <div className="route-notice" role="status">
          <span>{routeNotice}</span>
          <button className="route-notice-close" onClick={onDismissNotice} aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="app-grid">
        <aside className="sidebar">
          <WorkspaceSwitcher
            workspaces={org && org.workspaces ? org.workspaces : []}
            current={workspace}
            onPick={guardedSwitchWorkspace}
            fileCount={fileCount}
            onExpandAll={onExpandAll}
            onCollapseAll={onCollapseAll}
            orgSlug={orgSlug}
          />
          <button
            className={'sidebar-browse-btn' + (view === 'browse' ? ' is-active' : '')}
            onClick={() => { setTrashOpen(false); setView('browse'); }}
          >
            Browse all files
          </button>
          <SidebarTabs
            activeTab={activeTab}
            onPickTab={setActiveTab}
            trashOpen={trashOpen}
            onOpenTrash={onOpenTrash}
            trashCount={null}
          />
          <div className="sidebar-panes">
            <div className="sidebar-pane" hidden={activeTab !== 'files'}>
              <div className="sidebar-tree">{treePane}</div>
            </div>
            <div className="sidebar-pane" hidden={activeTab !== 'recent'}>
              <RecentPane
                tree={tree}
                activeFile={activeFile}
                orgSlug={orgSlug}
                wsHandle={wsHandle}
                onOpenFile={handleOpenFile}
              />
            </div>
          </div>
          <div className="sidebar-foot">
            <OrgSwitcher orgs={orgs} currentOrgSlug={org && org.slug} onSwitchOrg={(o) => { setView('docs'); guardedSwitchOrg(o); }} onOpenMembers={() => setView('members')} />
            {user && <UserMenu user={user} onSignOut={guardedSignOut} />}
          </div>
          <div className="sb-resize" onMouseDown={onResizeMouseDown} title="Drag to resize" />
        </aside>
        <main className="content-col">
          {view === 'docs' && !trashOpen && tabState.tabs.length > 0 ? docStack : contentPane}
        </main>
        {historyOpen && (
          <HistorySheet
            filePath={activeFile}
            data={historyData}
            loading={historyLoading}
            error={historyError}
            filter={historyFilter}
            currentUser={user && user.email}
            previewVersion={previewVersion}
            onClose={closeHistory}
            onFilterChange={setHistoryFilter}
            onPreview={handlePreview}
            onRestore={handleRestore}
          />
        )}
      </div>
      {modals}
    </div>
  );
}

// ---------- Invite modal ----------

function InviteModal({ org, signupAllowlistEnabled, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // {accept_url, email_sent}
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true); setError(null);
    apiCreateInvitation(org.slug, email.trim().toLowerCase())
      .then((data) => {
        setCreated(data);
        setSubmitting(false);
        if (onCreated) onCreated();
      })
      .catch((err) => {
        setError(err.message || 'Failed to send invitation');
        setSubmitting(false);
      });
  };

  const handleCopy = () => {
    if (!created) return;
    navigator.clipboard.writeText(created.accept_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div className="invite-modal-backdrop" onClick={onClose}>
      <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
        {!created && (
          <React.Fragment>
            <h2 className="invite-modal-title">Invite teammate to {org.name}</h2>
            <form onSubmit={handleSubmit}>
              <label className="invite-modal-label">
                Email
                <input
                  ref={inputRef}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="alice@example.com"
                  className="invite-modal-input"
                  disabled={submitting}
                />
              </label>
              {error && <div className="invite-modal-error">{error}</div>}
              <div className="invite-modal-actions">
                <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting || !email.trim()}>
                  {submitting ? 'Sending…' : 'Send invite'}
                </button>
              </div>
            </form>
          </React.Fragment>
        )}
        {created && (
          <React.Fragment>
            <h2 className="invite-modal-title">Invitation sent</h2>
            <p className="invite-modal-sub">
              {created.email_sent
                ? 'We emailed them. They can also use this link:'
                : 'Email transport not configured — share this link directly:'}
            </p>
            <div className="copy-link-row">
              <input readOnly value={created.accept_url} className="invite-modal-input" onFocus={(e) => e.target.select()} />
              <button type="button" className="btn-secondary" onClick={handleCopy}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
            {signupAllowlistEnabled && (
              <div className="allowlist-hint">
                Note: the invitee must be allowlisted to sign up. If they aren't yet, contact the deployment operator.
              </div>
            )}
            <div className="invite-modal-actions">
              <button type="button" className="btn-primary" onClick={onClose}>Done</button>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

// ---------- Members panel ----------

function MembersPanel({ org, isOwner, signupAllowlistEnabled }) {
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  const reload = useCallback(() => {
    if (!org || !org.slug) return;
    setLoading(true); setError(null);
    Promise.all([apiMembers(org.slug), apiPendingInvitations(org.slug)])
      .then(([m, p]) => { setMembers(m); setPending(p); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [org]);

  useEffect(() => { reload(); }, [reload]);

  const handleRevoke = (invId) => {
    if (!confirm('Revoke this invitation?')) return;
    apiRevokeInvitation(invId).then(reload).catch((e) => alert(e.message));
  };

  if (loading) return <div className="members-empty">Loading{"…"}</div>;
  if (error) return <div className="members-empty">{error}</div>;

  return (
    <div className="members-panel">
      <h1 className="members-title">Members of {org.name}</h1>
      {org.is_personal && (
        <div className="members-note">
          This is your personal organization. Create a new organization to collaborate with others.
        </div>
      )}
      <div className="members-section">
        {members.map((m) => (
          <div key={m.email} className="member-row">
            <div className="member-name">{m.name || m.email}</div>
            <div className="member-email">{m.email}</div>
            <div className="role-pill">{m.role === 'owner' ? 'Owner' : 'Member'}</div>
          </div>
        ))}
      </div>
      {pending.length > 0 && (
        <React.Fragment>
          <h2 className="members-subtitle">Pending invitations</h2>
          <div className="members-section">
            {pending.map((inv) => (
              <div key={inv.id} className="member-row">
                <div className="member-email">{inv.email}</div>
                <div className="member-name member-meta">invited by {inv.inviter.name || inv.inviter.email}</div>
                {isOwner && (
                  <button className="btn-revoke" onClick={() => handleRevoke(inv.id)}>Revoke</button>
                )}
              </div>
            ))}
          </div>
        </React.Fragment>
      )}
      {isOwner && !org.is_personal && (
        <div className="members-actions">
          <button className="btn-primary" onClick={() => setShowInvite(true)}>+ Invite teammate</button>
        </div>
      )}
      {showInvite && (
        <InviteModal
          org={org}
          signupAllowlistEnabled={signupAllowlistEnabled}
          onClose={() => setShowInvite(false)}
          onCreated={reload}
        />
      )}
    </div>
  );
}

// ---------- Root App ----------

function App() {
  const [screen, setScreen] = useState('loading');
  const [user, setUser] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [signupAllowlistEnabled, setSignupAllowlistEnabled] = useState(false);
  const [routeNotice, setRouteNotice] = useState(null);
  const [pendingFilePath, setPendingFilePath] = useState(null);
  const isApplyingUrl = useRef(false);

  // Color scheme + reader style — applied immediately on mount and on user pick.
  const [activeScheme, setActiveScheme] = useState(() => {
    const k = lsGet(KEY_SCHEME, 'coal');
    return SCHEMES[k] ? k : 'coal';
  });
  const [activeStyle, setActiveStyle] = useState(() => {
    const k = lsGet(KEY_STYLE, 'editorial');
    return STYLES[k] ? k : 'editorial';
  });
  useEffect(() => { applyScheme(activeScheme); }, [activeScheme]);
  useEffect(() => { applyStyle(activeStyle);  }, [activeStyle]);

  // Check session on mount
  useEffect(() => {
    apiMe()
      .then((data) => {
        isApplyingUrl.current = true;
        try {
          if (!data || !data.orgs || !data.orgs.length) { setScreen('landing'); return; }
          setUser(data.user);
          setOrgs(data.orgs);
          if (data.deployment) {
            setSignupAllowlistEnabled(!!data.deployment.signup_allowlist_enabled);
          }

          // ?org=<slug> still takes precedence — used by /app/invite/:id
          // success redirects. Strip after applying.
          let preselectedSlug = null;
          try {
            const url = new URL(window.location.href);
            preselectedSlug = url.searchParams.get('org');
            if (preselectedSlug) {
              url.searchParams.delete('org');
              window.history.replaceState(null, '', url.toString());
            }
          } catch (e) {}
          if (preselectedSlug) {
            const match = data.orgs.find((o) => o.slug === preselectedSlug);
            if (match) {
              const ws = (match.workspaces && match.workspaces[0]) || null;
              setSelectedOrg(match);
              setSelectedWorkspace(ws);
              setPendingFilePath(null);
              setScreen('workspace');
              lsSet(KEY_ORG, match.slug);
              if (ws) lsSet(keyOrgWorkspace(match.slug), ws.handle);
              window.history.replaceState(null, '', buildRoute({
                orgSlug: match.slug,
                wsHandle: ws ? ws.handle : null,
                filePath: null,
              }));
              return;
            }
          }

          // Path-based resolution (the new behavior).
          const route = parseRoute(window.location.pathname);

          let org = null;
          if (route.orgSlug) {
            org = data.orgs.find((o) => o.slug === route.orgSlug) || null;
            if (!org) {
              setRouteNotice("We couldn't find that organization. Showing your default.");
            }
          }
          if (!org) {
            const savedSlug = lsGet(KEY_ORG, null);
            if (savedSlug) org = data.orgs.find((o) => o.slug === savedSlug) || null;
          }
          if (!org) {
            if (data.orgs.length === 1) {
              org = data.orgs[0];
            } else {
              setScreen('org-picker');
              window.history.replaceState(null, '', '/');
              return;
            }
          }
          setSelectedOrg(org);
          lsSet(KEY_ORG, org.slug);

          let ws = null;
          if (route.wsHandle && org.workspaces) {
            ws = org.workspaces.find((w) => w.handle === route.wsHandle) || null;
            if (!ws) {
              setRouteNotice('Workspace `' + route.wsHandle + '` not found in `' + org.slug + '`. Showing default.');
            }
          }
          if (!ws) {
            const savedWs = lsGet(keyOrgWorkspace(org.slug), null);
            if (savedWs) ws = (org.workspaces || []).find((w) => w.handle === savedWs) || null;
          }
          if (!ws) {
            ws = (org.workspaces && org.workspaces[0]) || null;
          }
          setSelectedWorkspace(ws);
          setPendingFilePath(route.filePath || null);
          setScreen('workspace');

          window.history.replaceState(null, '', buildRoute({
            orgSlug: org.slug,
            wsHandle: ws ? ws.handle : null,
            filePath: route.filePath || null,
          }));
        } finally {
          // Clear the flag asynchronously so React's state updates above
          // don't trigger a state→URL push. setTimeout(0) lets the
          // post-effect render settle before the state→URL effect runs.
          setTimeout(() => { isApplyingUrl.current = false; }, 0);
        }
      })
      .catch(() => setScreen('landing'));
  }, []);

  // Push URL whenever org/workspace/file changes — but only when the
  // change is user-driven (not when applying a popstate or seeding
  // from the initial URL).
  useEffect(() => {
    if (isApplyingUrl.current) return;
    if (screen !== 'workspace') return;
    if (!selectedOrg) return;
    const url = buildRoute({
      orgSlug: selectedOrg.slug,
      wsHandle: selectedWorkspace ? selectedWorkspace.handle : null,
      filePath: pendingFilePath || null,
    });
    if (window.location.pathname !== url) {
      window.history.pushState(null, '', url);
      setRouteNotice(null);
    }
  }, [screen, selectedOrg, selectedWorkspace, pendingFilePath]);

  const handleFileChange = useCallback((p) => {
    setPendingFilePath(p);
  }, []);

  // Sync document.title with workspace + file.
  useEffect(() => {
    const ws = selectedWorkspace ? selectedWorkspace.display_name : null;
    const file = pendingFilePath ? pendingFilePath.split('/').pop() : null;
    let title;
    if (file && ws) title = file + ' · ' + ws + ' · mindex';
    else if (ws) title = ws + ' · mindex';
    else title = 'mindex';
    document.title = title;
  }, [selectedWorkspace, pendingFilePath]);

  // Back / forward — re-derive state from the URL using already-loaded
  // orgs. We don't refetch apiMe(); if membership changed since mount
  // (extremely rare for a back-button case), unknown orgs bounce to
  // landing. If popstate fires before apiMe resolved (orgs is empty),
  // we also bounce to landing — the next pop will work once orgs
  // populates.
  useEffect(() => {
    const onPopState = () => {
      const route = parseRoute(window.location.pathname);
      isApplyingUrl.current = true;
      try {
        if (!route.orgSlug) {
          if (orgs && orgs.length > 1) {
            setScreen('org-picker');
          } else if (orgs && orgs.length === 1) {
            setSelectedOrg(orgs[0]);
            setSelectedWorkspace((orgs[0].workspaces && orgs[0].workspaces[0]) || null);
            setPendingFilePath(null);
          }
          return;
        }
        const org = (orgs || []).find((o) => o.slug === route.orgSlug);
        if (!org) {
          setScreen('landing');
          return;
        }
        setSelectedOrg(org);
        let ws = null;
        if (route.wsHandle) {
          ws = org.workspaces.find((w) => w.handle === route.wsHandle) || null;
        }
        if (!ws) ws = (org.workspaces && org.workspaces[0]) || null;
        setSelectedWorkspace(ws);
        setPendingFilePath(route.filePath || null);
        setScreen('workspace');
      } finally {
        setTimeout(() => { isApplyingUrl.current = false; }, 0);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [orgs]);

  const onPickOrg = (org) => {
    setSelectedOrg(org);
    const ws = (org.workspaces && org.workspaces[0]) || null;
    setSelectedWorkspace(ws);
    setPendingFilePath(null);
    setScreen('workspace');
    lsSet(KEY_ORG, org.slug);
    if (ws) lsSet(keyOrgWorkspace(org.slug), ws.handle);
  };

  const onSwitchOrg = (org) => {
    setSelectedOrg(org);
    const savedWs = lsGet(keyOrgWorkspace(org.slug), null);
    let target = null;
    if (savedWs && org.workspaces) {
      target = org.workspaces.find((w) => w.handle === savedWs) || null;
    }
    if (!target) target = (org.workspaces && org.workspaces[0]) || null;
    setSelectedWorkspace(target);
    setPendingFilePath(null);
    lsSet(KEY_ORG, org.slug);
  };

  // setWorkspace passed to <Workspace>: clear file path so a switch
  // doesn't leak the previous workspace's file into the URL (and into
  // initialFilePath, which would make the new workspace try to open a
  // file by the same name).
  const handleSetWorkspace = useCallback((w) => {
    setSelectedWorkspace(w);
    setPendingFilePath(null);
    if (selectedOrg && w) lsSet(keyOrgWorkspace(selectedOrg.slug), w.handle);
  }, [selectedOrg]);

  if (screen === 'loading') {
    return (
      <div className="screen-center">
        <div style={{ color: 'var(--r-text-mute)', fontSize: 14 }}>Loading{"\u2026"}</div>
      </div>
    );
  }

  return (
    <div className="root" data-screen-label={screen}>
      <Toast />
      {screen === 'landing' && <Landing onSignIn={doSignIn} />}
      {screen === 'org-picker' && <OrgPicker orgs={orgs} onPick={onPickOrg} />}
      {screen === 'workspace' && selectedOrg && (
        <Workspace
          user={user}
          org={selectedOrg}
          workspace={selectedWorkspace}
          setWorkspace={handleSetWorkspace}
          orgs={orgs}
          onSwitchOrg={onSwitchOrg}
          onSwitchAccount={() => setScreen('landing')}
          onSignOut={doSignOut}
          signupAllowlistEnabled={signupAllowlistEnabled}
          initialFilePath={pendingFilePath}
          onFileChange={handleFileChange}
          routeNotice={routeNotice}
          onDismissNotice={() => setRouteNotice(null)}
          activeScheme={activeScheme}
          setActiveScheme={setActiveScheme}
          activeStyle={activeStyle}
          setActiveStyle={setActiveStyle}
        />
      )}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
